import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import DOMPurify from "dompurify";
import { clockText } from "../lib/clock_format";
import Sortable, { MultiDrag } from "sortablejs";

// Multi-select stacked drag for the slide-list sidebar (card #2788), extending
// the single-slide reorder from card #2770. MultiDrag is a Sortable.js plugin,
// not a separate library -- a per-row checkbox toggles a row into the drag
// selection (revised from an earlier ctrl/cmd-click mechanism per Jay's live
// feedback), shift-click on a checkbox range-selects, and dragging any
// selected row carries the whole selection together. Mounted ONCE at module
// scope (Sortable's own documented pattern) rather than per controller
// instance -- re-mounting on every connect() would just re-register the same
// global plugin repeatedly.
Sortable.mount(new MultiDrag());

// Allowlist for rich-text element content (card #2043). Mirrors Lexxy's own
// internal sanitiser (node_modules/@37signals/lexxy ALLOWED_HTML_TAGS /
// ALLOWED_HTML_ATTRIBUTES) so the canvas renders exactly what Lexxy emits.
// Lexxy already sanitises on input; this is defence-in-depth on render, and the
// server partial sanitises the same content again on the published slide.
const RICH_TEXT_ALLOWED_TAGS = [
  "a", "b", "blockquote", "br", "code", "em", "figcaption", "figure",
  "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "li", "mark", "ol",
  "p", "pre", "q", "s", "strong", "ul", "span", "u", "table", "tbody",
  "tr", "th", "td"
];
const RICH_TEXT_ALLOWED_ATTR = ["href", "title", "class", "style", "target", "rel"];

// Deck editor — the WYSIWYG authoring surface (ADR-0065 Slice 1', ADR-0066).
//
// Holds the deck_json as working JS state (seeded from the `deck` value, not
// fetched) and re-renders three panes from that state: the slide list, the live
// 1280x720 canvas for the selected slide, and a properties panel. On Save it
// PATCHes the whole deck back to the report via fetch (mirrors the SurveyJS
// creator's fetch+CSRF approach).
//
// Vocabulary is generic — deck / slide / element / slot (ADR-0066). The canvas
// reuses the published render's .airdrop-slide / .airdrop-el classes + inline
// geometry, so it is true WYSIWYG. Element/slide DOM is cloned from <template>
// tags (the carve-out for a client-side canvas editor); no server data is built
// into HTML strings here.
// Canvas size is a per-deck setting (ADR-0077). The deck's dimensions arrive as
// the canvasW / canvasH Stimulus values (rendered from the deck server-side) and
// are read through this.canvasW / this.canvasH getters; both default to the
// slidepack canvas (1280x720) so an editor mounted without them is unchanged.
const DEFAULT_CANVAS_W = 1280;
const DEFAULT_CANVAS_H = 720;

// Manual canvas zoom (card #1997). The cutting-board zoom widget steps this.scale
// between these bounds; Fit (null zoomScale) hands control back to the auto-fit.
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.1;

// Named canvas-size presets for the Themes-tab control (ADR-0077). Mirrors the
// metadata-form canvas_size_controller's presets — one source of intent for the
// two surfaces. A deck whose dims match neither preset reads as "custom".
const CANVAS_PRESETS = {
  "1280x720": { w: 1280, h: 720 },
  "1920x1080": { w: 1920, h: 1080 }
};

// Snap-to-grid presets, in canvas pixels (card #1916). The grid is a per-deck
// DESIGN choice (stored in deck.design.grid, the same place the font lives in
// deck.design.fonts), with three named cell sizes. Each value divides the
// 1280x720 canvas cleanly: fine = 64x36 cells, medium = 32x18, coarse = 16x9.
// When snap is on, both the snap target AND the visible overlay use the deck's
// chosen cell size, so the grid you see is exactly the grid you snap to.
const GRID_PRESETS = { fine: 20, medium: 40, coarse: 80 };
const DEFAULT_GRID = "medium";

// Default grid line colour (card #1916). A semi-transparent white so the grid
// reads on the default sapphire ground out of the box; overridable per deck via
// deck.design.grid_color (mirrors deck.design.grid / deck.design.fonts).
const DEFAULT_GRID_COLOR = "rgba(255, 255, 255, 0.25)";

// Default grid line STYLE and WIDTH (card #1916). The overlay is an inline SVG
// pattern (see gridOverlaySvg), so the line can be solid, dashed, or dotted at
// an authored px width. A thin dashed line reads as guidance, not chrome, so it
// is the out-of-the-box default; both are overridable per deck via
// deck.design.grid_line_style / deck.design.grid_line_width.
const DEFAULT_GRID_LINE_STYLE = "dashed";
const DEFAULT_GRID_LINE_WIDTH = 1;

// Minimum interactive size for an element's overlay handle, in canvas pixels
// (card #1916). Thin/small elements stay grabbable: the handle never shrinks
// below this floor, even though the rendered element keeps its true size.
const HANDLE_MIN = 12;

// Collection caption band (card: album captions) — the editor mirror of
// Airdrop::CollectionExpander's caption constants. When an album has display_fields,
// each auto-grid cell reserves CAPTION_BAND_HEIGHT at its bottom for stacked caption
// lines; the image shrinks by the band and the lines sit beneath it. Keep these in
// lockstep with the Ruby constants so the WYSIWYG matches what publishes.
const CAPTION_BAND_HEIGHT = 64;
const CAPTION_LINE_HEIGHT = 14;
const CAPTION_PRIMARY_SIZE = 13;
const CAPTION_SECONDARY_SIZE = 11;
const CAPTION_SECONDARY_OPACITY = 70;
// The album display_fields rendered as caption lines, in the SAME ORDER as
// CollectionExpander::CAPTION_FIELDS and _album_image_fields.html.erb. "tags" is
// deliberately omitted for v1 (chips are heavier than a stacked text line).
const CAPTION_FIELDS = ["client_name", "place_name", "question_title", "rating", "completed_at"];

// Image "pop" defaults (card: album image polish) — the editor mirror of the
// expander's DEFAULT_IMAGE_RADIUS / DEFAULT_IMAGE_SHADOW, applied to collection
// auto-grid cells so the designer canvas + Preview tiles read as cards in parity
// with the published render.
const COLLECTION_IMAGE_RADIUS = 8;
const COLLECTION_IMAGE_SHADOW = true;

// "Make it pop" shadow presets (card #2135) — the byte-for-byte twin of
// Deck::SHADOW_PRESETS. Keep the two in lockstep so the editor canvas and the
// published viewer draw the identical box-shadow. `soft` is also what the legacy
// `shadow: true` boolean and the collection auto-grid image cells resolve to.
const SHADOW_PRESETS = {
  soft: "0 2px 8px rgba(0,0,0,0.18)",
  lifted: "0 8px 24px rgba(0,0,0,0.22)",
  hard: "0 4px 0 rgba(0,0,0,0.35)"
};

// Slide KINDS that show NO page number by default (card #2093) — the byte-for-byte
// twin of Deck::PAGE_NUMBER_DEFAULT_OFF_KINDS. A deck typically opens on a
// cover/title, which should not carry "1 / N". Keyed on the slide's semantic `kind`
// (the field the Kind select writes), NOT the loose legacy `variant` token. Keep
// this list and slideShowsPageNumber in lockstep with the Ruby side.
const PAGE_NUMBER_DEFAULT_OFF_KINDS = ["cover", "title"];

// Slide lineage / historical scroll-back (card #2737) -- the byte-for-byte
// twin of Deck::PRIOR_CYCLE_SLIDE_NAMES. Only a slide whose `name` exact-
// matches one of these two gets the "Prior cycle" comparison tab at all
// (syncPriorSlideTab) -- keep this list in lockstep with the Ruby side.
const PRIOR_CYCLE_SLIDE_NAMES = ["Title", "Results in Focus"];

const ELEMENT_DEFAULTS = {
  headline: { x: 112, y: 120, w: 800, h: 100, options: { align: "left", size: 56, weight: 800, opacity: 100 }, content: "Headline" },
  text: { x: 112, y: 240, w: 800, h: 60, options: { align: "left", size: 24, weight: 400, opacity: 100 }, content: "Body text" },
  image: { x: 112, y: 320, w: 480, h: 280, options: { fit: "cover", alt: "", opacity: 100 }, content: "" },
  shape: { x: 112, y: 120, w: 200, h: 16, options: { fill: "var(--color-canary)", radius: 0, border_width: 0, border_color: "#000000", opacity: 100 }, content: null },
  html:      { x: 112, y: 160, w: 760, h: 480, options: { html: "", opacity: 100 }, content: null },
  // A live Porthole report frame (card #4008): options.frame is the
  // Insights::ReportFrames key, set on insert. Rendered as an iframe to the embed.
  janela_frame: { x: 112, y: 160, w: 760, h: 420, options: { frame: "", opacity: 100 }, content: null },
  // A live clock (card #4008): dd/mm and a 12 hour time in the Beacon's time zone.
  // Rendered by _canvas.html.erb's "clock" branch (and ticked by deck-clock).
  clock: { x: 960, y: 24, w: 300, h: 48, options: { align: "right", size: 28, weight: 600, show_date: true, show_seconds: true, opacity: 100 }, content: null },
  // Starter shape for the native "table" kind (card #3068). Schema is
  // documented in decks/slides/_canvas.html.erb's "table" render branch --
  // headers: [{content, align, width_pct}], rows: [{cells: [{content, align,
  // weight, color}]}]. Two header columns + one data row of placeholder text
  // so a freshly-inserted table has real, immediately right-click-editable
  // content instead of landing as a blank rectangle.
  table: {
    x: 112, y: 200, w: 640, h: 140,
    options: {
      border_color: "#d0d0d0", header_background: "#003D68", header_color: "#ffffff", opacity: 100,
      headers: [
        { content: "Header", align: "center" },
        { content: "Header", align: "center" }
      ],
      rows: [
        { cells: [{ content: "Cell", align: "center" }, { content: "Cell", align: "center" }] }
      ]
    },
    content: null
  },
  analytics: { x: 0, y: 0, w: 1280, h: 720, options: { url: "/analytics/main", opacity: 100 }, content: null }
};

export default class extends Controller {
  static targets = [
    "slideList", "slideRow", "stage", "canvasMount", "gridOverlay", "properties", "bindings",
    "dirty", "saveButton", "saveIdle", "saveSpinner", "saveDone", "saveFail", "snapButton",
    "pullButton", "pullIcon", "pullStatus",
    "slideRowTemplate", "elementHandleTemplate",
    "kindModal", "kindModalTitle", "kindApplyBanner", "kindList", "kindEmpty", "kindCard", "kindCardTemplate", "kindFilter",
    "assetModal", "assetScope", "assetTab", "assetGroupTabs", "assetFolderCaption",
    "assetGrid", "assetEmpty", "assetEmptyAction", "assetLoading",
    "assetTile", "assetTileTemplate",
    "objectModal", "objectSourceStep", "objectRecordStep", "objectRecordTitle",
    "objectRecordList", "objectRecordEmpty", "objectRecordLoading",
    "objectRecordTemplate", "objectImageTemplate",
    "tab", "panel", "previewMount", "themesMount", "measuresMount",
    "jsonArea", "jsonError", "jsonFile", "copyJsonButton", "copyJsonLabel",
    "slideViewTab", "slideDesignView", "slideJsonView", "slideNotesView", "slidePromptView", "zoomLabel",
    "priorSlideTab", "slidePriorView", "priorStage", "centreMain", "slideViewToolbar",
    "slideJsonArea", "slideJsonError", "slideJsonCopyButton", "slideJsonCopyLabel",
    "rightPanel", "rightPanelIcon",
    "leftPanel", "leftPanelIcon",
    "railAside", "resizeHandle", "leftRailAside",
    "undoButton", "redoButton",
    "gridColorSwatchButton", "gridColorSwatch", "gridColorPopoverMount",
    "kindSaveModal", "kindSaveModalName", "kindSaveModalDescription",
    "kindSaveModalConflict", "kindSaveModalSubmit", "kindSaveModalActions",
    "cropImage", "cropZoom"
  ];

  // The Preview tab mounts the SHARED viewer primitive (card #1902). The deck
  // editor builds the slide pages into the viewer's deck mount and tells the
  // viewer to re-scan via this outlet — it never reimplements presentation.
  static outlets = ["airdrop-report-viewer"];

  static values = {
    activeTab: { type: String, default: "designer" },
    deck: Object,
    updateUrl: String,
    kindsUrl: String,
    saveKindUrl: String,
    objectivesUrl: String,
    assetsUrl: String,
    albumsUrl: String,
    // Base URL for the deck's Notes & Prompts (card #2121). The Notes panel and the
    // per-slide Prompt view fetch from here, appending ?view=…&slide_uuid=… for the
    // current slide. Annotations live in a separate table from deck_json, so they
    // never ride the deck save path — the frames own their own CRUD.
    annotationsUrl: String,
    reportId: String,
    // Resolved live values from result_json (card #2061). Seeded server-side on
    // the build root so the Preview surface and the Bindings "Data fields" list can
    // show what each {slot} / bound element resolves to. Refreshed in place by
    // pullData(). The Designer canvas NEVER reads this — it stays literal.
    result: { type: Object, default: {} },
    pullUrl: { type: String, default: "" },
    // Base URL for the "Prior cycle" comparison read (card #2737) --
    // /decks/:id/prior_slide. loadPriorSlide appends ?slide_name=… for the
    // currently selected slide.
    priorSlideUrl: { type: String, default: "" },
    fonts: Object,
    palette: { type: Array, default: [] },
    // The closed slide-kind vocabulary (ADR-0078), as { key => label } pairs,
    // sourced from Deck.slide_kind_options on the build root — one source of
    // truth for the slide Kind select and the library picker filter.
    slideKinds: { type: Object, default: {} },
    // The live Porthole report frames this deck may carry, as [{ key, label }]
    // (Deck#insertable_report_frames, card #4008). Feeds the "Live frame" inserts.
    liveFrames: { type: Array, default: [] },
    canvasW: { type: Number, default: DEFAULT_CANVAS_W },
    canvasH: { type: Number, default: DEFAULT_CANVAS_H }
  };

  // The deck's canvas dimensions (ADR-0077). Read everywhere the editor used to
  // reference the CANVAS_W / CANVAS_H constants — stage scale, grid overlay,
  // surface sizing, geometry clamps. Falls back to the slidepack canvas when the
  // value is absent or non-positive, so the editor is unchanged without it.
  get canvasW() { return this.canvasWValue > 0 ? this.canvasWValue : DEFAULT_CANVAS_W; }
  get canvasH() { return this.canvasHValue > 0 ? this.canvasHValue : DEFAULT_CANVAS_H; }

  // The default element spec for `kind`, with the analytics embed sized to fill
  // the deck's full canvas (it is a viewport-filling iframe, not a fixed tile).
  // Every other kind uses its static ELEMENT_DEFAULTS footprint unchanged.
  elementDefault(kind, options = {}) {
    const def = ELEMENT_DEFAULTS[kind] || ELEMENT_DEFAULTS.text;
    if (kind === "analytics") {
      return { ...def, x: 0, y: 0, w: this.canvasW, h: this.canvasH };
    }
    // A stat tile (one number) is a small block for a footer band, not a chart.
    if (kind === "janela_frame" && this.liveFrameIsTile(options.frame)) {
      return { ...def, w: 300, h: 130 };
    }
    return def;
  }

  liveFrameIsTile(key) {
    return this.liveFramesValue.some((frame) => frame.key === key && frame.tile);
  }

  connect() {
    // Working state. Normalise to a slides array we can mutate freely.
    this.deck = this.deckValue && typeof this.deckValue === "object" ? this.deckValue : {};
    if (!Array.isArray(this.deck.slides)) this.deck.slides = [];
    if (!this.deck.bindings || typeof this.deck.bindings !== "object") this.deck.bindings = {};

    // Resolved live values (card #2061). Mirrors result_json from the server. The
    // Preview surface and the Bindings "Data fields" list read it; the Designer
    // canvas does not. Refreshed by pullData() after a save + pull round-trip.
    this.result = this.resultValue && typeof this.resultValue === "object" ? this.resultValue : {};

    this.selectedSlide = this.deck.slides.length ? 0 : null;
    this.selectedElement = null;
    this.selectionIndices = [];

    // Slide-list MULTI-select for stacked drag (card #2788) -- separate from
    // selectedSlide above, which still drives what's shown on the canvas. A
    // Set of slide indices currently marked for reordering; empty means "no
    // multi-select active", the normal case, where dragging behaves exactly as
    // card #2770 shipped it. Driven entirely by the per-row checkbox
    // (toggleSlideMultiselect) since the #2788 revision -- a plain or ctrl/cmd
    // click on the row itself never touches this Set, and neither does a
    // completed OR a missed/cancelled drag; the only way a row leaves this Set
    // is an explicit uncheck. _lastMultiSelectIndex is the shift-click anchor
    // (standard file-manager convention), updated on every checkbox click,
    // held steady across repeated shift-clicks.
    this.multiSelectedIndices = new Set();
    this._lastMultiSelectIndex = null;
    this.dirty = false;
    this.scale = 1;
    // Manual zoom state (card #1997). null = follow auto-fit; a number = the user
    // has zoomed and fitStage should preserve it across re-layouts until they Fit.
    this.zoomScale = null;
    this.fitScale = 1;

    this.history = [];
    this.future = [];
    this._burstActive = false;
    this._historyTimer = null;
    this._historySnapshot = this._captureState();
    this._inlineEditorEl = null;

    // Snap-to-grid is editor chrome (card #1916), held on the controller and NEVER
    // written to deck_json. On by default; the toolbar toggle flips it.
    this.snap = true;

    // Canvas preset state for the Themes-tab control (ADR-0077). Seed "custom"
    // when the deck's dims match neither named preset, so the panel opens with the
    // width/height fields shown. canvasPresetKey() honours this flag thereafter.
    this._canvasCustom = !CANVAS_PRESETS[`${this.canvasW}x${this.canvasH}`];

    this.boundResize = () => this.fitStage();
    window.addEventListener("resize", this.boundResize);

    // Editor-level keydown (card #1916): Escape deselects the current element so
    // the panel falls back to slide properties. It must NOT fire while a modal
    // (kind picker / asset picker) is open — those own their own Escape handler,
    // bound on open — or while the context menu is open (it closes itself). The
    // guard checks for an open modal/menu before acting; the modal handlers stay
    // intact.
    this.boundEditorKeydown = (event) => this.handleEditorKeydown(event);
    document.addEventListener("keydown", this.boundEditorKeydown);

    this.fitStage();
    this.applySnapState();
    this.renderGridColorSwatch();
    this.renderAll();
    this.showActiveTab();
    this._initSlideSortable();

    // Deep-link to a specific slide (ad-hoc build, no card -- Jay: "Each
    // slide near the COPY SLIDE can have an EDIT SLIDE"). decks/_slide_toolbar
    // .html.erb's "Edit slide" button links here with `?slide=<uuid>` so
    // clicking it from the show page opens the editor with that exact slide
    // already selected/scrolled-to, instead of always landing on slide 1.
    this._selectSlideFromQueryParam();

    // Restore persisted rail width (card #1966).
    if (this.hasRailAsideTarget) {
      const saved = localStorage.getItem("deck-editor-rail-width");
      if (saved) this.railAsideTarget.style.width = `${parseInt(saved, 10)}px`;
    }

    // Restore persisted left rail width.
    if (this.hasLeftRailAsideTarget) {
      const saved = localStorage.getItem("deck-editor-left-rail-width");
      if (saved) this.leftRailAsideTarget.style.width = `${parseInt(saved, 10)}px`;
    }

    // Background kind library fetch for drift detection (card #2007).
    this._fetchKindsForDrift();

    this.boundHandlePaste = (e) => this.handlePaste(e);
    document.addEventListener("paste", this.boundHandlePaste);

    // Prior-cycle alignment fix (card #2737 follow-up): every load of the
    // "deck_prior_slide" turbo frame REPLACES its DOM content wholesale (a
    // fresh `priorStageTarget` node with no inline transform/margin yet), so
    // re-run fitStage() once the new content lands to bring it back into
    // lockstep with the Design stage's current scale. The frame itself is
    // always present in the initial HTML (build.html.erb renders it
    // statically, only its `src` is set later by loadPriorSlide), so it's
    // safe to look up and bind here at connect time.
    this._priorSlideFrame = document.getElementById("deck_prior_slide");
    this.boundPriorSlideFrameLoad = () => this.fitStage();
    this._priorSlideFrame?.addEventListener("turbo:frame-load", this.boundPriorSlideFrameLoad);

    // Eagerly fetch objectives so chart skeletons render on first paint without
    // requiring the user to open the binding picker. No-op for decks without an
    // objectives URL (non-Mission reports).
    if (!this.objectives && !this.objectivesLoading) this.loadObjectives();

    this._updateUndoRedoButtons();
  }

  disconnect() {
    window.removeEventListener("resize", this.boundResize);
    if (this.boundKindKeydown) document.removeEventListener("keydown", this.boundKindKeydown);
    if (this.boundAssetKeydown) document.removeEventListener("keydown", this.boundAssetKeydown);
    if (this.boundEditorKeydown) document.removeEventListener("keydown", this.boundEditorKeydown);
    document.removeEventListener("mousemove", this._boundDoRailResize);
    document.removeEventListener("mouseup", this._boundStopRailResize);
    if (this.boundHandlePaste) document.removeEventListener("paste", this.boundHandlePaste);
    if (this.boundPriorSlideFrameLoad) this._priorSlideFrame?.removeEventListener("turbo:frame-load", this.boundPriorSlideFrameLoad);
    clearTimeout(this._historyTimer);
    clearTimeout(this._pullStatusTimer);
    this.closeContextMenu();
    if (this._inlineEditorEl) { this._inlineEditorEl.remove(); this._inlineEditorEl = null; }
    this._slideSortable?.destroy();
    this._slideSortable = null;
  }

  // Drag-and-drop slide reorder (card #2770). Sortable.create binds to the
  // persistent slideListTarget CONTAINER, not to individual rows -- those are
  // fully replaced by replaceChildren() on every renderSlideList() call, but
  // Sortable tracks the container, so it survives re-renders untouched. Only
  // torn down on disconnect. Drag is scoped to the grip handle (data-sort-handle)
  // so it doesn't fight the row's own click-to-select / right-click context menu.
  //
  // chosenClass is a SINGLE class name (deck-editor-slide-chosen, defined in
  // application.css), not a multi-word Tailwind string -- Sortable.js applies
  // it via el.classList.add(name) with the whole option string as one token,
  // which throws InvalidCharacterError on a space-containing value and
  // silently aborts the drag. Caught live via Playwright against a real
  // pointer drag (card #2770) -- the album/card sortable analogs carry this
  // same latent bug in their chosenClass, just never hit it because it fires
  // after chosenClass tries to apply, which is early in Sortable's own start
  // handling. selectedClass carries the exact same constraint (card #2788) --
  // it's the class MultiDrag's Sortable.utils.select/deselect toggle, so it
  // must be the same single class name renderSlideList applies directly.
  //
  // multiDrag: true activates the plugin mounted at module scope above. No
  // multiDragKey is set -- selection is handled entirely by OUR OWN
  // toggleSlideMultiselect (bound to the per-row checkbox, card #2788
  // revision), which stays the single source of truth and re-syncs Sortable's
  // internal selection state on every render (see renderSlideList). The
  // plugin's own built-in click-to-select toggle fires for a plain
  // mousedown+mouseup that ORIGINATES on the handle (Sortable's `handle`
  // option gates its whole tap-start lifecycle, not just the drag itself) AND
  // doesn't travel far enough to register as a real drag -- confirmed LIVE
  // (Playwright, card #2788 revision) that this is not the rare path the
  // original comment here assumed: MultiDrag's own `drop` handler toggles its
  // OWN internal selectedClass/multiDragElements bookkeeping the instant
  // `dragStarted` is false, entirely bypassing onEnd below and therefore never
  // triggering a renderSlideList() resync -- exactly the class of "missed your
  // drop zone" case Jay flagged. onSelect/onDeselect (Sortable's own hooks for
  // that internal toggle, see node_modules/sortablejs's MultiDrag `drop`
  // method) force an immediate resync from multiSelectedIndices, the single
  // source of truth, so the plugin's opinion never wins. Deferred to a
  // microtask rather than run inline, since the toggle can fire mid-loop
  // (shift-range selection) while Sortable's own drop() handler still holds a
  // live reference to the row collection being iterated.
  _initSlideSortable() {
    if (!this.hasSlideListTarget) return;
    this._slideSortable = Sortable.create(this.slideListTarget, {
      handle: "[data-sort-handle]",
      animation: 150,
      ghostClass: "opacity-40",
      chosenClass: "deck-editor-slide-chosen",
      multiDrag: true,
      selectedClass: "deck-editor-slide-multiselected",
      forceFallback: true,
      fallbackOnBody: false,
      swapThreshold: 0.65,
      onEnd: (evt) => this.handleSlideDragEnd(evt),
      onSelect: () => queueMicrotask(() => this.renderSlideList()),
      onDeselect: () => queueMicrotask(() => this.renderSlideList())
    });
  }

  // Dispatch a completed slide-list drag to the right mover (card #2788). Only
  // treated as a multi-drag when 2+ rows are ACTIVELY multi-selected AND the
  // dragged row is one of them -- multiSelectedIndices (not Sortable's own
  // evt shape) is the single source of truth per _initSlideSortable's note
  // above. Any other case -- no multi-select, or a single leftover
  // multi-selected row -- is byte-for-byte the card #2770 single-slide path.
  //
  // evt.newIndex is Sortable's settled index for the row the user actually
  // grabbed, not necessarily the FIRST (lowest original index) row in the
  // selection -- MultiDrag folds the whole selection in original order
  // adjacent to the dragged row, then unfolds it starting at the group's
  // target position, so the dragged row lands at
  // (group start + its own offset within the sorted selection). moveSlidesByDrag
  // wants the group's start, so that offset is subtracted back out here.
  handleSlideDragEnd(evt) {
    if (this.multiSelectedIndices.size > 1 && this.multiSelectedIndices.has(evt.oldIndex)) {
      const sortedSelection = Array.from(this.multiSelectedIndices).sort((a, b) => a - b);
      const draggedOffset = sortedSelection.indexOf(evt.oldIndex);
      this.moveSlidesByDrag(sortedSelection, evt.newIndex - draggedOffset);
    } else {
      this.moveSlideByDrag(evt.oldIndex, evt.newIndex);
    }
  }

  // ---- Layout / scaling --------------------------------------------------

  // Scale the 1280x720 stage to fit the centre pane while keeping the canvas
  // itself at true 1280x720. The scale factor is what converts screen-pixel
  // pointer deltas back into canvas-space geometry (see startMove/startResize).
  //
  // PRIOR-CYCLE ALIGNMENT (card #2737 follow-up): the SAME computed scale is
  // also applied to `priorStageTarget` (the Prior-cycle tab's stage box, sized
  // to the identical --canvas-w/--canvas-h true-size box) whenever it is
  // present, so switching between the Design and Prior cycle slide-view tabs
  // never visibly resizes or repositions the slide — only the tab's content
  // swaps. See applyScale/computeFitScale for how the two stay in lockstep
  // even though only one of the two is ever visible (display) at a time.
  fitStage() {
    if (!this.hasStageTarget) return;
    this.fitScale = this.computeFitScale();
    this.applyScale(this.zoomScale ?? this.fitScale);
  }

  // The auto-fit scale: the largest scale (capped at 1.0 true-size) that fits the
  // 1280x720 stage inside the centre pane with a 64px gutter. Pure calc, no DOM
  // writes — fitStage and Fit both read it.
  //
  // Reads `centreMainTarget` (the `<main>` housing both the Design and Prior
  // cycle slide-view panes), NOT `stageTarget.parentElement` directly — the
  // Design/Prior panes toggle `hidden` (display:none) between each other, so
  // whichever one is NOT currently shown reports clientWidth/clientHeight 0.
  // `main` itself is never hidden, so it stays a stable measurement anchor
  // regardless of which slide-view tab is active; `slideViewToolbarTarget`'s
  // own height is subtracted since it sits above both panes inside `main`.
  computeFitScale() {
    const pane = this.centrePaneSize();
    const available = Math.min(pane.width - 64, (pane.height - 64) * (this.canvasW / this.canvasH));
    return Math.max(0.1, Math.min(1, available / this.canvasW));
  }

  // The available width/height for the stage, measured off a container that
  // stays in the layout regardless of which slide-view tab is showing (see
  // computeFitScale's comment). Falls back to the OLD stage-parent reading if
  // `centreMain`/`slideViewToolbar` aren't present, so this degrades safely.
  centrePaneSize() {
    if (this.hasCentreMainTarget) {
      const toolbarHeight = this.hasSlideViewToolbarTarget ? this.slideViewToolbarTarget.offsetHeight : 0;
      return {
        width: this.centreMainTarget.clientWidth,
        height: this.centreMainTarget.clientHeight - toolbarHeight
      };
    }
    const pane = this.stageTarget.parentElement;
    return { width: pane.clientWidth, height: pane.clientHeight };
  }

  // Apply a scale to the stage(s) + reconcile their layout footprint with the
  // scaled visual, then refresh the zoom % label. Applies to BOTH the Design
  // stage and the Prior-cycle stage (when present) so the two are always
  // pixel-identical — see fitStage's comment.
  applyScale(scale) {
    this.scale = scale;
    this.applyStageScale(this.stageTarget, scale);
    if (this.hasPriorStageTarget) this.applyStageScale(this.priorStageTarget, scale);
    this.updateZoomLabel();
  }

  // Apply `scale` to one stage element's transform + margin reconciliation.
  // Extracted from applyScale (card #2737 follow-up) so the Design stage and
  // the Prior-cycle stage — two separate DOM nodes sized identically via the
  // same --canvas-w/--canvas-h box — go through the exact same maths.
  applyStageScale(stage, scale) {
    stage.style.transform = `scale(${scale})`;
    // transform: scale() shrinks/grows the visual but leaves the layout box at the
    // full 1280x720. The margins reconcile the reserved footprint with the scaled
    // visual: NEGATIVE (scale<1) collapses it so the flex pane centres the smaller
    // canvas; POSITIVE (scale>1) grows it so the pane overflows and scrolls.
    stage.style.marginRight = `${this.canvasW * (scale - 1)}px`;
    stage.style.marginBottom = `${this.canvasH * (scale - 1)}px`;
  }

  // ---- Canvas zoom widget (card #1997) -----------------------------------

  zoomIn() { this.setScale((this.zoomScale ?? this.scale) + ZOOM_STEP); }
  zoomOut() { this.setScale((this.zoomScale ?? this.scale) - ZOOM_STEP); }

  // Cmd/Ctrl + wheel over the cutting board zooms; plain wheel scrolls as normal.
  zoomWheel(event) {
    if (!(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    const dir = event.deltaY < 0 ? 1 : -1;
    this.setScale((this.zoomScale ?? this.scale) + dir * ZOOM_STEP);
  }

  // Manual zoom: clamp to the 25%–200% range, pin zoomScale (so re-layouts keep
  // it) and apply. Rounded to whole percents to keep steps tidy.
  setScale(value) {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(value * 100) / 100));
    this.zoomScale = clamped;
    this.applyScale(clamped);
  }

  // Fit: drop manual zoom and recompute the auto-fit. Bound to the % label click.
  zoomFit() {
    this.zoomScale = null;
    this.fitStage();
  }

  updateZoomLabel() {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(this.scale * 100)}%`;
  }

  // ---- Snap to grid (card #1916, editor-only) ----------------------------

  // Toolbar toggle: flip snap, re-apply the overlay + button state. Snap state
  // never touches deck_json, so this never marks the deck dirty.
  toggleSnap() {
    this.snap = !this.snap;
    this.applySnapState();
  }

  // The deck's chosen snap cell size in canvas pixels (card #1916). Reads the
  // per-deck design choice (deck.design.grid), falling back to DEFAULT_GRID when
  // blank or unknown — mirrors deckFontKey's fallback for deck.design.fonts.
  gridSize() {
    return GRID_PRESETS[this.deck?.design?.grid] || GRID_PRESETS[DEFAULT_GRID];
  }

  // The deck's chosen grid line colour, falling back to DEFAULT_GRID_COLOR when
  // blank — mirrors gridSize()'s fallback for deck.design.grid.
  gridColor() {
    const c = this.deck?.design?.grid_color;
    return (typeof c === "string" && c.trim()) ? c.trim() : DEFAULT_GRID_COLOR;
  }

  // Paint the inline grid-colour swatch (Slide-view row) from the current
  // deck.design.grid_color, falling back to DEFAULT_GRID_COLOR via gridColor().
  // The data-driven colour is the justified inline-style exception, same as
  // colorField's native swatch and the geometry code. The checkerboard backing
  // (CSS .deck-grid-swatch-checker) shows through a translucent value so a
  // 25%-white default reads as translucent, not blank.
  renderGridColorSwatch() {
    if (this.hasGridColorSwatchTarget) {
      this.gridColorSwatchTarget.style.backgroundColor = this.gridColor();
    }
  }

  // Open/refresh the grid-colour popover (card #1916 follow-up): (re)mount the
  // shared colorField picker into the popover so it always reflects the CURRENT
  // deck.design.grid_color, and wire its change to the same path the Themes-tab
  // control uses — set the value, mark dirty, re-tint the live grid overlay
  // (applySnapState rebuilds the SVG), and repaint the inline swatch. Reusing
  // colorField means rgba values and the brand palette quick-picks round-trip
  // identically on both surfaces. The dropdown controller owns show/hide; this
  // runs on the swatch button's click alongside dropdown#toggle.
  openGridColorPopover() {
    if (!this.hasGridColorPopoverMountTarget) return;
    const mount = this.gridColorPopoverMountTarget;
    mount.replaceChildren();
    mount.appendChild(
      this.colorField("Grid colour", this.deck?.design?.grid_color || DEFAULT_GRID_COLOR, (v) => {
        this.deck.design = this.deck.design || {};
        this.deck.design.grid_color = v;
        this.markDirty();
        this.applySnapState();
        this.renderGridColorSwatch();
      })
    );
  }

  // The deck's chosen grid line style (card #1916). One of solid/dashed/dotted,
  // falling back to DEFAULT_GRID_LINE_STYLE when blank or unknown — mirrors
  // gridSize()/gridColor()'s fallback for the other grid design choices.
  gridLineStyle() {
    const s = this.deck?.design?.grid_line_style;
    return ["solid", "dashed", "dotted"].includes(s) ? s : DEFAULT_GRID_LINE_STYLE;
  }

  // The deck's chosen grid line width in px (card #1916). A positive integer,
  // falling back to DEFAULT_GRID_LINE_WIDTH when blank or non-numeric.
  gridLineWidth() {
    const w = parseInt(this.deck?.design?.grid_line_width, 10);
    return Number.isFinite(w) && w > 0 ? w : DEFAULT_GRID_LINE_WIDTH;
  }

  // Map a line style + width to the SVG stroke-dasharray + stroke-linecap that
  // renders it (card #1916). Dashes/dots are width-proportional so they keep
  // their character as the line gets thicker:
  //   - solid  → continuous stroke, butt cap
  //   - dashed → dash 6w / gap 4w, butt cap
  //   - dotted → zero-length dash with a round cap, gap 3w → renders as dots
  gridLineStroke(style, width) {
    if (style === "dotted") return { dasharray: `0 ${width * 3}`, linecap: "round" };
    if (style === "dashed") return { dasharray: `${width * 6} ${width * 4}`, linecap: "butt" };
    return { dasharray: "none", linecap: "butt" };
  }

  // Build the inline SVG grid overlay (card #1916). Replaces the old CSS
  // linear-gradient background, which could only draw solid lines and left the
  // closing right/bottom lines outside the box. The <pattern> path draws the top
  // + left edge of each cell, so it covers every internal line plus the left/top
  // boundary; the inset outer <rect> then closes the right + bottom boundary
  // cleanly (inset by half the stroke so the stroke is not clipped by the SVG
  // edge). preserveAspectRatio="none" is fine — the stage scales uniformly, so a
  // px stroke width stays consistent. Editor-only chrome; never published.
  gridOverlaySvg() {
    const cell = this.gridSize();
    const colour = this.gridColor();
    const width = this.gridLineWidth();
    const { dasharray, linecap } = this.gridLineStroke(this.gridLineStyle(), width);
    const inset = width / 2;
    const canvasW = this.canvasW;
    const canvasH = this.canvasH;

    return `<svg width="100%" height="100%" viewBox="0 0 ${canvasW} ${canvasH}" preserveAspectRatio="none" aria-hidden="true">` +
      `<defs>` +
      `<pattern id="deck-grid-pattern" width="${cell}" height="${cell}" patternUnits="userSpaceOnUse">` +
      `<path d="M ${cell} 0 L 0 0 L 0 ${cell}" fill="none" stroke="${colour}" stroke-width="${width}" stroke-dasharray="${dasharray}" stroke-linecap="${linecap}" />` +
      `</pattern>` +
      `</defs>` +
      `<rect width="${canvasW}" height="${canvasH}" fill="url(#deck-grid-pattern)" />` +
      `<rect x="${inset}" y="${inset}" width="${canvasW - width}" height="${canvasH - width}" fill="none" stroke="${colour}" stroke-width="${width}" stroke-dasharray="${dasharray}" stroke-linecap="${linecap}" />` +
      `</svg>`;
  }

  // Reflect the current snap flag in the canvas chrome: the grid overlay layered
  // on top of the slide and the active/inactive styling on the toolbar button.
  // The grid is a dedicated overlay div (sibling after canvasMount) so it sits
  // ABOVE the opaque slide surface; pointer-events: none keeps it from blocking
  // selection. It never reaches the published partial or the Preview render. The
  // overlay is an inline SVG pattern (gridOverlaySvg) built from the deck's grid
  // density, colour, line style, and line width, so the VISIBLE grid matches the
  // snap target exactly and closes at all four edges (card #1916). The SVG is
  // rebuilt every call so a live theme change re-tints/re-draws immediately; when
  // snap is off the div is display:none, so a stale SVG inside would be harmless,
  // but rebuilding keeps it honest.
  applySnapState() {
    if (this.hasGridOverlayTarget) {
      this.gridOverlayTarget.classList.toggle("deck-editor-grid-overlay--on", this.snap);
      this.gridOverlayTarget.innerHTML = this.gridOverlaySvg();
    }
    if (this.hasSnapButtonTarget) {
      const button = this.snapButtonTarget;
      button.setAttribute("aria-pressed", this.snap ? "true" : "false");
      button.classList.toggle("text-sapphire", this.snap);
      button.classList.toggle("bg-sapphire/10", this.snap);
      button.classList.toggle("text-gray-500", !this.snap);
    }
  }

  // Round a canvas-space value to the grid when snap is on, else to whole pixels.
  // Used by both startMove and startResize so move and resize share one gesture.
  // The grid step is the deck's chosen cell size (card #1916).
  snapValue(value) {
    if (this.snap) {
      const grid = this.gridSize();
      return Math.round(value / grid) * grid;
    }
    return Math.round(value);
  }

  // ---- Tabs (card #1901) -------------------------------------------------

  // SurveyJS-style top navigation. Each tab toggles a sibling panel; switching is
  // pure show/hide in the controller (no server round-trip). The active tab is
  // held in `activeTabValue`; its changed-callback re-applies panel visibility +
  // tab styling and lazily (re)renders the panel that needs working state.
  switchTab(event) {
    const tab = event.currentTarget.dataset.deckTab;
    if (tab) this.activeTabValue = tab;
  }

  activeTabValueChanged() {
    this.showActiveTab();
  }

  showActiveTab() {
    const active = this.activeTabValue || "designer";

    this.panelTargets.forEach((panel) => {
      const match = panel.dataset.deckTab === active;
      panel.classList.toggle("hidden", !match);
      // Panels are flex containers when shown; restore the layout display.
      if (match && (active === "designer" || active === "json")) {
        panel.classList.add("flex");
      } else {
        panel.classList.remove("flex");
      }
    });

    // Active-tab styling — sapphire underline, per WIN tabs convention.
    this.tabTargets.forEach((tab) => {
      const match = tab.dataset.deckTab === active;
      tab.setAttribute("aria-selected", match ? "true" : "false");
      tab.classList.toggle("border-sapphire", match);
      tab.classList.toggle("text-sapphire", match);
      tab.classList.toggle("border-transparent", !match);
      tab.classList.toggle("text-gray-600", !match);
    });

    // Panels that mirror working state must be (re)rendered on entry so they
    // reflect any edits made on the Designer tab.
    if (active === "designer") this.fitStage();
    if (active === "preview") this.renderPreview();
    if (active === "themes") this.renderThemes();
    if (active === "json") this.syncJson();
    if (active === "measures") this.renderMeasures();
    // Whole-report Notes / Prompt tabs (card #2129): lazily load the deck-scope
    // single-annotation editor frame the first time the tab is opened.
    if (active === "notes") this.loadAnnotationEditor("deck", "note");
    if (active === "prompt") this.loadAnnotationEditor("deck", "prompt");
  }

  // ---- Preview (card #1901) ----------------------------------------------

  // Read-only WYSIWYG: render each slide from the WORKING editor state (the same
  // deck the Designer canvas draws from), reusing elementNode, but WITHOUT the
  // canvas's selection/resize handles. This shows slot placeholders like
  // {quality_pct}, NOT server-resolved values — resolveValue returns the slot
  // name as a placeholder (see resolveValue). It is byte-faithful to the editor
  // canvas, not to the published render. Server-resolved values live in
  // result_json server-side; surfacing them here would need a resolve endpoint
  // (known limitation, not yet built).
  //
  // The Preview tab mounts the SHARED viewer primitive (card #1902). We build the
  // slide PAGES (.airdrop-viewer__slide > .airdrop-slide-scale > .airdrop-slide)
  // into the viewer's deck mount, then call the viewer's #refresh so it re-scans
  // and re-draws its chrome. The viewer owns all presentation behaviour
  // (fullscreen, slideshow, nav, counter, print, filmstrip); the editor only
  // supplies the slide DOM, the same idiom it already uses for the canvas.
  renderPreview() {
    const deck = this.previewDeckMount();
    if (!deck) return;
    deck.replaceChildren();

    if (!this.deck.slides.length) {
      deck.appendChild(this.hint("This deck has no slides yet. Add slides on the Designer tab."));
      this.refreshPreviewViewer();
      return;
    }

    // Preview RESOLVES live values; the Designer canvas stays literal (card #2061).
    // elementNode() is shared by both surfaces, so we set a transient flag while the
    // (synchronous) slide surfaces are built and reset it in a finally — the canvas,
    // which never sets the flag, is unaffected. The viewer re-scan below reads no
    // values, so it sits safely outside the flag.
    // Rebuild the deck-wide page-number sequence once per preview render (card #2093)
    // so each surface draws its ABSOLUTE "position / total" — parity with the published render.
    this._pageSeq = this.pageNumberSequence();

    this._resolveValues = true;
    try {
      // Each slide yields ONE OR MORE surfaces: a canvas/title slide yields one, a
      // collection slide EXPANDS into its N print pages (mirroring the server
      // CollectionExpander / Deck#deck_slides_expanded), so the viewer paginates across
      // the real generated slides exactly as they will publish. We flatten to a single
      // sequence and number the viewer pages over it, so the slide counter is faithful
      // to the published deck length, not the authored slide count.
      let pageNumber = 0;
      this.deck.slides.forEach((slide, slideIndex) => {
        this.previewSlideSurfaces(slide, slideIndex).forEach((surface) => {
          pageNumber += 1;
          const page = document.createElement("div");
          page.className = "airdrop-viewer__slide";
          page.id = `slide-${pageNumber}`;
          page.setAttribute("data-airdrop-report-viewer-target", "slide");

          const frame = document.createElement("div");
          frame.className = "airdrop-slide-scale rounded-lg border border-gray-300 bg-white shadow-sm";
          frame.appendChild(surface);

          page.appendChild(frame);
          deck.appendChild(page);
        });
      });
    } finally {
      this._resolveValues = false;
    }

    this.refreshPreviewViewer();
  }

  // The preview surface(s) for one authored slide. Canvas and title slides yield one
  // surface; a COLLECTION slide expands into its N print pages (one surface each),
  // faithfully mirroring Airdrop::CollectionExpander. The viewer then paginates over
  // the flattened sequence, so Preview = the published deck, page for page.
  previewSlideSurfaces(slide, slideIndex) {
    if (slide.type === "collection") return this.previewCollectionSurfaces(slide, slideIndex);
    const surface = this.previewSlideNode(slide);
    // Deck-wide page number (card #2093) on the single preview surface, drawn IF this
    // slide shows one — parity with renderCanvasSlide and the published render.
    this.appendPageNumber(surface, slideIndex, 1);
    return [surface];
  }

  // Expand a collection slide into its N print-page surfaces, mirroring
  // Airdrop::CollectionExpander#slides: pages = max(ceil(image_count / page_size), 1),
  // each page a deep-faithful copy of the authored chrome + an auto-grid of THAT
  // page's image slice + the "n / N" badge, using the SAME geometry as the expander.
  //
  // PRINT-FAITHFUL (unlike the authoring canvas): a short final page shows ONLY its
  // actual images with NO dashed placeholders — matching grid_cells' filter_map, which
  // skips blank urls and so emits FEWER cells on a short page rather than padding it.
  previewCollectionSurfaces(slide, slideIndex) {
    slide.collection = slide.collection || { source: "album" };
    const collection = slide.collection;

    const album = this.albumFor(collection.source_id);
    const imageUrls = album ? (album.image_urls || []) : [];
    const imageCount = album ? (album.image_count || 0) : 0;

    const pageSize = Math.max(1, this.int(collection.page_size) || 6);
    const columns = Math.max(1, this.int(collection.columns) || 3);
    // Single source of truth for the page count (card #2093) — the same helper the
    // page-number sequence uses, so the preview surfaces and the numbers agree.
    const pages = this.collectionPageCount(slide);

    // Caption parity (card: album captions): the same album-driven fields the designer
    // canvas + the expander use, passed through so each preview page draws the stacked
    // caption lines beneath every real image, index-aligned with the url slice.
    const captionFields = this.collectionCaptionFields(album);
    const band = this.collectionCaptionBand(album);
    const allCaptions = album ? (album.captions || []) : [];

    const surfaces = [];
    for (let page = 1; page <= pages; page++) {
      const start = (page - 1) * pageSize;
      const slice = imageUrls.slice(start, start + pageSize);
      const captionSlice = allCaptions.slice(start, start + pageSize);
      surfaces.push(this.previewCollectionPageNode(
        slide, collection, slice, page, pages, pageSize, columns, captionFields, captionSlice, band, slideIndex
      ));
    }
    return surfaces;
  }

  // One faithful collection PAGE surface for the Preview tab: the authored chrome
  // elements + the auto-grid of this page's image slice + the "n / N" badge, using the
  // SAME geometry as CollectionExpander. Print-faithful: the grid emits a cell ONLY for
  // a non-blank url (mirroring grid_cells' filter_map), so a short final page shows its
  // real images and NO placeholders — exactly what publishes.
  previewCollectionPageNode(slide, collection, slice, page, pages, pageSize, columns, captionFields = [], captionSlice = [], band = 0, slideIndex = -1) {
    const surface = document.createElement("div");
    surface.className = "airdrop-slide airdrop-slide--canvas";
    surface.style.width = `${this.canvasW}px`;
    surface.style.height = `${this.canvasH}px`;
    surface.style.fontFamily = this.deckFontFamily();
    const bg = this.resolveBackground(slide.options && slide.options.background);
    surface.style.backgroundColor = bg.backgroundColor;
    if (bg.ink) surface.style.color = bg.ink;

    // Authored static chrome — the normal element render path (no handles, no link
    // badges; this is a faithful preview, not the canvas), identical on every page.
    (slide.elements || []).forEach((element) => surface.appendChild(this.elementNode(element)));

    // Auto-grid image cells — geometry mirrors CollectionExpander#grid_cells. Rows are
    // sized to a full page_size (the grid does not reflow on a short page), but a cell
    // is emitted ONLY for a real url, so a short page shows fewer images and NO dashed
    // placeholders — print parity with grid_cells' filter_map.
    const grid = this.collectionGridRegion(collection);
    const rows = Math.max(Math.ceil(pageSize / columns), 1);
    const cellW = Math.floor((grid.w - (grid.gap * (columns - 1))) / columns);
    const cellH = Math.floor((grid.h - (grid.gap * (rows - 1))) / rows);
    // Image strip = cell minus the caption band (0 when the album has no fields).
    const imageH = cellH - band;

    slice.forEach((url, index) => {
      if (!url) return;
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = grid.x + (col * (cellW + grid.gap));
      const y = grid.y + (row * (cellH + grid.gap));
      const img = document.createElement("img");
      img.className = "airdrop-el airdrop-el--image";
      // Radius + shadow "pop" + the shrunk image height, in parity with the expander
      // image cell and _canvas's image branch.
      let style = `left:${x}px;top:${y}px;width:${cellW}px;height:${imageH}px;object-fit:cover;`;
      if (COLLECTION_IMAGE_RADIUS) style += `border-radius:${COLLECTION_IMAGE_RADIUS}px;`;
      if (COLLECTION_IMAGE_SHADOW) style += "box-shadow:0 2px 8px rgba(0,0,0,0.18);";
      img.setAttribute("style", style);
      img.src = url;
      img.alt = "";
      surface.appendChild(img);

      // Stacked caption lines beneath the image — index-aligned with the url slice,
      // mirroring CollectionExpander#caption_cells.
      this.collectionCaptionNodes(captionFields, captionSlice[index], x, y + imageH, cellW)
        .forEach((node) => surface.appendChild(node));
    });

    // Deck-wide page number (card #2093), bottom-right — this collection PAGE's
    // ABSOLUTE "position / total" in the deck sequence, drawn IF the collection slide
    // shows numbers. Parity with the published render (Deck#number_pages).
    this.appendPageNumber(surface, slideIndex, page);

    return surface;
  }

  // The viewer's deck mount inside the Preview panel. The previewMount element
  // hosts the viewer controller; its deck target is where slide pages live.
  previewDeckMount() {
    if (!this.hasPreviewMountTarget) return null;
    return this.previewMountTarget.querySelector(
      "[data-airdrop-report-viewer-target='deck']"
    );
  }

  // Tell the mounted viewer to re-scan its slides + redraw chrome. The outlet is
  // present once the viewer controller has connected; guard for the brief window
  // before that (Stimulus connects child controllers asynchronously).
  refreshPreviewViewer() {
    if (this.hasAirdropReportViewerOutlet) {
      this.airdropReportViewerOutlet.refresh();
    }
  }

  // One slide rendered exactly as the canvas draws it, minus the handle overlay.
  previewSlideNode(slide) {
    if (slide.type === "canvas") {
      const surface = document.createElement("div");
      surface.className = "airdrop-slide airdrop-slide--canvas";
      surface.style.width = `${this.canvasW}px`;
      surface.style.height = `${this.canvasH}px`;
      surface.style.fontFamily = this.deckFontFamily();
      // Slide background (card #1916): parity with renderCanvasSlide / partial.
      const bg = this.resolveBackground(slide.options && slide.options.background);
      surface.style.backgroundColor = bg.backgroundColor;
      if (bg.ink) surface.style.color = bg.ink;
      (slide.elements || []).forEach((element) => surface.appendChild(this.elementNode(element)));
      return surface;
    }

    // Non-canvas slides are preview-only in the editor too.
    const surface = document.createElement("div");
    surface.className = "airdrop-slide airdrop-slide--title";
    surface.style.width = `${this.canvasW}px`;
    surface.style.height = `${this.canvasH}px`;
    surface.style.fontFamily = this.deckFontFamily();
    const title = document.createElement("h2");
    title.className = "airdrop-slide__title";
    title.textContent = this.slideLabel(slide) + " slide";
    surface.appendChild(title);
    return surface;
  }

  // ---- Themes (card #1901) -----------------------------------------------

  // The deck-level design choices, each stored on deck.design (font + snap grid).
  // Choosing either marks dirty and re-applies live. Both ride along in deck_json
  // on Save; the grid is editor-only chrome so the published partial ignores it.
  renderThemes() {
    if (!this.hasThemesMountTarget) return;
    this.themesMountTarget.replaceChildren();

    // Canvas size (ADR-0077, surface 2). The most deck-structural setting, so it
    // leads the panel. Unlike font/grid (which write to deck.design and ride in
    // deck_json), the canvas dims are real Deck columns — they are applied live to
    // the Stimulus values + CSS custom properties and persisted as their own
    // top-level params on Save, never into deck_json.
    const canvas = document.createElement("h2");
    canvas.className = "text-sm font-semibold uppercase tracking-wide text-gray-500";
    canvas.textContent = "Canvas";
    this.themesMountTarget.appendChild(canvas);

    const presetKey = this.canvasPresetKey();
    this.themesMountTarget.appendChild(
      this.selectFieldKeyed(
        "Canvas size", presetKey,
        ["1280x720", "1920x1080", "custom"],
        ["Slidepack (1280 × 720)", "Dashboard (1920 × 1080)", "Custom"],
        (key) => {
          const preset = CANVAS_PRESETS[key];
          if (preset) {
            this._canvasCustom = false;
            this.applyCanvasSize(preset.w, preset.h);
          } else {
            this._canvasCustom = true;
          }
          // Re-render the panel to add/remove the custom width/height fields.
          this.renderThemes();
        }
      )
    );

    // Custom dims: shown when the deck matches no preset, or the author just chose
    // "Custom". Number fields apply live but do NOT re-render per keystroke (that
    // would steal focus) — they only markDirty + redraw the canvas.
    if (presetKey === "custom") {
      this.themesMountTarget.appendChild(
        this.numberField("Width", this.canvasW, (v) => {
          if (v > 0) this.applyCanvasSize(v, this.canvasH);
        })
      );
      this.themesMountTarget.appendChild(
        this.numberField("Height", this.canvasH, (v) => {
          if (v > 0) this.applyCanvasSize(this.canvasW, v);
        })
      );
    }

    const fontHeading = document.createElement("h2");
    fontHeading.className = "mt-2 text-sm font-semibold uppercase tracking-wide text-gray-500";
    fontHeading.textContent = "Font";
    this.themesMountTarget.appendChild(fontHeading);

    const fontKeys = Object.keys(this.fontsValue || {});
    if (fontKeys.length) {
      const labels = fontKeys.map((key) => this.fontsValue[key].label || key);
      this.themesMountTarget.appendChild(
        this.selectFieldKeyed("Font", this.deckFontKey(), fontKeys, labels, (key) => {
          this.deck.design = this.deck.design || {};
          this.deck.design.fonts = key;
          this.markDirty();
          this.renderCanvas();
        })
      );
    } else {
      this.themesMountTarget.appendChild(this.hint("No fonts are registered for this deck."));
    }

    // Page numbers (card #2093). The deck-wide MASTER switch — when on, every shown
    // slide prints an ABSOLUTE bottom-right "position / total" number (total = full
    // deck size; cover/title and hidden slides count but show no badge; per-slide
    // override in slide properties). Written to
    // deck.design.page_numbers, the single source of truth Deck#number_pages reads.
    const pageNumHeading = document.createElement("h2");
    pageNumHeading.className = "mt-2 text-sm font-semibold uppercase tracking-wide text-gray-500";
    pageNumHeading.textContent = "Page numbers";
    this.themesMountTarget.appendChild(pageNumHeading);
    this.themesMountTarget.appendChild(
      this.checkboxField("Show page numbers", this.deck?.design?.page_numbers === true, (checked) => {
        this.deck.design = this.deck.design || {};
        this.deck.design.page_numbers = checked ? true : undefined;
        this.markDirty();
        this.renderCanvas();
      })
    );

    // Snap grid (card #1916). A per-deck design choice between three preset cell
    // sizes — written to deck.design.grid (mirroring the font picker's pattern),
    // then re-applied live so the canvas overlay updates immediately when snap is
    // on. Editor-only: it shapes authoring, never the published render.
    const grid = document.createElement("h2");
    grid.className = "mt-2 text-sm font-semibold uppercase tracking-wide text-gray-500";
    grid.textContent = "Grid";
    this.themesMountTarget.appendChild(grid);

    const gridKeys = ["fine", "medium", "coarse"];
    const gridLabels = ["Fine", "Medium", "Coarse"];
    const currentGrid = GRID_PRESETS[this.deck?.design?.grid] ? this.deck.design.grid : DEFAULT_GRID;
    this.themesMountTarget.appendChild(
      this.selectFieldKeyed("Snap grid", currentGrid, gridKeys, gridLabels, (key) => {
        this.deck.design = this.deck.design || {};
        this.deck.design.grid = key;
        this.markDirty();
        this.applySnapState();
      })
    );

    // Grid colour (card #1916). A per-deck design choice written to
    // deck.design.grid_color (mirroring grid density), re-applied live so the
    // overlay re-tints immediately. Reuses colorField: its text field is
    // authoritative and accepts CSS rgba strings, so the default white-alpha
    // value round-trips even though the swatch can only show #hex. Editor-only.
    this.themesMountTarget.appendChild(
      this.colorField("Grid colour", this.deck?.design?.grid_color || DEFAULT_GRID_COLOR, (v) => {
        this.deck.design = this.deck.design || {};
        this.deck.design.grid_color = v;
        this.markDirty();
        this.applySnapState();
      })
    );

    // Grid line style (card #1916). A per-deck design choice written to
    // deck.design.grid_line_style, re-applied live so the overlay redraws with
    // the new dash pattern immediately. Defaults to dashed. Editor-only.
    this.themesMountTarget.appendChild(
      this.selectFieldKeyed("Grid line", this.gridLineStyle(),
        ["solid", "dashed", "dotted"], ["Solid", "Dashed", "Dotted"], (key) => {
          this.deck.design = this.deck.design || {};
          this.deck.design.grid_line_style = key;
          this.markDirty();
          this.applySnapState();
        })
    );

    // Grid line width (card #1916). A per-deck design choice written to
    // deck.design.grid_line_width (px), floored at 1 so the line never vanishes,
    // re-applied live. Editor-only.
    this.themesMountTarget.appendChild(
      this.numberField("Grid line width (px)", this.gridLineWidth(), (v) => {
        this.deck.design = this.deck.design || {};
        this.deck.design.grid_line_width = Math.max(1, v);
        this.markDirty();
        this.applySnapState();
      })
    );
  }

  // The current canvas preset key for the Themes-tab select. Once the author has
  // explicitly chosen "Custom" (the transient _canvasCustom flag), it stays custom
  // even if the typed dims happen to land on a preset; otherwise it derives from
  // the deck's live dims — 1280×720 → Slidepack, 1920×1080 → Dashboard, else custom.
  canvasPresetKey() {
    if (this._canvasCustom) return "custom";
    const key = `${this.canvasW}x${this.canvasH}`;
    return CANVAS_PRESETS[key] ? key : "custom";
  }

  // Apply a canvas size change live (ADR-0077). Updates the Stimulus values (which
  // back the canvasW/canvasH getters used by fitStage + the SVG grid), the CSS
  // custom properties on the editor root (so the stage box in build.html.erb
  // resizes), then re-fits, redraws, and marks dirty so Save reflects the change.
  // The new dims persist on Save as their own params (see save()), not in deck_json.
  applyCanvasSize(w, h) {
    this.canvasWValue = w;
    this.canvasHValue = h;
    this.element.style.setProperty("--canvas-w", w);
    this.element.style.setProperty("--canvas-h", h);
    this.fitStage();
    this.renderCanvas();
    this.markDirty();
  }

  // ---- Measures (card #1904) ---------------------------------------------

  // Read-only deck-wide bindings overview. Walks every slide's bindable slots
  // (canvas elements with a `slot`, plus title slides' `data` slot map) and shows,
  // per slot: the slide it lives on, the slot name, the bound objective's resolved
  // name, and the value type. Unbound slots are surfaced too, visibly marked, so
  // gaps in wiring are obvious at a glance — the main point of this view.
  //
  // Objective names resolve from the SAME cached objectives list the binding
  // picker loads (lazy-loaded via objectivesUrl the first time the picker or this
  // tab is shown). Editing bindings stays in the Designer binding picker — this is
  // strictly read-only.
  //
  // Resolved current values are NOT shown: they live in result_json on the server,
  // not in the working deck state, and surfacing them would need a new endpoint.
  // Out of scope tonight (card #1904).
  renderMeasures() {
    if (!this.hasMeasuresMountTarget) return;
    this.measuresMountTarget.replaceChildren();

    const rows = this.collectBindableSlots();
    if (!rows.length) {
      this.measuresMountTarget.appendChild(this.hint("No data bindings in this deck yet."));
      return;
    }

    // Lazy-load objectives so bound slots can resolve a name (mirrors the binding
    // picker). The fetch callback re-renders Properties; re-render Measures too if
    // it is still the active panel when objectives arrive.
    if (!this.objectives && !this.objectivesLoading) this.loadObjectives();

    // Group rows by slide, rendering a section header per slide then its slots.
    let currentSlide = null;
    rows.forEach((row) => {
      if (row.slideIndex !== currentSlide) {
        currentSlide = row.slideIndex;
        this.measuresMountTarget.appendChild(this.measuresSlideHeader(row));
      }
      this.measuresMountTarget.appendChild(this.measuresRow(row));
    });
  }

  // Every bindable slot across the deck, in slide then element order. A row is
  // { slideIndex, slideLabel, slot, binding } where binding is the deck-level
  // bindings entry (or null when the slot is declared but unbound).
  collectBindableSlots() {
    const bindings = (this.deck && this.deck.bindings) || {};
    const rows = [];

    (this.deck.slides || []).forEach((slide, slideIndex) => {
      const label = `${slideIndex + 1}. ${this.slideLabel(slide)}`;

      // Canvas slides: each element that carries a slot is bindable.
      (slide.elements || []).forEach((element) => {
        if (!element.slot) return;
        rows.push({
          slideIndex,
          slideLabel: label,
          slot: element.slot,
          kind: element.kind,
          binding: bindings[element.slot] || null
        });
      });

      // Title slides carry a `data` slot map ({ slot => binding_name }); each slot
      // is bindable too. Title decks predate the canvas binding picker, so they
      // rarely have a deck-level bindings entry — they still belong in the overview.
      if (slide.data && typeof slide.data === "object") {
        Object.keys(slide.data).forEach((slot) => {
          rows.push({
            slideIndex,
            slideLabel: label,
            slot,
            kind: "data",
            binding: bindings[slot] || null
          });
        });
      }
    });

    return rows;
  }

  measuresSlideHeader(row) {
    const header = document.createElement("div");
    header.className = "mt-4 first:mt-0 border-b border-gray-200 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500";
    header.textContent = row.slideLabel;
    return header;
  }

  // One slot row: slot name + kind on the left, the resolved binding on the right.
  // A bound slot shows its objective name and value type as pills; an unbound slot
  // is marked "— not bound" in muted red so the gap reads at a glance.
  measuresRow(row) {
    const wrap = document.createElement("div");
    wrap.className = "flex items-center justify-between gap-3 rounded border border-gray-200 px-3 py-2 text-sm";

    const left = document.createElement("div");
    left.className = "flex min-w-0 flex-col";
    const name = document.createElement("span");
    name.className = "truncate font-medium text-gray-800";
    name.textContent = row.slot;
    const kind = document.createElement("span");
    kind.className = "text-2xs uppercase tracking-wide text-gray-400";
    kind.textContent = row.kind;
    left.appendChild(name);
    left.appendChild(kind);

    const right = document.createElement("div");
    right.className = "flex shrink-0 items-center gap-2";
    if (row.binding) {
      const objective = document.createElement("span");
      objective.className = "rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700";
      objective.textContent = this.bindingSourceLabel(row.binding);
      const value = document.createElement("span");
      value.className = "rounded bg-gray-100 px-2 py-0.5 text-2xs font-medium uppercase tracking-wide text-gray-600";
      value.textContent = row.binding.value || "display";
      right.appendChild(objective);
      right.appendChild(value);
    } else {
      const unbound = document.createElement("span");
      unbound.className = "text-xs font-medium text-red-500";
      unbound.textContent = "— not bound";
      right.appendChild(unbound);
    }

    wrap.appendChild(left);
    wrap.appendChild(right);
    return wrap;
  }

  // A human label for a binding row in the Measures overview, source-aware
  // (ADR-0070 §2). An objective binding resolves its name from the cached
  // objectives list; an image binding shows the picked asset's cached label
  // (captured at bind time) or its id. Reads the generalised source_id.
  bindingSourceLabel(binding) {
    const id = this.bindingSourceId(binding);
    if (binding.source === "image") {
      return (binding.label && String(binding.label)) || `Image #${id}`;
    }
    return this.objectiveName(id);
  }

  // Resolve a bound objective's name from the cached objectives list. Falls back
  // to the raw id while the list is still loading or when the objective is missing
  // (e.g. removed from the mission since the binding was authored).
  objectiveName(objectiveId) {
    const objective = (this.objectives || []).find((o) => String(o.id) === String(objectiveId));
    if (objective) return objective.name;
    if (!this.objectives) return `Objective #${objectiveId} (loading…)`;
    return `Objective #${objectiveId} (not found)`;
  }

  // ---- JSON (card #1901) -------------------------------------------------

  // Mirror the working deck into the textarea. Called on entering the JSON tab so
  // the view always reflects the current state, including edits made elsewhere.
  syncJson() {
    if (!this.hasJsonAreaTarget) return;
    this.jsonAreaTarget.value = JSON.stringify(this.deck, null, 2);
    this.clearJsonError();
  }

  // Editing the textarea marks the deck dirty (the user is changing it), but does
  // NOT apply until "Apply JSON" — so an in-progress, momentarily-invalid edit
  // never corrupts the working deck.
  jsonChanged() {
    this.markDirty();
  }

  // Parse the textarea and replace the working deck. Invalid JSON shows an inline
  // error and leaves the working deck untouched (guard, don't crash).
  applyJson() {
    if (!this.hasJsonAreaTarget) return;
    this.applyJsonText(this.jsonAreaTarget.value);
  }

  // Shared parse → validate → replace path used by both Apply JSON and Import
  // (card #1901). Invalid JSON or a non-object deck shows the inline error and
  // leaves the working deck untouched. On success the deck is replaced, the
  // textarea is re-synced, the canvas re-renders, and the deck is marked dirty.
  // Returns true when the deck was applied so callers can branch.
  applyJsonText(text) {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      this.showJsonError(`Invalid JSON: ${e.message}`);
      return false;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      this.showJsonError("Deck must be a JSON object.");
      return false;
    }

    this.deck = parsed;
    if (!Array.isArray(this.deck.slides)) this.deck.slides = [];
    if (!this.deck.bindings || typeof this.deck.bindings !== "object") this.deck.bindings = {};

    this.selectedSlide = this.deck.slides.length ? 0 : null;
    this.selectedElement = null;
    this.selectionIndices = [];
    // Whole-deck replace (card #2788): old slide indices are meaningless
    // against the new deck, so any in-progress multi-select is dropped.
    this.multiSelectedIndices.clear();
    this.clearJsonError();
    this.syncTextarea();
    this.markDirty();
    this.fitStage();
    this.renderAll();
    return true;
  }

  // Refresh the textarea from the working deck WITHOUT clearing an existing error
  // (syncJson clears it; this is used after applyJsonText, which owns error state).
  syncTextarea() {
    if (this.hasJsonAreaTarget) this.jsonAreaTarget.value = JSON.stringify(this.deck, null, 2);
  }

  // ---- JSON import / export / copy (card #1901) --------------------------

  // Import: trigger the hidden file input. The change handler reads + applies the
  // file through the same applyJsonText path as Apply JSON (object guard, inline
  // error on invalid) so there is one validation path, not two.
  openJsonImport() {
    if (this.hasJsonFileTarget) this.jsonFileTarget.click();
  }

  importJsonFile(event) {
    const input = event.currentTarget;
    const file = input.files && input.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => this.applyJsonText(String(reader.result));
    reader.onerror = () => this.showJsonError("Could not read the selected file.");
    reader.readAsText(file);

    // Reset so re-importing the same file fires change again.
    input.value = "";
  }

  // Export: download the working deck as a pretty-printed .json file via a
  // temporary object URL + anchor, revoked once the click is dispatched.
  exportJson() {
    const json = JSON.stringify(this.deck, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `deck-${this.reportIdValue || "export"}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  // Copy: write the current JSON to the clipboard, flipping the button label to
  // "Copied" briefly (a flash-and-revert idiom, like the Save button's icon).
  copyJson() {
    const json = JSON.stringify(this.deck, null, 2);
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(json).then(() => this.flashCopy());
  }

  flashCopy() {
    if (!this.hasCopyJsonLabelTarget) return;
    this.copyJsonLabelTarget.textContent = "Copied";
    setTimeout(() => { this.copyJsonLabelTarget.textContent = "Copy"; }, 1500);
  }

  showJsonError(message) {
    if (!this.hasJsonErrorTarget) return;
    this.jsonErrorTarget.textContent = message;
    this.jsonErrorTarget.classList.remove("hidden");
  }

  clearJsonError() {
    if (!this.hasJsonErrorTarget) return;
    this.jsonErrorTarget.textContent = "";
    this.jsonErrorTarget.classList.add("hidden");
  }

  // ---- Per-slide JSON editor (card #1935) ---------------------------------

  // Switch the centre-pane view between the WYSIWYG canvas (Design) and the
  // per-slide JSON editor. Reads data-view from the clicked button.
  switchSlideView(event) {
    const view = event.currentTarget.dataset.view || "design";

    if (this.hasSlideDesignViewTarget) {
      this.slideDesignViewTarget.classList.toggle("hidden", view !== "design");
    }
    if (this.hasSlideJsonViewTarget) {
      this.slideJsonViewTarget.classList.toggle("hidden", view !== "json");
    }
    // Per-slide Notes / Prompt views (card #2129) — two peer editor surfaces.
    if (this.hasSlideNotesViewTarget) {
      this.slideNotesViewTarget.classList.toggle("hidden", view !== "notes");
    }
    if (this.hasSlidePromptViewTarget) {
      this.slidePromptViewTarget.classList.toggle("hidden", view !== "prompt");
    }
    // Prior-cycle comparison view (card #2737) -- same peer toggle as Notes/Prompt.
    if (this.hasSlidePriorViewTarget) {
      this.slidePriorViewTarget.classList.toggle("hidden", view !== "prior");
    }

    // Update tab active styles.
    this.slideViewTabTargets.forEach(btn => {
      const active = btn.dataset.view === view;
      btn.classList.toggle("bg-white", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-gray-800", active);
      btn.classList.toggle("text-gray-500", !active);
    });

    // Seed the textarea when switching to JSON; load the slide note / prompt editor
    // frame when switching to its tab (card #2129).
    if (view === "json") this.syncSlideJson();
    if (view === "notes") this.loadAnnotationEditor("slide", "note", true);
    if (view === "prompt") this.loadAnnotationEditor("slide", "prompt", true);
    if (view === "prior") {
      this.loadPriorSlide(true);
      // Re-assert the shared Design/Prior scale on tab-open (card #2737
      // follow-up) -- covers the case where the frame's content was already
      // loaded from a previous tab visit (no fresh turbo:frame-load fires)
      // but the pane geometry changed while this tab was hidden.
      this.fitStage();
    }
  }

  // Seed the slide JSON textarea from the currently selected slide.
  // No-ops when no slide is selected (leaves textarea with placeholder text).
  syncSlideJson() {
    if (!this.hasSlideJsonAreaTarget) return;
    if (this.selectedSlide === null || this.selectedSlide === undefined) {
      this.slideJsonAreaTarget.value = "";
      this.clearSlideJsonError();
      // Disable the slide-scoped views (JSON, Notes, Prompt) when no slide is
      // selected — each edits one slide (card #2129 adds Notes to this gating).
      this.slideViewTabTargets.forEach(btn => {
        if (["json", "notes", "prompt"].includes(btn.dataset.view)) btn.disabled = true;
      });
      return;
    }
    // Enable the slide-scoped views.
    this.slideViewTabTargets.forEach(btn => {
      if (["json", "notes", "prompt"].includes(btn.dataset.view)) btn.disabled = false;
    });
    const slide = this.deck.slides[this.selectedSlide];
    this.slideJsonAreaTarget.value = slide ? JSON.stringify(slide, null, 2) : "";
    this.clearSlideJsonError();
  }

  slideJsonChanged() {
    this.markDirty();
  }

  // Parse the slide textarea and replace only the current slide in the deck.
  // Invalid JSON or a non-object shows an inline error and leaves the deck untouched.
  applySlideJson() {
    if (!this.hasSlideJsonAreaTarget) return;
    if (this.selectedSlide === null || this.selectedSlide === undefined) return;

    let parsed;
    try {
      parsed = JSON.parse(this.slideJsonAreaTarget.value);
    } catch (e) {
      this.showSlideJsonError(`Invalid JSON: ${e.message}`);
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      this.showSlideJsonError("Slide must be a JSON object.");
      return;
    }

    // Keep a stable slide id (card #2120): preserve the edited slide's existing id
    // when the pasted JSON omits one (the common case — authors edit content, not
    // the id), and mint a fresh one if neither has it.
    parsed.id = parsed.id || this.deck.slides[this.selectedSlide]?.id || crypto.randomUUID();

    this.deck.slides[this.selectedSlide] = parsed;
    this.clearSlideJsonError();
    this.markDirty();
    this.renderAll();
  }

  copySlideJson() {
    if (!this.hasSlideJsonAreaTarget) return;
    const json = this.slideJsonAreaTarget.value;
    if (!navigator.clipboard || !json) return;
    navigator.clipboard.writeText(json).then(() => this.flashSlideJsonCopy());
  }

  flashSlideJsonCopy() {
    if (!this.hasSlideJsonCopyLabelTarget) return;
    this.slideJsonCopyLabelTarget.textContent = "Copied";
    setTimeout(() => { this.slideJsonCopyLabelTarget.textContent = "Copy"; }, 1500);
  }

  showSlideJsonError(message) {
    if (!this.hasSlideJsonErrorTarget) return;
    this.slideJsonErrorTarget.textContent = message;
    this.slideJsonErrorTarget.classList.remove("hidden");
  }

  clearSlideJsonError() {
    if (!this.hasSlideJsonErrorTarget) return;
    this.slideJsonErrorTarget.textContent = "";
    this.slideJsonErrorTarget.classList.add("hidden");
  }

  // ---- Right-panel icon rail (card #1965) --------------------------------

  // Switch between the Properties and Activity panels in the right rail.
  // Reads data-panel from the clicked icon button.
  switchRightPanel(event) {
    this.activateRightPanel(event.currentTarget.dataset.panel);
  }

  // Show a right-rail panel by name and sync the icon-rail active state. Extracted
  // from switchRightPanel (card #1977) so editPanelContent() can bring the
  // Properties panel forward without synthesising a click event.
  activateRightPanel(panel) {
    this.rightPanelTargets.forEach(el => {
      el.classList.toggle("hidden", el.dataset.rightPanel !== panel);
    });

    this.rightPanelIconTargets.forEach(btn => {
      const active = btn.dataset.panel === panel;
      btn.classList.toggle("text-sapphire", active);
      btn.classList.toggle("bg-white", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-gray-400", !active);
    });

  }

  // ---- Notes & Prompts (card #2129) --------------------------------------
  //
  // One model (DeckAnnotation, foundation #2120), four single-annotation EDITOR
  // surfaces: deck note, deck prompt, slide note, slide prompt. Each is a giant
  // page-filling Lexxy editor in its own turbo frame
  // (deck_annotation_editor_<scope>_<kind>), loaded lazily by setting frame.src to
  // /decks/:id/annotations?view=editor&scope=…&kind=…[&slide_uuid=…]. No HTML is
  // built here — the server renders the frame.

  // The CURRENT slide's stable UUID (foundation #2120), or "" when nothing is
  // selected. Slide-scoped annotations key off THIS id, never the array index.
  currentSlideUuid() {
    const slide = this.currentSlide();
    return slide && slide.id ? slide.id : "";
  }

  // Point an annotation editor frame at its single-annotation read for the given
  // scope/kind. Slide-scope surfaces also pass the current slide UUID. A per-frame
  // last-loaded guard (keyed by scope+kind+uuid) skips redundant fetches; `force`
  // refetches even when nothing changed (used on tab/slide-view open).
  loadAnnotationEditor(scope, kind, force = false) {
    if (!this.hasAnnotationsUrlValue) return;
    const frame = document.getElementById(`deck_annotation_editor_${scope}_${kind}`);
    if (!frame) return;

    const uuid = scope === "slide" ? this.currentSlideUuid() : "";
    const key = `${scope}:${kind}:${uuid}`;
    this._editorKeys = this._editorKeys || {};
    if (!force && this._editorKeys[`${scope}:${kind}`] === key) return;
    this._editorKeys[`${scope}:${kind}`] = key;

    const url = new URL(this.annotationsUrlValue, window.location.origin);
    url.searchParams.set("view", "editor");
    url.searchParams.set("scope", scope);
    url.searchParams.set("kind", kind);
    if (uuid) url.searchParams.set("slide_uuid", uuid);
    frame.src = url.pathname + url.search;
  }

  // Re-fetch whichever slide annotation surface is currently on screen for the
  // newly selected slide. Called from renderAll() (every slide-change path runs
  // it), but only does work when a slide editor surface is actually visible — it
  // never builds DOM, it just re-points a frame src so the server re-renders.
  refreshAnnotations() {
    if (this.hasSlideNotesViewTarget && !this.slideNotesViewTarget.classList.contains("hidden")) {
      this.loadAnnotationEditor("slide", "note");
    }
    if (this.hasSlidePromptViewTarget && !this.slidePromptViewTarget.classList.contains("hidden")) {
      this.loadAnnotationEditor("slide", "prompt");
    }
  }

  // ---- Slide lineage / historical scroll-back (card #2737) ----------------
  //
  // A "Prior cycle" slide-view tab, alongside Design/JSON/Notes/Prompt, shown
  // ONLY when the selected slide's name is one of the two whitelisted kinds
  // this slice covers (Title / Results in Focus, PRIOR_CYCLE_SLIDE_NAMES --
  // server-enforced too, see Deck::PRIOR_CYCLE_SLIDE_NAMES / #prior_cycle_slide).
  // The panel is a single lazily-loaded turbo frame the server renders
  // read-only from the client's immediately-prior ClientCycle's matching
  // slide -- no HTML is built here, the frame's src is just re-pointed,
  // mirroring loadAnnotationEditor's own pattern exactly.

  // Show/hide + enable/disable the "Prior cycle" tab for the newly selected
  // slide (called from renderAll() -- every slide-change path runs it). When
  // the tab is currently the active view and the new slide no longer
  // qualifies, falls back to Design rather than leave a hidden tab active.
  syncPriorSlideTab() {
    if (!this.hasPriorSlideTabTarget) return;

    const slide = this.currentSlide();
    const eligible = !!slide && PRIOR_CYCLE_SLIDE_NAMES.includes(slide.name);

    this.priorSlideTabTarget.classList.toggle("hidden", !eligible);
    this.priorSlideTabTarget.disabled = !eligible;

    const panelOpen = this.hasSlidePriorViewTarget && !this.slidePriorViewTarget.classList.contains("hidden");
    if (!panelOpen) return;

    if (eligible) {
      this.loadPriorSlide();
    } else {
      this.switchSlideView({ currentTarget: { dataset: { view: "design" } } });
    }
  }

  // Point the prior-slide frame at its read for the CURRENTLY selected slide.
  // A last-loaded guard (keyed by slide id) skips redundant fetches when
  // nothing changed; `force` refetches regardless (used on tab-open).
  loadPriorSlide(force = false) {
    if (!this.hasPriorSlideUrlValue) return;
    const frame = document.getElementById("deck_prior_slide");
    if (!frame) return;

    const slide = this.currentSlide();
    if (!slide) return;

    const key = slide.id || slide.name;
    if (!force && this._priorSlideKey === key) return;
    this._priorSlideKey = key;

    const url = new URL(this.priorSlideUrlValue, window.location.origin);
    url.searchParams.set("slide_name", slide.name || "");
    frame.src = url.pathname + url.search;
  }

  switchLeftPanel(event) {
    const panel = event.currentTarget.dataset.panel;

    this.leftPanelTargets.forEach(el => {
      el.classList.toggle("hidden", el.dataset.leftPanel !== panel);
    });

    this.leftPanelIconTargets.forEach(btn => {
      const active = btn.dataset.panel === panel;
      btn.classList.toggle("text-sapphire", active);
      btn.classList.toggle("bg-white", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-gray-400", !active);
      btn.classList.toggle("hover:text-gray-600", !active);
    });
  }

  // ---- Rail resize (card #1966) ------------------------------------------

  startRailResize(event) {
    event.preventDefault();
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = this.railAsideTarget.offsetWidth;
    this._boundDoRailResize = this.doRailResize.bind(this);
    this._boundStopRailResize = this.stopRailResize.bind(this);
    document.addEventListener("mousemove", this._boundDoRailResize);
    document.addEventListener("mouseup", this._boundStopRailResize);
  }

  doRailResize(event) {
    const delta = this._resizeStartX - event.clientX;
    const newWidth = Math.max(240, Math.min(480, this._resizeStartWidth + delta));
    this.railAsideTarget.style.width = `${newWidth}px`;
  }

  stopRailResize() {
    document.removeEventListener("mousemove", this._boundDoRailResize);
    document.removeEventListener("mouseup", this._boundStopRailResize);
    localStorage.setItem("deck-editor-rail-width", String(this.railAsideTarget.offsetWidth));
    this.fitStage();
  }

  startLeftRailResize(event) {
    event.preventDefault();
    this._leftResizeStartX = event.clientX;
    this._leftResizeStartWidth = this.leftRailAsideTarget.offsetWidth;
    this._boundDoLeftRailResize = this.doLeftRailResize.bind(this);
    this._boundStopLeftRailResize = this.stopLeftRailResize.bind(this);
    document.addEventListener("mousemove", this._boundDoLeftRailResize);
    document.addEventListener("mouseup", this._boundStopLeftRailResize);
  }

  doLeftRailResize(event) {
    const delta = event.clientX - this._leftResizeStartX;
    const newWidth = Math.max(160, Math.min(480, this._leftResizeStartWidth + delta));
    this.leftRailAsideTarget.style.width = `${newWidth}px`;
  }

  stopLeftRailResize() {
    document.removeEventListener("mousemove", this._boundDoLeftRailResize);
    document.removeEventListener("mouseup", this._boundStopLeftRailResize);
    localStorage.setItem("deck-editor-left-rail-width", String(this.leftRailAsideTarget.offsetWidth));
    this.fitStage();
  }

  // ---- Render ------------------------------------------------------------

  renderAll() {
    this.renderSlideList();
    this.renderCanvas();
    this.renderProperties();
    this.syncSlideJson();
    // Re-point the Notes panel / Prompt view at the current slide when on screen
    // (card #2121). Guarded internally so an element edit (also a renderAll) does
    // not refetch — only a slide change moves the slide_uuid.
    this.refreshAnnotations();
    // Show/hide the "Prior cycle" tab for the current slide, and keep an
    // already-open panel in sync (card #2737).
    this.syncPriorSlideTab();
  }

  renderSlideList() {
    if (!this.hasSlideListTarget) return;

    // Multi-select drag (card #2788) tracks its own DOM-side selection state
    // via Sortable.utils.select/deselect (MultiDrag's selectedClass toggle),
    // but this method REPLACES every row on every call -- so the plugin's
    // internal multiDragElements list would otherwise accumulate references
    // to detached nodes. Deselect every currently-registered row before
    // tearing the DOM down; multiSelectedIndices (the Set on the controller)
    // is the single source of truth and gets fully reapplied to the fresh
    // rows below.
    Array.from(this.slideListTarget.children).forEach((row) => Sortable.utils.deselect(row));
    this.slideListTarget.replaceChildren();

    this.deck.slides.forEach((slide, index) => {
      const frag = this.slideRowTemplateTarget.content.cloneNode(true);
      const row = frag.querySelector("[data-deck-editor-target='slideRow']");
      row.dataset.index = index;
      row.querySelector("[data-role='index']").textContent = index + 1;
      row.querySelector("[data-role='label']").textContent = this.slideLabel(slide);
      // Multi-select checkbox (card #2788 revision) -- rows are fully replaced on
      // every render, so the checkbox's checked state is re-derived from
      // multiSelectedIndices (the single source of truth) rather than surviving
      // in the DOM. This is what keeps a row checked after a re-render triggered
      // by a completed drag, a missed/cancelled drag, or any other renderAll --
      // nothing here clears the Set, only toggleSlideMultiselect does.
      row.querySelector("[data-role='multiselect']").checked = this.multiSelectedIndices.has(index);
      if (index === this.selectedSlide) {
        row.classList.add("bg-gray-100", "border-gray-300");
      }
      // Drift dot: amber indicator when this slide's template has changed (card #2007).
      if (this._isSlideDrifted(slide)) {
        row.classList.add("relative");
        const dot = document.createElement("span");
        dot.className = "absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-400";
        dot.title = "Template has changed";
        row.appendChild(dot);
      }
      // "Hidden from client" toggle (card #3204) -- greys the row and flips
      // the eye/eye-slash icon to reflect this slide's CURRENT slide.hidden,
      // re-derived on every render exactly like the drift dot above (neither
      // is persisted DOM state -- both are recomputed from this.deck.slides
      // each time renderSlideList() replaces every row).
      const hiddenToggle = row.querySelector("[data-role='hiddenToggle']");
      if (hiddenToggle) {
        const isHidden = slide.hidden === true;
        row.classList.toggle("opacity-50", isHidden);
        hiddenToggle.setAttribute("aria-pressed", String(isHidden));
        hiddenToggle.title = isHidden ? "Show to client (currently hidden)" : "Hide from client";
        hiddenToggle.querySelector("[data-role='eyeIcon']").classList.toggle("hidden", isHidden);
        hiddenToggle.querySelector("[data-role='eyeSlashIcon']").classList.toggle("hidden", !isHidden);
      }
      this.slideListTarget.appendChild(frag);
      // Multi-select highlight + Sortable drag-tracking state (card #2788), in
      // one call -- Sortable.utils.select both toggles the selectedClass
      // (deck-editor-slide-multiselected) and registers the row so a drag
      // starting from ANY selected row carries the whole set. Must run AFTER
      // appendChild: select() resolves the owning Sortable instance off
      // el.parentNode.
      if (this.multiSelectedIndices.has(index)) {
        Sortable.utils.select(row);
      }
    });
  }

  slideLabel(slide) {
    // A slide carries its own human kind name (slide.name) when an author has set
    // one (card #1825 follow-on). Prefer it; else fall back to the capitalised
    // primitive type. This flows into the left slide list, the canvas preview
    // title, and the Measures grouping automatically.
    if (slide.name && slide.name.trim()) return slide.name.trim();
    const base = slide.variant || slide.type || "slide";
    return base.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
  }

  renderCanvas() {
    if (!this.hasCanvasMountTarget) return;
    this.canvasMountTarget.replaceChildren();

    // Rebuild the deck-wide page-number sequence once per render (card #2093) so each
    // per-surface draw reads the same ABSOLUTE "position / total" map.
    this._pageSeq = this.pageNumberSequence();

    const slide = this.currentSlide();
    if (!slide) return;

    if (slide.type === "canvas") {
      this.renderCanvasSlide(slide);
    } else if (slide.type === "collection") {
      this.renderCollectionSlide(slide);
    } else {
      this.renderReadonlySlide(slide);
    }
  }

  // Build the live canvas slide using the SAME classes/inline geometry as
  // app/views/decks/slides/_canvas.html.erb, then overlay a
  // selectable/draggable handle per element.
  renderCanvasSlide(slide) {
    const surface = document.createElement("div");
    surface.className = "airdrop-slide airdrop-slide--canvas";
    surface.style.width = `${this.canvasW}px`;
    surface.style.height = `${this.canvasH}px`;
    surface.style.fontFamily = this.deckFontFamily();
    // Slide background (card #1916): data-driven colour + readable default ink,
    // resolved in parity with the server partial. Only set color when definite.
    const bg = this.resolveBackground(slide.options && slide.options.background);
    surface.style.backgroundColor = bg.backgroundColor;
    if (bg.ink) surface.style.color = bg.ink;

    (slide.elements || []).forEach((element, elIndex) => {
      surface.appendChild(this.elementNode(element));
      // Link badge (ADR-0070 §3): a small chain-link affordance in the element's
      // corner so the author SEES the element carries a resource link. Editor chrome
      // ONLY — the published render wraps the element in <a> instead; the badge has
      // no ERB parity. Drawn after the element so it sits on top.
      const badge = this.linkBadgeNode(element);
      if (badge) surface.appendChild(badge);
      surface.appendChild(this.handleNode(element, elIndex));
    });

    // Combined dashed box over a 2+ multi-selection (card #1979), drawn on top of the
    // per-element handles. No-op for 0/1 selected.
    const groupBox = this.groupBoxNode(slide);
    if (groupBox) surface.appendChild(groupBox);

    // Deck-wide page number (card #2093), bottom-right — drawn IF this slide shows
    // one in the absolute sequence. Editor chrome mirroring Deck#page_number_element.
    this.appendPageNumber(surface, this.selectedSlide, 1);

    // Crop overlay (card #2130), drawn last so it sits above all elements/handles.
    if (this._cropMode) this.renderCropOverlay(surface, slide);

    this.canvasMountTarget.appendChild(surface);
  }

  // The collection-slide WYSIWYG (ADR-0070 §4b): a true preview of any render-time
  // PAGE. It draws the authored static chrome (the SAME element render path as a
  // canvas slide), PLUS an auto-grid of THAT page's images tiled with the EXACT
  // geometry of Airdrop::CollectionExpander#grid_cells, PLUS a bottom-right deck-wide
  // page number (card #2093, when this page shows one), PLUS a prev/next pager so the
  // author can flip through every expanded page. The image tiles + page number + pager
  // are EDITOR CHROME (like the link badge) — the real pages are expanded server-side;
  // nothing here is written to deck_json. The per-slide page index lives on
  // this.collectionPreviewPage (keyed by slide index), held on the controller like
  // snap, never in deck_json.
  //
  // PARITY WITH RUBY: the cell geometry below mirrors CollectionExpander#grid_cells
  // (rows/cell_w/cell_h/positions) and #grid_region (DEFAULT_GRID merged with the
  // per-slide override). The page count N mirrors the expander's pages =
  // max(ceil(count / page_size), 1). Keep the two in lockstep.
  //
  // AUTHORING vs PRINT: this is the AUTHORING canvas, so a short final page (or an
  // unpicked album) keeps the dashed placeholder cells, so the author always sees the
  // full grid shape. The print render (and the Preview tab) shows ONLY real images on
  // a short page — see previewCollectionPageNode for that print-faithful parity.
  renderCollectionSlide(slide) {
    slide.collection = slide.collection || { source: "album" };
    const collection = slide.collection;
    const slideIndex = this.selectedSlide;

    const album = this.albumFor(collection.source_id);
    const imageUrls = album ? (album.image_urls || []) : [];
    const imageCount = album ? (album.image_count || 0) : 0;

    const pageSize = Math.max(1, this.int(collection.page_size) || 6);
    const columns = Math.max(1, this.int(collection.columns) || 3);

    // N = the expander's page count via the shared helper (card #2093): ceil(
    // image_count / page_size), floored at 1 so an empty/unpicked album still shows
    // one chrome-only page. Single source of truth with the page-number sequence.
    const pages = this.collectionPageCount(slide);
    const page = this.collectionPage(slideIndex, pages);

    const surface = document.createElement("div");
    surface.className = "airdrop-slide airdrop-slide--canvas";
    surface.style.width = `${this.canvasW}px`;
    surface.style.height = `${this.canvasH}px`;
    surface.style.fontFamily = this.deckFontFamily();
    const bg = this.resolveBackground(slide.options && slide.options.background);
    surface.style.backgroundColor = bg.backgroundColor;
    if (bg.ink) surface.style.color = bg.ink;

    // Authored chrome — drawn through the EXACT canvas editing path (ADR-0070 §4b):
    // elementNode + link badge + a selectable/draggable handleNode per element, the
    // same three appends renderCanvasSlide makes. So the author selects, drags,
    // resizes, right-clicks, and edits properties of collection chrome identically
    // to a canvas slide; the edits write to slide.elements and the expander deep-dups
    // them onto every page. The DERIVED layer (grid cells, badge, pager) is drawn
    // AFTER this, on top, and is non-interactive (pointer-events:none) so it never
    // steals a click meant for a chrome element/handle beneath it.
    (slide.elements || []).forEach((element, elIndex) => {
      surface.appendChild(this.elementNode(element));
      const badge = this.linkBadgeNode(element);
      if (badge) surface.appendChild(badge);
      surface.appendChild(this.handleNode(element, elIndex));
    });

    // Combined dashed box over a 2+ multi-selection of chrome elements (card #1979),
    // drawn on top of the per-element handles. No-op for 0/1 selected.
    const groupBox = this.groupBoxNode(slide);
    if (groupBox) surface.appendChild(groupBox);

    // This page's image slice — urls[(page-1)*page_size, page_size]. Pages beyond the
    // url cap (image_count > image_urls.length) yield an empty slice, so every cell on
    // them falls back to a dashed placeholder (counted, never silently dropped).
    const sliceStart = (page - 1) * pageSize;
    const slice = imageUrls.slice(sliceStart, sliceStart + pageSize);
    // Index-aligned caption slice (card: album captions) + the album's enabled fields.
    // When fields are present each cell reserves a band at its bottom — the image
    // shrinks and the stacked caption lines sit in the freed strip, mirroring
    // CollectionExpander#grid_cells. With no fields the band is 0 (full-height image).
    const captionSlice = album ? (album.captions || []).slice(sliceStart, sliceStart + pageSize) : [];
    const captionFields = this.collectionCaptionFields(album);
    const band = this.collectionCaptionBand(album);

    // Auto-grid cells for this page — geometry mirrors CollectionExpander#grid_cells.
    const grid = this.collectionGridRegion(collection);
    const rows = Math.max(Math.ceil(pageSize / columns), 1);
    const cellW = Math.floor((grid.w - (grid.gap * (columns - 1))) / columns);
    const cellH = Math.floor((grid.h - (grid.gap * (rows - 1))) / rows);
    const imageH = cellH - band;

    // A full page_size worth of cells. Empty cells become dashed placeholders so the
    // author sees the grid shape even on a short final page or before an album is
    // picked — the authoring affordance, distinct from print's no-placeholder pages.
    const cellsOnPage = Math.min(pageSize, rows * columns);
    for (let index = 0; index < cellsOnPage; index++) {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = grid.x + (col * (cellW + grid.gap));
      const y = grid.y + (row * (cellH + grid.gap));
      const url = slice[index];
      // Image occupies only the image strip (cell minus the caption band).
      surface.appendChild(this.collectionCellNode(x, y, cellW, imageH, url));
      // Caption lines beneath — only for a real image on this page (a placeholder cell
      // has no caption, matching the expander which emits captions only per real image).
      if (url) {
        this.collectionCaptionNodes(captionFields, captionSlice[index], x, y + imageH, cellW)
          .forEach((node) => surface.appendChild(node));
      }
    }

    // Deck-wide page number (card #2093), bottom-right — this collection PAGE's
    // ABSOLUTE "position / total" in the deck sequence (its expanded pages each take a
    // surface), drawn IF the collection slide shows numbers. Editor chrome mirroring
    // Deck#page_number_element; the per-page index tracks the pager.
    this.appendPageNumber(surface, slideIndex, page);

    // Prev/next pager so the author can flip through every expanded page. Drawn on
    // top of the surface; clicking re-renders the canvas at the new page.
    if (pages > 1) {
      surface.appendChild(this.collectionPagerNode(slideIndex, page, pages));
    }

    // No silent truncation: when the album has more images than the url cap, the
    // pages past the cap show placeholders. Tell the author the designer only tiles
    // the first N images; the real render paginates the whole album.
    if (imageCount > imageUrls.length) {
      const note = document.createElement("div");
      note.className = "deck-editor-collection-note";
      note.textContent = `Preview tiles the first ${imageUrls.length} images; the published deck paginates all ${imageCount}.`;
      surface.appendChild(note);
    }

    // Crop overlay (card #2130) for a chrome image element on a collection slide,
    // drawn last so it sits above the derived grid/pager. Same overlay as a canvas
    // slide — the crop rides slide.elements, which the expander deep-dups per page.
    if (this._cropMode) this.renderCropOverlay(surface, slide);

    this.canvasMountTarget.appendChild(surface);
  }

  // The per-slide designer preview page (1-based), clamped to [1, pages]. Held on
  // this.collectionPreviewPage keyed by slide index — editor chrome, never deck_json.
  // Defaults to 1 and re-clamps so shrinking page_size/album can't strand the index
  // past the last page.
  collectionPage(slideIndex, pages) {
    if (!this.collectionPreviewPage) this.collectionPreviewPage = {};
    const current = this.collectionPreviewPage[slideIndex] || 1;
    const clamped = Math.min(Math.max(1, current), pages);
    this.collectionPreviewPage[slideIndex] = clamped;
    return clamped;
  }

  // Move the selected collection slide's designer preview to a new page and re-render
  // the canvas. Clamped in collectionPage on the way back out.
  setCollectionPage(slideIndex, page) {
    if (!this.collectionPreviewPage) this.collectionPreviewPage = {};
    this.collectionPreviewPage[slideIndex] = page;
    this.renderCanvas();
  }

  // The DEFAULT_GRID merged with the slide's per-grid override, every value coerced
  // to an int — mirrors CollectionExpander#grid_region. Default leaves a top band
  // for the chrome (title).
  collectionGridRegion(collection) {
    const defaults = { x: 64, y: 120, w: 1152, h: 536, gap: 16 };
    const override = collection.grid || {};
    const out = {};
    ["x", "y", "w", "h", "gap"].forEach((key) => {
      const v = override[key];
      out[key] = (v === undefined || v === null || v === "") ? defaults[key] : this.int(v);
    });
    return out;
  }

  // One auto-grid cell. With a url it draws an object-fit:cover image (the same look
  // as the expander's image element, INCLUDING the radius + shadow "pop"); without one
  // it draws a dashed placeholder so the grid shape reads even before an album is
  // picked or for a short final page. `h` is the IMAGE height (already shrunk by any
  // caption band by the caller), so the placeholder/image both occupy only the image
  // strip and the caption lines sit beneath them.
  collectionCellNode(x, y, w, h, url) {
    const geometry = `left:${x}px;top:${y}px;width:${w}px;height:${h}px;`;
    // The auto-grid is DERIVED chrome drawn on top of the editable elements, so each
    // cell is non-interactive — pointer-events:none lets a click pass through to a
    // chrome element's handle beneath it (the placeholder cell gets this from its CSS
    // class; the image cell sets it inline since it carries an inline style string).
    if (url) {
      const img = document.createElement("img");
      img.className = "airdrop-el airdrop-el--image";
      // Radius + shadow mirror the expander's image-cell defaults and _canvas's image
      // branch, so the designer tile reads as a card exactly like the print render.
      let style = geometry + "object-fit:cover;pointer-events:none;";
      if (COLLECTION_IMAGE_RADIUS) style += `border-radius:${COLLECTION_IMAGE_RADIUS}px;`;
      if (COLLECTION_IMAGE_SHADOW) style += "box-shadow:0 2px 8px rgba(0,0,0,0.18);";
      img.setAttribute("style", style);
      img.src = url;
      img.alt = "";
      return img;
    }
    const placeholder = document.createElement("div");
    placeholder.className = "deck-editor-collection-cell";
    placeholder.setAttribute("style", geometry);
    return placeholder;
  }

  // The album's enabled caption fields, in CAPTION_FIELDS order — the editor mirror of
  // CollectionExpander#caption_fields_for. [] when the album is absent or has no
  // display_fields, in which case the grid draws full-height images with no band.
  collectionCaptionFields(album) {
    const enabled = (album && Array.isArray(album.display_fields)) ? album.display_fields : [];
    return CAPTION_FIELDS.filter((field) => enabled.includes(field));
  }

  // The caption-band height for this album: CAPTION_BAND_HEIGHT when any field is
  // enabled, else 0 (full-height image, current behaviour). Mirrors the expander's
  // `band = fields.any? ? CAPTION_BAND_HEIGHT : 0`.
  collectionCaptionBand(album) {
    return this.collectionCaptionFields(album).length ? CAPTION_BAND_HEIGHT : 0;
  }

  // The stacked caption-line nodes for one tile — one small <div> per enabled field
  // whose value is non-blank, mirroring CollectionExpander#caption_cells. `captions`
  // is the per-image caption value hash shipped on the album (index-aligned with
  // image_urls); a missing entry (e.g. a page past the url cap) yields no lines. The
  // first line (client) is bolder/larger; the rest are smaller and muted. Returns []
  // when no fields are enabled or every value is blank. Non-interactive chrome.
  collectionCaptionNodes(fields, captions, x, bandY, width) {
    if (!fields.length || !captions) return [];

    const nodes = [];
    let lineIndex = 0;
    fields.forEach((field) => {
      const value = (captions[field] || "").toString();
      if (!value) return;

      const primary = lineIndex === 0;
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--text";
      let style = `left:${x}px;top:${bandY + (lineIndex * CAPTION_LINE_HEIGHT)}px;` +
        `width:${width}px;height:${CAPTION_LINE_HEIGHT}px;pointer-events:none;` +
        "overflow:hidden;white-space:nowrap;text-overflow:ellipsis;";
      if (primary) {
        style += `font-size:${CAPTION_PRIMARY_SIZE}px;font-weight:600;`;
      } else {
        style += `font-size:${CAPTION_SECONDARY_SIZE}px;opacity:${CAPTION_SECONDARY_OPACITY / 100};`;
      }
      node.setAttribute("style", style);
      node.textContent = value;
      nodes.push(node);
      lineIndex += 1;
    });
    return nodes;
  }

  // The designer page pager — editor chrome ONLY (no ERB parity), bottom-centre of
  // the collection canvas. Prev/next buttons flank an "n / N" label; each click moves
  // this.collectionPreviewPage and re-renders the grid with that page's image slice.
  // Buttons disable at the ends. pointer-events live (unlike the badge) so the author
  // can click; built from DOM nodes, never HTML strings.
  collectionPagerNode(slideIndex, page, pages) {
    const pager = document.createElement("div");
    pager.className = "deck-editor-collection-pager";

    const prev = this.collectionPagerButton("‹", page <= 1, () => {
      this.setCollectionPage(slideIndex, page - 1);
    });
    prev.setAttribute("aria-label", "Previous page");

    const label = document.createElement("span");
    label.className = "deck-editor-collection-pager__label";
    label.textContent = `Page ${page} of ${pages}`;

    const next = this.collectionPagerButton("›", page >= pages, () => {
      this.setCollectionPage(slideIndex, page + 1);
    });
    next.setAttribute("aria-label", "Next page");

    pager.append(prev, label, next);
    return pager;
  }

  // One pager button. Disabled at a range end (no-op + dimmed); otherwise wires the
  // click. type=button so it never submits the surrounding form.
  collectionPagerButton(glyph, disabled, onClick) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "deck-editor-collection-pager__btn";
    button.textContent = glyph;
    if (disabled) {
      button.disabled = true;
    } else {
      button.addEventListener("click", onClick);
    }
    return button;
  }

  // Read-only render for non-canvas slides (e.g. the title slide). The title
  // editor is deferred until title becomes a canvas kind (ADR-0065).
  renderReadonlySlide(slide) {
    const surface = document.createElement("div");
    surface.className = "airdrop-slide airdrop-slide--title";
    surface.style.width = `${this.canvasW}px`;
    surface.style.height = `${this.canvasH}px`;
    surface.style.fontFamily = this.deckFontFamily();

    const note = document.createElement("div");
    note.className = "airdrop-slide__period";
    // Collection slides now render a real page-1 WYSIWYG via renderCollectionSlide
    // (ADR-0070 §4b); this read-only branch handles the remaining non-canvas type
    // (title), which the title editor will own when title becomes a canvas kind.
    note.textContent = "Preview only — not yet editable";
    surface.appendChild(note);

    const title = document.createElement("h2");
    title.className = "airdrop-slide__title";
    title.textContent = this.slideLabel(slide) + " slide";
    surface.appendChild(title);

    this.canvasMountTarget.appendChild(surface);
  }

  // The visible element, drawn exactly as the render partial does.
  elementNode(element) {
    const geometry = `left:${this.int(element.x)}px;top:${this.int(element.y)}px;` +
      `width:${this.int(element.w)}px;height:${this.int(element.h)}px;`;
    const options = element.options || {};
    const value = this.resolveValue(element);

    // Chart skeleton: when this text/headline element is bound to a chart-type
    // objective, render a visual skeleton instead of the {slot} placeholder.
    // Editor-only — elementNode() is never called in the server-rendered viewer.
    if (element.slot && (element.kind === "text" || element.kind === "headline")) {
      const chartKind = this.chartKindForElement(element);
      if (chartKind === "doughnut" || chartKind === "pie") {
        return this.skeletonDoughnutNode(element, geometry);
      }
      if (chartKind === "bar") {
        return this.skeletonBarNode(element, geometry, false);
      }
      if (chartKind === "column") {
        return this.skeletonBarNode(element, geometry, true);
      }
      if (chartKind === "gauge") {
        return this.skeletonGaugeNode(element, geometry);
      }
      // "text", "stat", "table" — fall through to the existing stat/text render
    }

    if (element.kind === "headline" || element.kind === "text") {
      // Stat visual (ADR-0070 §2/D5): an objective-bound text element drawn as a
      // stat card — big value + small label beneath — instead of inline text. The
      // editor has no resolved payload (result_json lives server-side), so the
      // value shows the `{slot}` placeholder and the label shows a placeholder too;
      // it is structurally WYSIWYG against the published stat render. PARITY: the
      // published partial's stat branch mirrors this markup + sizing.
      if (this.isStatVisual(element)) {
        return this.statNode(element, geometry, options, value);
      }

      const isHeadline = element.kind === "headline";
      // Typography shared by both layouts (the pill-inner span, or the plain text div).
      // Per-element font override (card #2081): elementFontFamily returns the resolved
      // stack, or null → inherit the deck-wide font (no font-family emitted). Mirrors
      // the _canvas.html.erb type_style order EXACTLY (color, font-family, font-size,
      // font-weight) for byte-for-byte parity.
      const elementFont = this.elementFontFamily(element);
      const typeStyle =
        (options.color ? `color:${options.color};` : "") +
        (elementFont ? `font-family:${elementFont};` : "") +
        `font-size:${options.size ? this.int(options.size) : (isHeadline ? 48 : 24)}px;` +
        `font-weight:${options.weight || (isHeadline ? 800 : 400)};`;
      // Optional pill (card #2093): options.background draws the text inside a rounded
      // pill that HUGS its content — the element box becomes a flex wrapper aligned per
      // options.align, with an inner span carrying fill/radius/padding/shadow. Mirrors
      // _canvas.html.erb's text branch EXACTLY (same z-index, fill, radius, padding,
      // shadow, colour) so the editor preview matches publish. Absent background →
      // unchanged plain text render. `target` is whatever node receives the content.
      const hasPill = !!options.background;
      const node = document.createElement("div");
      let target;
      if (hasPill) {
        node.className = "airdrop-el airdrop-el--text airdrop-el--pill";
        const justify = options.align === "center" ? "center"
          : (options.align === "right" ? "flex-end" : "flex-start");
        let wrapStyle = geometry + `display:flex;align-items:center;justify-content:${justify};`;
        if (options.z_index != null) wrapStyle += `z-index:${this.int(options.z_index)};`;
        wrapStyle += this.opacityStyle(options);
        node.setAttribute("style", wrapStyle);
        const span = document.createElement("span");
        let pillStyle = typeStyle + `background:${options.background};`;
        pillStyle += `border-radius:${options.radius != null ? this.int(options.radius) : 999}px;`;
        pillStyle += "padding:2px 10px;box-shadow:0 1px 3px rgba(0,0,0,0.15);";
        span.setAttribute("style", pillStyle);
        node.appendChild(span);
        target = span;
      } else {
        node.className = "airdrop-el airdrop-el--text";
        let style = geometry;
        if (options.align) style += `text-align:${options.align};`;
        style += typeStyle;
        if (options.z_index != null) style += `z-index:${this.int(options.z_index)};`;
        style += this.opacityStyle(options);
        node.setAttribute("style", style);
        target = node;
      }
      // Rich-text render parity (card #2043). An UNBOUND text element stores
      // Lexxy HTML in element.content, so we inject it as sanitised innerHTML to
      // preserve bold/italic/lists/links. Bound text (the {slot} placeholder)
      // and every headline stay on textContent: a slot is a literal token, not
      // markup, and headlines keep their own size/weight/align controls rather
      // than inline formatting. The server partial's text branch mirrors this
      // exact split (raw sanitize for unbound text, escaped for the rest).
      if (element.kind === "text" && !element.slot) {
        // Unbound rich text. In Preview (the flag is set) resolve inline {slot}
        // tokens in the already-sanitised HTML, escaping each substituted value
        // (post-sanitise, into HTML) — mirrors the server _canvas raw branch. On
        // the Designer canvas the flag is false, so the literal {slot} stays.
        const html = this.sanitizeRichText(element.content || "");
        target.innerHTML = this._resolveValues ? this.resolveInlineTokens(html, true) : html;
      } else if (element.kind === "headline" && !element.slot && this._resolveValues) {
        // Unbound headline in Preview: resolve inline tokens into the textContent
        // (no escaping needed — textContent is inert). Designer stays literal.
        target.textContent = this.resolveInlineTokens(value || "");
      } else {
        target.textContent = value || "";
      }
      return node;
    }

    if (element.kind === "image") {
      const fit = options.fit || "cover";
      // Non-destructive frame crop (card #2130). When element.crop resolves to a
      // transform, the element box becomes a clip WINDOW (overflow:hidden) wrapping
      // an <img> that is scaled/panned behind it — the PowerPoint/Figma model. PARITY:
      // _canvas.html.erb's image branch renders the identical wrapper + transform, so
      // the editor canvas and the published viewer frame the image the same way.
      // "Make it pop" chrome (card #2135). Radius / border / shadow resolved through
      // the SAME helpers the shape branch and the server _canvas partial use, so all
      // three surfaces build the identical strings for WYSIWYG.
      const imageBorder = this.borderCss(options);
      const imageShadow = this.shadowCss(options);
      const cropTransform = this.cropTransform(element.crop);
      if (value && cropTransform) {
        const wrap = document.createElement("div");
        wrap.className = "airdrop-el airdrop-el--image airdrop-el--image-crop";
        let wrapStyle = geometry + "overflow:hidden;";
        if (options.radius) wrapStyle += `border-radius:${this.int(options.radius)}px;`;
        if (imageBorder) wrapStyle += `border:${imageBorder};box-sizing:border-box;`;
        if (imageShadow) wrapStyle += `box-shadow:${imageShadow};`;
        wrapStyle += this.opacityStyle(options);
        wrap.setAttribute("style", wrapStyle);
        const img = document.createElement("img");
        img.setAttribute(
          "style",
          `display:block;width:100%;height:100%;object-fit:${fit};` +
            `transform:${cropTransform};transform-origin:center center;`
        );
        img.src = value;
        img.alt = options.alt || "";
        wrap.appendChild(img);
        return wrap;
      }

      const node = document.createElement("img");
      node.className = "airdrop-el airdrop-el--image";
      // Image "pop" (card: album image polish). Border-radius + soft drop shadow,
      // driven off element.options so ONLY opt-in images (collection auto-grid cells,
      // via the expander defaults) get the card look — parity with _canvas.html.erb's
      // image branch. The shadow string is the exact rgba the server writes.
      let imgStyle = geometry + `object-fit:${fit};`;
      if (options.radius) imgStyle += `border-radius:${this.int(options.radius)}px;`;
      if (imageBorder) imgStyle += `border:${imageBorder};box-sizing:border-box;`;
      if (imageShadow) imgStyle += `box-shadow:${imageShadow};`;
      imgStyle += this.opacityStyle(options);
      node.setAttribute("style", imgStyle);
      if (value) node.src = value;
      node.alt = options.alt || "";
      return node;
    }

    if (element.kind === "html") {
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--html";
      let style = geometry + "overflow:hidden;pointer-events:none;";
      style += this.opacityStyle(options);
      node.setAttribute("style", style);
      // Render static HTML in the editor canvas. Scripts do not execute via
      // innerHTML (browser security), so Chart.js won't run here — that is
      // intentional. The server-rendered slide viewer executes scripts.
      // Parity note: _canvas.html.erb renders via `raw`, which DOES execute
      // scripts on the published slide page.
      node.innerHTML = options.html || "";
      return node;
    }

    if (element.kind === "table") {
      // Native table element (card #3068) -- WYSIWYG parity with
      // decks/slides/_canvas.html.erb's "table" branch. The build editor has
      // no authoring UI for tables in v1 (Sky/Pavo author deck_json directly);
      // this render exists so a table dropped into a deck by that path still
      // looks right in the Preview tab, matching every other kind's parity
      // discipline. No override lookup here -- the build editor works off
      // deck_json in memory, never result_json's live override map.
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--table";
      let wrapStyle = geometry + `font-family:${this.deckFontFamily()};`;
      wrapStyle += this.opacityStyle(options);
      node.setAttribute("style", wrapStyle);

      const borderColor = options.border_color || "#d0d0d0";
      const headerBg = options.header_background || "#003D68";
      const headerColor = options.header_color || "#ffffff";
      const headers = options.headers || [];
      const rows = options.rows || [];

      const table = document.createElement("table");
      table.setAttribute(
        "style",
        `width:100%;height:100%;border-collapse:collapse;background:#fff;border:1px solid ${borderColor};box-shadow:0 1px 4px rgba(0,0,0,0.06);`
      );

      if (headers.length) {
        const thead = document.createElement("thead");
        const tr = document.createElement("tr");
        tr.setAttribute("style", `background:${headerBg};color:${headerColor};`);
        headers.forEach((cell) => {
          const th = document.createElement("th");
          let thStyle = `padding:0.8rem 1rem;font-weight:600;text-align:${cell.align || "center"};`;
          if (cell.width_pct) thStyle += `width:${this.int(cell.width_pct)}%;`;
          th.setAttribute("style", thStyle);
          th.textContent = cell.content || "";
          tr.appendChild(th);
        });
        thead.appendChild(tr);
        table.appendChild(thead);
      }

      const tbody = document.createElement("tbody");
      rows.forEach((row, rowIndex) => {
        const tr = document.createElement("tr");
        if (rowIndex !== rows.length - 1) tr.setAttribute("style", `border-bottom:1px solid ${borderColor};`);
        (row.cells || []).forEach((cell, colIndex) => {
          const td = document.createElement("td");
          let tdStyle = "padding:0.8rem 1rem;";
          tdStyle += `text-align:${cell.align || (colIndex === 0 ? "left" : "center")};`;
          if (cell.weight) tdStyle += `font-weight:${cell.weight};`;
          if (cell.color) tdStyle += `color:${cell.color};`;
          td.setAttribute("style", tdStyle);
          td.textContent = cell.content || "";
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      node.appendChild(table);
      return node;
    }

    if (element.kind === "janela_frame") {
      // Porthole report frame (card #4008): a placeholder in the editor, the
      // slide partial renders the live iframe. Authored via MCP, no insert UI yet.
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--analytics";
      const style = geometry
        + "background:#f0f4ff;border:2px dashed #6366f1;"
        + "display:flex;align-items:center;justify-content:center;"
        + "font-size:14px;color:#6366f1;gap:8px;overflow:hidden;box-sizing:border-box;";
      node.setAttribute("style", style);
      node.textContent = `Report frame: ${options.frame || "unset"}`;
      return node;
    }

    if (element.kind === "clock") {
      // Live clock (card #4008). PARITY: _canvas.html.erb "clock" branch, which
      // the deck-clock controller ticks in the viewer; the editor shows a still.
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--clock";
      const justify = { left: "flex-start", center: "center", right: "flex-end" }[options.align] || "flex-end";
      let style = geometry
        + `display:flex;align-items:center;justify-content:${justify};white-space:nowrap;`
        + `font-size:${options.size ? this.int(options.size) : 28}px;`
        + `font-weight:${options.weight || 600};font-variant-numeric:tabular-nums;`;
      if (this.isHexColor(options.color)) style += `color:${options.color};`;
      const clockFont = this.elementFontFamily(element);
      if (clockFont) style += `font-family:${clockFont};`;
      if (options.opacity != null && this.int(options.opacity) < 100) style += `opacity:${this.int(options.opacity) / 100};`;
      node.setAttribute("style", style);
      node.textContent = clockText(new Date(), {
        timeZone: options.time_zone || undefined,
        showDate: options.show_date !== false,
        showSeconds: options.show_seconds !== false
      });
      return node;
    }

    if (element.kind === "analytics") {
      // Analytics: show a styled placeholder in editor mode (live iframes are too
      // noisy in the canvas). The published slide partial renders the real iframe.
      const node = document.createElement("div");
      node.className = "airdrop-el airdrop-el--analytics";
      const style = geometry
        + "background:#f0f4ff;border:2px dashed #6366f1;"
        + "display:flex;align-items:center;justify-content:center;"
        + "font-size:14px;color:#6366f1;gap:8px;overflow:hidden;box-sizing:border-box;";
      node.setAttribute("style", style);
      node.textContent = `Analytics: ${options.url || "/analytics/main"}`;
      return node;
    }

    // shape
    const node = document.createElement("div");
    node.className = "airdrop-el airdrop-el--shape";
    let style = geometry;
    style += `background-color:${options.fill || "var(--color-canary)"};`;
    if (options.radius) style += `border-radius:${this.int(options.radius)}px;`;
    // Border (card #1916, style extended #2135) + shadow (#2135), only when present.
    // box-sizing:border-box keeps the border inside the element's authored w/h so the
    // visual footprint matches the handle overlay. Mirrors the server partial exactly
    // via the shared borderCss / shadowCss helpers.
    const shapeBorder = this.borderCss(options);
    if (shapeBorder) style += `border:${shapeBorder};box-sizing:border-box;`;
    const shapeShadow = this.shadowCss(options);
    if (shapeShadow) style += `box-shadow:${shapeShadow};`;
    style += this.opacityStyle(options);
    node.setAttribute("style", style);
    return node;
  }

  // Per-element opacity (card #1916). Mirrors the server partial: emit nothing
  // when opacity is absent or full, so the common case keeps a clean style string.
  opacityStyle(options) {
    const opacity = options.opacity;
    if (opacity === undefined || opacity === null || this.int(opacity) >= 100) return "";
    return `opacity:${this.int(opacity) / 100};`;
  }

  // Non-destructive crop transform (card #2130). The JS twin of Ruby
  // Deck.crop_transform — keep the two in lockstep. A crop ({ scale, x, y }) is a
  // pan/zoom of the image WITHIN its box: `scale` is a zoom multiplier (>= 1, 1 =
  // object-fit:cover, the pre-crop render); `x`/`y` are pan offsets in PERCENT of
  // the box (0 = centred). Returns a CSS transform VALUE
  // ("translate(x%,y%) scale(s)"), or null when there is no meaningful crop (absent,
  // or scale<=1 with no pan) so an uncropped image keeps its plain bare-<img> render.
  cropTransform(crop) {
    if (!crop || typeof crop !== "object") return null;
    let scale = parseFloat(crop.scale);
    if (!(scale > 0)) scale = 1;
    const x = parseFloat(crop.x) || 0;
    const y = parseFloat(crop.y) || 0;
    if (scale <= 1 && x === 0 && y === 0) return null;
    return `translate(${x}%,${y}%) scale(${scale})`;
  }

  // "Make it pop" shadow (card #2135). JS twin of Deck.element_shadow_css — the two
  // MUST build the identical string for editor↔viewer WYSIWYG. options.shadow is a
  // preset key ("soft"|"lifted"|"hard"), the LEGACY literal true (-> "soft", so old
  // decks and collection image cells keep their shadow), "custom" (per-axis, built
  // from shadow_x/y/blur/spread/color/opacity), or false/absent/unknown (no shadow).
  shadowCss(options) {
    const shadow = options.shadow;
    if (!shadow) return null;

    if (shadow === "custom") {
      const x = this.int(options.shadow_x);
      const y = this.int(options.shadow_y);
      const blur = Math.max(this.int(options.shadow_blur), 0);
      const spread = this.int(options.shadow_spread);
      const color = this.shadowRgba(options.shadow_color || "#000000", options.shadow_opacity);
      return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
    }
    if (shadow === true) return SHADOW_PRESETS.soft;
    return SHADOW_PRESETS[shadow] || null;
  }

  // Border CSS VALUE ("Wpx style colour"), or null when width <= 0. JS twin of
  // Deck.element_border_css. Style defaults to solid, colour to black.
  borderCss(options) {
    const width = this.int(options.border_width);
    if (width <= 0) return null;
    const style = ["solid", "dashed", "dotted"].includes(options.border_style)
      ? options.border_style
      : "solid";
    const color = options.border_color || "#000000";
    return `${width}px ${style} ${color}`;
  }

  // Compose #hex + 0-100 opacity into rgba() for a custom shadow. Blank opacity =
  // fully opaque; a non-hex colour returns as-is. JS twin of Deck.shadow_rgba.
  shadowRgba(color, opacity) {
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(color))) return color;
    const alpha =
      opacity === undefined || opacity === null || opacity === ""
        ? 1
        : Math.min(1, Math.max(0, parseInt(opacity, 10) / 100));
    let h = color.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    // Number#toString drops trailing zeros ("1" not "1.0"), matching format_crop_number.
    return `rgba(${r},${g},${b},${alpha})`;
  }

  // The interactive overlay handle (selection outline + resize grip) cloned
  // from the template and positioned in canvas pixels over the element.
  //
  // Thin/small elements (e.g. a 2px-tall bar) are nearly unselectable if the
  // invisible handle is only as tall as the element (card #1916). So the handle
  // enforces a MINIMUM interactive size (HANDLE_MIN): when the element's true w/h
  // is below the floor, the handle is grown to the floor and shifted by half the
  // overshoot on that axis so it stays CENTRED over the real element. This affects
  // ONLY the invisible interaction overlay — the rendered .airdrop-el keeps its
  // true x/y/w/h, so geometry and the WYSIWYG render are untouched.
  handleNode(element, elIndex) {
    const frag = this.elementHandleTemplateTarget.content.cloneNode(true);
    const handle = frag.querySelector("[data-deck-editor-target='elementHandle']");
    handle.dataset.index = elIndex;

    const x = this.int(element.x);
    const y = this.int(element.y);
    const w = this.int(element.w);
    const h = this.int(element.h);

    const handleW = Math.max(w, HANDLE_MIN);
    const handleH = Math.max(h, HANDLE_MIN);
    // Centre the grown handle over the true element on whichever axis was floored.
    const handleX = x - (handleW - w) / 2;
    const handleY = y - (handleH - h) / 2;

    handle.style.left = `${handleX}px`;
    handle.style.top = `${handleY}px`;
    handle.style.width = `${handleW}px`;
    handle.style.height = `${handleH}px`;
    // Selection treatment (card #1979). With a multi-selection (2+), each member gets
    // a LIGHT outline only (is-multi) and NO resize grip — the combined dashed group
    // box (groupBoxNode) carries the selection chrome, and group resize is out of
    // scope. With 0/1 selected, behaviour is exactly as before: the single primary
    // gets is-selected (solid ring + resize grip).
    if (this.selectionIndices.length >= 2) {
      if (this.selectionIndices.includes(elIndex)) handle.classList.add("is-multi");
    } else if (elIndex === this.selectedElement) {
      handle.classList.add("is-selected");
    }
    return handle;
  }

  // The combined dashed bounding box drawn over a 2+ multi-selection (card #1979).
  // Returns a single absolutely-positioned node spanning the union bbox of every
  // selected element (min x/y → max x+w / y+h), in the SAME canvas-pixel coordinate
  // space as handleNode (the justified canvas exception). Non-interactive so it never
  // steals a press meant for an element/handle beneath it. Returns null for 0/1
  // selected, where the per-element handle carries the selection instead.
  groupBoxNode(slide) {
    if (this.selectionIndices.length < 2) return null;
    const elements = slide.elements || [];
    let minX = Infinity, minY = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
    this.selectionIndices.forEach((i) => {
      const el = elements[i];
      if (!el) return;
      const x = this.int(el.x), y = this.int(el.y), w = this.int(el.w), h = this.int(el.h);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxRight = Math.max(maxRight, x + w);
      maxBottom = Math.max(maxBottom, y + h);
    });
    if (!Number.isFinite(minX)) return null;          // every index was stale
    const box = document.createElement("div");
    box.className = "deck-editor-group-box";
    box.style.left = `${minX}px`;
    box.style.top = `${minY}px`;
    box.style.width = `${maxRight - minX}px`;
    box.style.height = `${maxBottom - minY}px`;
    return box;
  }

  // A small chain-link badge in the TOP-RIGHT corner of a linked element (ADR-0070
  // §3), so the author sees at a glance which elements navigate. Returns null when
  // the element carries no link. Editor-chrome only — the published render wraps the
  // element in <a> (the href resolves server-side via Deck#resolve_element_link);
  // this badge is purely a visual cue, so there is NO ERB parity to maintain. The
  // badge is non-interactive (pointer-events:none) so it never steals a click meant
  // for the element/handle beneath it.
  linkBadgeNode(element) {
    if (!element.options || !element.options.link) return null;

    const badge = document.createElement("div");
    badge.className = "deck-editor-link-badge";
    badge.title = `Links to ${this.linkLabelFor(element.options.link)}`;
    // Position over the element's top-right corner, in canvas pixels (the justified
    // canvas-coordinate exception, same as handleNode).
    const x = this.int(element.x);
    const y = this.int(element.y);
    const w = this.int(element.w);
    badge.style.left = `${x + w - 18}px`;
    badge.style.top = `${y + 2}px`;
    badge.textContent = "🔗";
    return badge;
  }

  // An element-id-keyed override entry (ADR-0094, card #1978) — the JS mirror of
  // Ruby Deck#element_override. Returns null when the element carries no id, there
  // is no override map, or the map holds no entry for it.
  elementOverride(element) {
    const id = element && element.id;
    if (!id) return null;
    const overrides = this.result && this.result["_manual_overrides"];
    if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) return null;
    const entry = overrides[id];
    return (entry && typeof entry === "object") ? entry : null;
  }

  // Return element.id, minting a fresh crypto.randomUUID() the first time an
  // element needs one — mirrors the slide-id minting convention (card #2120,
  // applySlideJson/addSlide) applied to individual elements. Elements have never
  // needed a stable id before ADR-0094; one is minted lazily, only when an edit
  // actually needs to key an override entry, rather than backfilling every
  // element up front.
  ensureElementId(element) {
    if (!element.id) element.id = crypto.randomUUID();
    return element.id;
  }

  // The editor shows static content / slot name only — the live WIN-data slot
  // picker is Slice 2'. A bound slot renders its name as a placeholder.
  resolveValue(element) {
    // Element-id override wins over everything (ADR-0094) — a live edit against
    // this exact element, bound or unbound. Checked BEFORE the Preview/Designer
    // split below: the author's own in-progress correction must show on the
    // Designer canvas too, not only in Preview.
    const override = this.elementOverride(element);
    if (override && "content" in override) return override.content;

    // Preview resolves against this.result; the Designer canvas stays literal
    // (card #2061). The transient this._resolveValues flag is set ONLY while the
    // Preview surfaces are built, so the SAME elementNode() draws a literal
    // {slot} on the canvas and the resolved value in Preview.
    if (this._resolveValues && element.slot && element.slot in this.result) {
      return this.formatDisplay(this.scalarFrom(this.result[element.slot]));
    }
    if (element.slot) return `{${element.slot}}`;
    return element.content;
  }

  // Unwrap a resolved slot value to its inline display scalar — the JS mirror of
  // Ruby Deck#scalar_from (card #2061). A structured payload (a non-null, non-array
  // object carrying a "value" key) yields its "value"; anything else passes through
  // unchanged. Keeps the SCALAR-or-HASH backward-compat (ADR-0070 §2/D4) on the
  // client exactly as the server does.
  scalarFrom(value) {
    if (value !== null && typeof value === "object" && !Array.isArray(value) && "value" in value) {
      return value.value;
    }
    return value;
  }

  // Format a resolved scalar for inline DISPLAY (card #2063) — the JS mirror of
  // Ruby Deck#format_display. Trims a redundant trailing ".0" so a whole-number
  // objective value like "142.0" renders as "142"; genuine decimals ("87.5") and
  // non-numeric values pass through unchanged. Display only.
  formatDisplay(value) {
    if (value === null || value === undefined) return value;
    const str = String(value);
    return /^-?\d+\.0+$/.test(str) ? str.replace(/\.0+$/, "") : value;
  }

  // Resolve inline binding tokens within authored prose — the JS mirror of Ruby
  // Deck#resolve_inline_tokens (card #2061, ADR-0080). Each {slot} is replaced by
  // its resolved value from this.result; an unbound/unresolved slot (not in
  // this.result, or a null/undefined value) is left as the LITERAL {slot} so the
  // sentence never blank-collapses. escapeValues HTML-escapes each substituted
  // value for safe insertion into already-sanitised rich-text HTML.
  resolveInlineTokens(text, escapeValues = false) {
    if (text === null || text === undefined || text === "") return text;
    return String(text).replace(/\{([A-Za-z0-9_]+)\}/g, (match, slot) => {
      if (!(slot in this.result)) return match;
      const v = this.scalarFrom(this.result[slot]);
      if (v === null || v === undefined) return match;
      const display = this.formatDisplay(v);
      return escapeValues ? this.escapeHtml(String(display)) : String(display);
    });
  }

  // Minimal HTML-escape for substituted token values (card #2061). Mirrors Ruby
  // ERB::Util.html_escape over the five XML/HTML metacharacters.
  escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Returns the chart_kind string for a bound objective slot, or null.
  // Used to decide whether to render an SVG skeleton in place of the slot text.
  chartKindForElement(element) {
    if (!element.slot || !this.objectives) return null;
    const binding = (this.deck.bindings || {})[element.slot];
    if (!binding || binding.source !== "objective") return null;
    const id = binding.source_id != null ? binding.source_id : binding.objective_id;
    const objective = this.objectives.find(o => String(o.id) === String(id));
    return objective ? (objective.chart_kind || null) : null;
  }

  // True when a text/headline element is set to draw as a stat card (ADR-0070
  // §2/D5). Only text-family elements carry the stat visual; image/shape never do.
  isStatVisual(element) {
    return (element.kind === "text" || element.kind === "headline") &&
      element.options && element.options.visual === "stat";
  }

  // The label a stat card shows beneath its value. The resolved label lives in
  // result_json server-side (the bound objective's name), out of the editor's
  // reach, so the editor shows the bound slot name as a stand-in (or the element's
  // static content when unbound). Structurally WYSIWYG against the published card.
  statLabelFor(element) {
    // In Preview (the flag is set), a bound slot whose resolved payload carries a
    // "label" shows the real label (the bound objective's name) beneath the value
    // — card #2061. On the Designer canvas the flag is false, so the slot name
    // stands in, exactly as before.
    if (this._resolveValues && element.slot && element.slot in this.result) {
      const payload = this.result[element.slot];
      if (payload !== null && typeof payload === "object" && !Array.isArray(payload) && payload.label != null) {
        return payload.label;
      }
    }
    if (element.slot) return element.slot;
    return element.content || "";
  }

  // Build the stat-card node (ADR-0070 §2/D5): a flex column with a big value on
  // top and a small label beneath. Geometry + colour are the justified data-driven
  // inline-style exception (same as the plain text branch). PARITY: the published
  // partial's stat branch mirrors this markup and sizing.
  statNode(element, geometry, options, value) {
    const isHeadline = element.kind === "headline";
    const valueSize = options.size ? this.int(options.size) : (isHeadline ? 48 : 40);

    const node = document.createElement("div");
    node.className = "airdrop-el airdrop-el--stat";
    let style = geometry;
    style += "display:flex;flex-direction:column;justify-content:center;";
    style += `text-align:${options.align || "left"};`;
    if (options.align === "center") style += "align-items:center;";
    else if (options.align === "right") style += "align-items:flex-end;";
    else style += "align-items:flex-start;";
    if (options.color) style += `color:${options.color};`;
    // Per-element font override (card #2081) — on the stat container so value +
    // label both adopt it. null → inherit the deck-wide font. Mirrors the ERB
    // stat branch order (color, font-family, opacity).
    const statFont = this.elementFontFamily(element);
    if (statFont) style += `font-family:${statFont};`;
    style += this.opacityStyle(options);
    node.setAttribute("style", style);

    const valueEl = document.createElement("div");
    valueEl.className = "airdrop-el--stat-value";
    valueEl.setAttribute("style", `font-size:${valueSize}px;font-weight:${options.weight || 800};line-height:1.1;`);
    valueEl.textContent = value || "";

    node.appendChild(valueEl);

    // Hide label (card #2157 part 1): options.hide_label suppresses the label
    // row entirely — not just visually, so no vertical space is reserved for
    // it. PARITY: the published partial's stat branch omits the same div.
    if (options.hide_label !== true) {
      const labelEl = document.createElement("div");
      labelEl.className = "airdrop-el--stat-label";
      labelEl.setAttribute("style", `font-size:${Math.max(12, Math.round(valueSize * 0.3))}px;font-weight:400;opacity:0.7;`);
      labelEl.textContent = this.statLabelFor(element);
      node.appendChild(labelEl);
    }

    return node;
  }

  // ---- Chart skeleton nodes -----------------------------------------------
  // These render editor-only SVG placeholders when a text/headline element is
  // bound to a chart-type objective. They give visual feedback without needing
  // a resolved payload (which lives server-side in result_json).

  skeletonDoughnutNode(element, geometry) {
    const node = document.createElement("div");
    node.className = "airdrop-el airdrop-el--skeleton-chart";
    node.setAttribute("style", geometry + "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;");
    node.setAttribute("title", element.slot);

    const size = Math.min(this.int(element.w), this.int(element.h)) * 0.8;
    const r = size / 2;
    const inner = r * 0.55;
    const cx = r, cy = r;

    node.innerHTML = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="#e5e7eb"/>
        <circle cx="${cx}" cy="${cy}" r="${inner}" fill="white"/>
      </svg>
      <span style="font-size:10px;color:#9ca3af;max-width:${this.int(element.w)}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{${element.slot}}</span>
    `;
    return node;
  }

  skeletonBarNode(element, geometry, isColumn = false) {
    const node = document.createElement("div");
    node.className = "airdrop-el airdrop-el--skeleton-chart";
    node.setAttribute("style", geometry + "display:flex;flex-direction:column;align-items:center;justify-content:flex-end;gap:4px;padding:8px;box-sizing:border-box;");
    node.setAttribute("title", element.slot);

    const w = this.int(element.w) - 16;
    const h = this.int(element.h) - 24;
    const bars = 4;
    const heights = [0.7, 0.4, 0.9, 0.55];

    let barsHtml;
    if (isColumn) {
      const bw = Math.floor((w - (bars - 1) * 4) / bars);
      barsHtml = `<svg width="${bars * bw + (bars - 1) * 4}" height="${h}" viewBox="0 0 ${bars * bw + (bars - 1) * 4} ${h}" fill="none">
        ${heights.map((frac, i) => `<rect x="${i * (bw + 4)}" y="${Math.round(h * (1 - frac))}" width="${bw}" height="${Math.round(h * frac)}" rx="2" fill="#d1d5db"/>`).join("")}
      </svg>`;
    } else {
      const bh = Math.floor((h - (bars - 1) * 4) / bars);
      barsHtml = `<svg width="${w}" height="${bars * bh + (bars - 1) * 4}" viewBox="0 0 ${w} ${bars * bh + (bars - 1) * 4}" fill="none">
        ${heights.map((frac, i) => `<rect x="0" y="${i * (bh + 4)}" width="${Math.round(w * frac)}" height="${bh}" rx="2" fill="#d1d5db"/>`).join("")}
      </svg>`;
    }

    node.innerHTML = `${barsHtml}<span style="font-size:10px;color:#9ca3af;max-width:${this.int(element.w)}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{${element.slot}}</span>`;
    return node;
  }

  skeletonGaugeNode(element, geometry) {
    const node = document.createElement("div");
    node.className = "airdrop-el airdrop-el--skeleton-chart";
    node.setAttribute("style", geometry + "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;");
    node.setAttribute("title", element.slot);

    const w = this.int(element.w);
    const h = this.int(element.h);
    const r = Math.min(w, h) * 0.4;
    const cx = w / 2, cy = h * 0.55;

    node.innerHTML = `
      <svg width="${w}" height="${h * 0.7}" viewBox="0 0 ${w} ${h * 0.7}" fill="none">
        <path d="M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}" stroke="#e5e7eb" stroke-width="${r * 0.3}" fill="none" stroke-linecap="round"/>
        <path d="M ${cx - r * 0.7} ${cy} A ${r * 0.7} ${r * 0.7} 0 0 1 ${cx} ${cy - r * 0.7}" stroke="#d1d5db" stroke-width="${r * 0.25}" fill="none" stroke-linecap="round"/>
      </svg>
      <span style="font-size:10px;color:#9ca3af;">{${element.slot}}</span>
    `;
    return node;
  }

  // ---- Selection ---------------------------------------------------------

  // See connect()'s call site above for why this exists (Edit Slide deep
  // link). Accepts either a slide UUID (preferred -- stable across reorders,
  // what _slide_toolbar.html.erb's "Edit slide" button sends) or a plain
  // integer index. Silently no-ops (stays on the default slide 0) when the
  // param is absent or doesn't match any slide -- a stale/bad link must never
  // throw. Small and scoped deliberately -- not a full deep-linking system,
  // just enough to land on the right slide on load.
  _selectSlideFromQueryParam() {
    const param = new URLSearchParams(window.location.search).get("slide");
    if (!param) return;

    let index = this.deck.slides.findIndex((slide) => slide.id === param);
    if (index === -1) {
      const parsed = parseInt(param, 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < this.deck.slides.length) index = parsed;
    }
    if (index === -1) return;

    this.selectedSlide = index;
    this.renderAll();

    const row = this.hasSlideListTarget
      ? this.slideListTarget.querySelector(`[data-deck-editor-target='slideRow'][data-index='${index}']`)
      : null;
    row?.scrollIntoView({ block: "nearest" });
  }

  selectSlideFromRow(event) {
    const row = event.currentTarget;
    const index = parseInt(row.dataset.index, 10);

    // Plain click: today's exact single-select behaviour (card #2770 and
    // earlier) -- picks the slide shown on the canvas, full stop. Multi-select
    // (multiSelectedIndices) is a SEPARATE concept driven entirely by the
    // per-row checkbox (toggleSlideMultiselect, card #2788 revision) and is
    // deliberately NOT touched here. Per Jay's live feedback, a row click must
    // never clear an existing multi-selection -- only an explicit uncheck
    // does that -- so no click on this row, whatever else it does, may clear
    // the Set.

    // Crop-mode guard (card #2130): switching slides applies the in-progress crop
    // before leaving, so the working state never strands on a hidden slide.
    if (this._cropMode) this.exitCropMode();
    this.selectedSlide = index;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.fitStage();
    this.renderAll();
  }

  // Sidebar checkbox toggle for the multi-drag selection (card #2788 revision
  // -- replaces the earlier ctrl/cmd-click mechanism per Jay's live feedback:
  // a checkbox is a completely separate control from row selection, so it
  // can't be cleared as a side effect of clicking elsewhere, dragging, or a
  // drag landing outside any valid drop target). Stops propagation so the
  // click never reaches the row's own selectSlideFromRow listener -- checking
  // a box must not also move the canvas selection.
  //
  // The browser has already flipped event.target.checked (native checkbox
  // activation behaviour) by the time this handler runs, so a plain click
  // just adopts that into the Set. A shift-click extends the range from the
  // last checkbox interacted with (standard file-manager convention) instead
  // of the native single-checkbox toggle.
  toggleSlideMultiselect(event) {
    event.stopPropagation();
    const row = event.target.closest("[data-deck-editor-target='slideRow']");
    const index = parseInt(row.dataset.index, 10);

    if (event.shiftKey && this._lastMultiSelectIndex !== null) {
      const [start, end] = [this._lastMultiSelectIndex, index].sort((a, b) => a - b);
      for (let i = start; i <= end; i++) this.multiSelectedIndices.add(i);
    } else if (event.target.checked) {
      this.multiSelectedIndices.add(index);
    } else {
      this.multiSelectedIndices.delete(index);
    }
    this._lastMultiSelectIndex = index;
    this.renderSlideList();
  }

  // Select an element. A PLAIN call (additive=false) is single-select: the
  // selection set collapses to just this element and it becomes the primary —
  // byte-for-byte the old behaviour. An ADDITIVE call (additive=true, wired from a
  // Shift+click) toggles this element's membership of the multi-selection set
  // (card #1979). `selectedElement` stays the load-bearing PRIMARY index everywhere
  // (properties panel, layering, nudge, delete, duplicate); `selectionIndices` is
  // the additive context the group box + group move read. Invariant: a one-member
  // set means selectionIndices === [selectedElement]; an empty set means
  // selectedElement === null.
  selectElement(elIndex, additive = false) {
    if (additive) {
      const at = this.selectionIndices.indexOf(elIndex);
      if (at !== -1) {
        // Already selected → remove it; primary falls back to the last remaining
        // member, or null when the set is now empty.
        this.selectionIndices.splice(at, 1);
        this.selectedElement = this.selectionIndices.length
          ? this.selectionIndices[this.selectionIndices.length - 1]
          : null;
      } else {
        // Not selected → add it (set kept sorted, no duplicates); newest is primary.
        this.selectionIndices.push(elIndex);
        this.selectionIndices.sort((a, b) => a - b);
        this.selectedElement = elIndex;
      }
    } else {
      this.selectedElement = elIndex;
      this.selectionIndices = [elIndex];
    }
    this.renderCanvas();
    this.renderProperties();
  }

  // Clear the element selection so the properties panel falls back to slide
  // properties (card #1916). Three affordances reach this: clicking empty canvas,
  // the "← Back to slide" link, and Escape. No-op when nothing is selected so it
  // never costs a needless re-render.
  deselectElement() {
    if (this.selectedElement === null) return;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.renderCanvas();
    this.renderProperties();
  }

  // Click on the canvas BACKGROUND deselects (card #1916). Element handles sit
  // over the surface and capture pointerdown for startMove; their click bubbles
  // here too, so we act only when the click target is the mount or the slide
  // surface itself — a genuine empty-canvas click, not one from a handle.
  canvasBackgroundClick(event) {
    // Crop-mode guard (card #2130): crop mode is STICKY — a background click must not
    // deselect or exit. The author leaves only via the Done button or Escape. Stand
    // down so fiddling (zoom/pan/re-zoom) never drops out of crop.
    if (this._cropMode) return;
    // Inline-edit guard (card #1977): while the double-click textarea is open, the
    // click that follows startInlineEdit's focus bubbles to the canvas mount and
    // would deselect → renderCanvas → replaceChildren, ripping the textarea out of
    // the DOM and firing its blur → commit. Stand down so the editor survives.
    if (this._inlineEditorEl) return;
    const onSurface = event.target === this.canvasMountTarget ||
      event.target.classList.contains("airdrop-slide");
    if (onSurface) this.deselectElement();
  }

  // Editor-level Escape (card #1916). Deselect the current element, but stand
  // down while a modal or the context menu is open — those own their own Escape.
  handleEditorKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && !["INPUT", "TEXTAREA"].includes(event.target.tagName)) {
      if (event.key === "z" && !event.shiftKey) { event.preventDefault(); this.undo(); return; }
      if (event.key === "y" || (event.key === "z" && event.shiftKey)) { event.preventDefault(); this.redo(); return; }
    }
    // Arrow-key nudge of the selected element (card #2094). Left/Right move X by
    // ∓1, Up/Down move Y by ∓1; Shift makes it a 10px coarse step. Editor-only
    // state — mutates element.x/y, which the server already renders from, so
    // there is no deck.rb parity change. Stand down on the same conditions as
    // Escape below (a modal/context menu owns its own keys), and never fire while
    // typing in a field or inline-editing rich text.
    const nudge = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
    if (nudge) {
      if (this.modalOpen() || this.contextMenuEl) return;
      if (["INPUT", "TEXTAREA"].includes(event.target.tagName) || event.target.isContentEditable) return;
      if (this._inlineEditorEl) return;
      if (this.selectedElement === null) return;
      const slide = this.currentSlide();
      const element = slide && slide.elements[this.selectedElement];
      if (!element) return;
      const step = event.shiftKey ? 10 : 1;
      event.preventDefault();                         // don't scroll the panel/page
      // Clamp to canvas bounds exactly as the drag move handler does, but WITHOUT
      // snapValue: drag snaps to the grid, a fine nudge must not — snapping would
      // defeat a 1px adjustment.
      element.x = this.clamp(this.int(element.x) + nudge[0] * step, 0, this.canvasW - this.int(element.w));
      element.y = this.clamp(this.int(element.y) + nudge[1] * step, 0, this.canvasH - this.int(element.h));
      this.renderCanvas();                            // redraw the moved element
      this.markDirty();                               // burst-debounced undo snapshot (same path as drag commit)
      this.renderProperties();                        // keep the X/Y fields in sync, as drag's pointerup does
      return;
    }
    if (event.key !== "Escape") return;
    if (this.modalOpen() || this.contextMenuEl) return;
    // Crop mode (card #2130) owns Escape: exit crop mode (applying the crop) rather
    // than deselecting the element beneath it.
    if (this._cropMode) { event.preventDefault(); this.exitCropMode(); return; }
    if (this._inlineEditorEl) { this._inlineEditorEl.blur(); return; }
    if (this.selectedElement === null) return;
    this.deselectElement();
  }

  // True when either picker overlay is showing — used to keep the editor Escape
  // handler from racing the modal Escape handlers.
  modalOpen() {
    const kindOpen = this.hasKindModalTarget && !this.kindModalTarget.classList.contains("hidden");
    const assetOpen = this.hasAssetModalTarget && !this.assetModalTarget.classList.contains("hidden");
    return kindOpen || assetOpen;
  }

  // ---- Drag move / resize (canvas-space geometry) ------------------------

  // Drag vs. layer-walk on overlapping stacks (card #1916):
  //
  // Handles render in array (z) order, so the FRONT-most element at any point
  // masks the ones behind it and is the one whose handle receives pointerdown.
  // We hit-test the press point in canvas coords against every element's bbox to
  // build the stack of candidates under the cursor (front to back), then split
  // the two gestures that used to fight over a single pointerdown:
  //   - pointerdown DRAGS the element you're working with. If the currently
  //     selected element is under the cursor, it stays selected and gets dragged;
  //     otherwise the front-most element under the cursor is selected and dragged
  //     (falling back to the handle's own element when nothing hit-tests).
  //   - a CLICK with no meaningful drag (pointerup under the 2px threshold) on the
  //     already-selected element in a multi-element stack walks DOWN to the next
  //     element below it, wrapping back to the front after the back-most.
  // So the first click selects the front-most, repeated clicks walk the stack and
  // loop, and a drag at any point moves the current selection — the cycle can no
  // longer steal a drag off the element you grabbed.
  startMove(event) {
    if (event.button !== 0) return;                 // left button only

    // Crop-mode guard (card #2130): while an image is being cropped, the crop overlay
    // owns pointer gestures (pan/zoom the image behind the box). A press on a normal
    // element handle must not select/drag a different element out from under it.
    if (this._cropMode) return;

    // Inline-edit guard (card #1977): the headline inline textarea is opened via
    // the right-click "Edit text" menu item (the double-click trigger was removed —
    // it never fired with a real mouse). While that textarea is open, a fresh press
    // on a handle would selectElement → renderCanvas and tear the editor out. Stand
    // down and let the textarea's own blur commit handle the press instead.
    if (this._inlineEditorEl) return;

    this._flushBurst();

    // The handle that fired is the fallback drag target when nothing hit-tests under
    // the cursor (its own element). Read it from the event — the double-click block
    // that used to declare it was removed with the trigger (card #1977).
    const handleIndex = parseInt(event.currentTarget.dataset.index, 10);

    const point = this.canvasPointFromEvent(event);
    const stack = this.elementsAtPoint(point);      // front-to-back indices under cursor
    const prevSelected = this.selectedElement;
    const prevSelection = this.selectionIndices.slice();   // capture BEFORE any change
    const additive = event.shiftKey;                // Shift+press toggles membership

    // Group move (card #1979): a PLAIN press that lands on a member of an existing
    // 2+ multi-selection drags the WHOLE group. The selection is kept intact through
    // the drag (the collapse-to-single is deferred to a no-drag click inside
    // startGroupMove), so a drag moves all but a plain click still narrows to one.
    const memberUnderCursor = stack.find((i) => prevSelection.includes(i));
    if (!additive && prevSelection.length >= 2 && memberUnderCursor !== undefined) {
      this.startGroupMove(event, prevSelection, memberUnderCursor);
      return;
    }

    // Drag target: keep the current selection if the press lands on it (so you can
    // drag the element you're working with); otherwise grab the front-most under
    // the cursor, falling back to the handle's own element when nothing hit-tests.
    // A Shift+press targets the front-most element actually under the cursor (the one
    // the author sees) so the toggle hits what was clicked, not the prior primary.
    const elIndex = additive
      ? (stack.length ? stack[0] : handleIndex)
      : (stack.includes(prevSelected)
          ? prevSelected
          : (stack.length ? stack[0] : handleIndex));
    this.selectElement(elIndex, additive);

    // A Shift+press is a discrete membership toggle, never a drag — the set already
    // changed and re-rendered, so don't arm a move (group resize/drag-add is out of
    // scope this pass). Single-select press falls through to the unchanged drag path.
    if (additive) return;

    const element = this.currentSlide().elements[elIndex];
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = this.int(element.x);
    const originY = this.int(element.y);
    let moved = false;                              // distinguishes a drag from a click
    event.preventDefault();

    const onMove = (e) => {
      // Ignore sub-threshold jitter so a plain click never nudges the element.
      if (!moved && (Math.abs(e.clientX - startX) > 2 || Math.abs(e.clientY - startY) > 2)) moved = true;
      if (!moved) return;
      const dx = (e.clientX - startX) / this.scale;
      const dy = (e.clientY - startY) / this.scale;
      element.x = this.clamp(this.snapValue(originX + dx), 0, this.canvasW - this.int(element.w));
      element.y = this.clamp(this.snapValue(originY + dy), 0, this.canvasH - this.int(element.h));
      this.renderCanvas();
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (moved) {
        this.markDirty();
        this.renderProperties();
        return;
      }
      // Click (no drag) on the already-selected element in an overlap → cycle to
      // the next element below, wrapping. This is the layer-walk gesture, now
      // separated from drag so dragging always moves the current selection.
      if (stack.length > 1 && prevSelected === elIndex) {
        const at = stack.indexOf(elIndex);
        this.selectElement(stack[(at + 1) % stack.length]);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Drag every member of a 2+ multi-selection by the same delta (card #1979). Each
  // member's origin is captured up front; the drag applies the SAME clamped dx/dy to
  // all of them and re-renders once per frame. The group is clamped as ONE — the
  // delta is bounded by the selection's COLLECTIVE bbox so the whole group stops at
  // the canvas edge together (clamping each element independently would deform the
  // group). A press with no drag collapses the selection to the clicked member,
  // mirroring the single-select layer-walk's "click narrows" behaviour.
  startGroupMove(event, selection, primaryIndex) {
    const slide = this.currentSlide();
    const startX = event.clientX;
    const startY = event.clientY;

    // Origins + the group's collective bounds, captured before the drag begins.
    const origins = selection
      .map((i) => ({ index: i, el: slide.elements[i] }))
      .filter((m) => m.el)
      .map((m) => ({ index: m.index, x: this.int(m.el.x), y: this.int(m.el.y), w: this.int(m.el.w), h: this.int(m.el.h) }));
    let minX = Infinity, minY = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
    origins.forEach((o) => {
      minX = Math.min(minX, o.x);
      minY = Math.min(minY, o.y);
      maxRight = Math.max(maxRight, o.x + o.w);
      maxBottom = Math.max(maxBottom, o.y + o.h);
    });

    let moved = false;
    event.preventDefault();

    const onMove = (e) => {
      if (!moved && (Math.abs(e.clientX - startX) > 2 || Math.abs(e.clientY - startY) > 2)) moved = true;
      if (!moved) return;
      // Snap the delta (keeps every member's relative grid alignment), then clamp the
      // delta to the group's collective bounds so the whole selection halts together.
      let dx = this.snapValue((e.clientX - startX) / this.scale);
      let dy = this.snapValue((e.clientY - startY) / this.scale);
      dx = this.clamp(dx, -minX, this.canvasW - maxRight);
      dy = this.clamp(dy, -minY, this.canvasH - maxBottom);
      origins.forEach((o) => {
        const el = slide.elements[o.index];
        if (!el) return;
        el.x = o.x + dx;
        el.y = o.y + dy;
      });
      this.renderCanvas();
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (moved) {
        this.markDirty();
        this.renderProperties();
        return;
      }
      // Click (no drag) → collapse the multi-selection to the clicked member.
      this.selectElement(primaryIndex, false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Resize from any of the eight grips (card #2130 follow-up). The pressed grip's
  // data-edge names which sides move: a letter in the edge string means that side is
  // dragged. E/S sides grow w/h from the far edge (x/y fixed); W/N sides move x/y AND
  // shrink/grow w/h so the OPPOSITE edge stays pinned — the natural box-handle feel.
  // Works in crop mode too: resizing reshapes the crop WINDOW, and the live preview
  // refreshes with the new box.
  startResize(event) {
    // Left button only: a right-click on the grip must not start a resize (#1916).
    if (event.button !== 0) return;
    // Inline-edit guard (card #1977): pressing a resize grip while the textarea is
    // open would selectElement → renderCanvas and destroy the editor. Stand down.
    if (this._inlineEditorEl) return;

    // The resize grip lives inside the handle; stop the move handler firing too.
    event.stopPropagation();
    event.preventDefault();
    this._flushBurst();

    const edge = event.currentTarget.dataset.edge || "se";
    const movingE = edge.includes("e");
    const movingW = edge.includes("w");
    const movingS = edge.includes("s");
    const movingN = edge.includes("n");

    const handle = event.currentTarget.closest("[data-deck-editor-target='elementHandle']");
    const elIndex = parseInt(handle.dataset.index, 10);
    // In crop mode, keep resizing the element being cropped; otherwise select+resize
    // the pressed element as before.
    if (this._cropMode) {
      if (elIndex !== this._cropIndex) return;        // only the cropped box resizes
    } else {
      this.selectElement(elIndex);
    }

    const slide = this.currentSlide();
    const element = slide.elements[elIndex];
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = this.int(element.x);
    const originY = this.int(element.y);
    const originW = this.int(element.w);
    const originH = this.int(element.h);
    const originRight = originX + originW;             // pinned when dragging W
    const originBottom = originY + originH;            // pinned when dragging N

    const onMove = (e) => {
      const dx = (e.clientX - startX) / this.scale;
      const dy = (e.clientY - startY) / this.scale;

      if (movingE) {
        element.w = this.clamp(this.snapValue(originW + dx), 16, this.canvasW - originX);
      }
      if (movingS) {
        element.h = this.clamp(this.snapValue(originH + dy), 16, this.canvasH - originY);
      }
      if (movingW) {
        // West edge: move x, keep the right edge pinned. New x is clamped to
        // [0, right-16] so width never drops below the floor or leaves the canvas.
        const newX = this.clamp(this.snapValue(originX + dx), 0, originRight - 16);
        element.x = newX;
        element.w = originRight - newX;
      }
      if (movingN) {
        // North edge: move y, keep the bottom edge pinned.
        const newY = this.clamp(this.snapValue(originY + dy), 0, originBottom - 16);
        element.y = newY;
        element.h = originBottom - newY;
      }
      this.renderCanvas();
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      this.markDirty();
      this.renderProperties();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // ---- Non-destructive image crop / reframe (card #2130) -----------------
  //
  // Crop is a pan/zoom of the image WITHIN its element box (the PowerPoint/Figma
  // model), stored as element.crop = { scale, x, y }, NOT a pixel cut. The box is the
  // visible window; the author drags the image behind it and zooms in. The transform
  // (cropTransform) is applied identically on the editor canvas (elementNode) and the
  // published viewer (_canvas.html.erb via Deck.crop_transform), so WYSIWYG holds. No
  // server round-trip, no new asset — the crop lives in deck_json and rides the
  // existing markDirty + Save flow.

  // Eligibility for crop mode: an IMAGE element with a resolvable source. A bound
  // image (a {slot}/Object source) is still croppable — the crop is on the ELEMENT
  // box, independent of what fills it — so we only require the element be an image.
  cropEditable(element) {
    return !!element && element.kind === "image";
  }

  // Enter crop mode on the currently-selected image element. Snapshots the pre-crop
  // state for cancel-via-undo parity, seeds a working crop (defaulting to identity),
  // and draws the crop overlay. Mirrors the "Edit text" entry (#1977): the element is
  // already selected by the caller (context menu / properties button).
  enterCropMode() {
    if (this._cropMode) return;
    const slide = this.currentSlide();
    const element = slide && this.selectedElement !== null && slide.elements[this.selectedElement];
    if (!this.cropEditable(element)) return;
    if (!this.resolveValue(element)) return;          // no source to crop yet

    this._flushBurst();
    // Seed a working crop so the sliders/drag always have concrete numbers. An
    // absent crop starts at identity (scale 1, centred). We do NOT markDirty on
    // entry — only a real pan/zoom change dirties the deck (exitCropMode commits).
    if (!element.crop || typeof element.crop !== "object") {
      element.crop = { scale: 1, x: 0, y: 0 };
    } else {
      element.crop.scale = parseFloat(element.crop.scale) || 1;
      element.crop.x = parseFloat(element.crop.x) || 0;
      element.crop.y = parseFloat(element.crop.y) || 0;
    }
    this._cropMode = true;
    this._cropIndex = this.selectedElement;
    this.renderCanvas();
    this.renderProperties();
  }

  // Exit crop mode. Normalises the working crop (an identity crop is deleted so the
  // element falls back to the plain bare-<img> render), commits via markDirty (the
  // existing Save/undo path), and redraws the canvas without the overlay.
  exitCropMode() {
    if (!this._cropMode) return;
    const slide = this.currentSlide();
    const element = slide && this._cropIndex !== null && slide.elements[this._cropIndex];
    if (element && element.crop) {
      const scale = parseFloat(element.crop.scale) || 1;
      const x = parseFloat(element.crop.x) || 0;
      const y = parseFloat(element.crop.y) || 0;
      if (scale <= 1 && x === 0 && y === 0) {
        delete element.crop;                          // identity → no crop stored
      } else {
        element.crop = { scale, x, y };
      }
    }
    this._cropMode = false;
    this._cropIndex = null;
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // Swallow a pointer/click on the crop dim layer so it neither pans (that's the
  // window's job) nor exits crop mode. Keeps crop mode sticky (card #2130 follow-up):
  // clicking the dark surround is a no-op, not an accidental "done".
  swallowCropClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Clear the crop on the selected image element back to the plain cover render.
  // Reached from the right-click "Reset crop" item and the properties-panel button;
  // also usable while cropping (resets the working crop to identity).
  resetCrop() {
    const slide = this.currentSlide();
    const index = this._cropMode ? this._cropIndex : this.selectedElement;
    const element = slide && index !== null && slide.elements[index];
    if (!element) return;
    if (this._cropMode) {
      element.crop = { scale: 1, x: 0, y: 0 };
    } else {
      delete element.crop;
      this.markDirty();
    }
    this.renderCanvas();
    this.renderProperties();
  }

  // Draw the crop overlay for the element being cropped. Appended to the canvas
  // surface AFTER the elements/handles (so it sits on top). Structure:
  //   - a full-canvas DIM layer (click = done) so the image reads as "framed"
  //   - the box window: a bright, non-dimmed cut-out at the element's x/y/w/h that
  //     shows the FULL image (object-fit:cover + the working transform) — this is
  //     exactly what will render after exit, so the author sees WYSIWYG live
  //   - a draggable surface over the window: pointer-drag PANS the image (updates
  //     crop.x / crop.y in % of the box); the wheel zooms
  //   - a floating toolbar: a zoom slider + Reset + Done, so the whole gesture is
  //     testable without wheel/pinch
  // Everything here is EDITOR CHROME — nothing but element.crop is written to
  // deck_json, and that write mirrors the server render (parity via cropTransform).
  renderCropOverlay(surface, slide) {
    const element = this._cropIndex !== null && slide.elements[this._cropIndex];
    if (!this.cropEditable(element)) { this._cropMode = false; return; }
    const src = this.resolveValue(element);
    if (!src) { this._cropMode = false; return; }

    const x = this.int(element.x), y = this.int(element.y);
    const w = this.int(element.w), h = this.int(element.h);
    const crop = element.crop;

    // Full-canvas dim layer so the framed region reads as bright. Crop mode is
    // STICKY (card #2130 follow-up): a stray click on the dim must NOT exit — the
    // author zooms, pans, and re-zooms freely, and leaves ONLY via the explicit Done
    // button (or Escape). Swallow the click so it neither exits nor deselects.
    const dim = document.createElement("div");
    dim.className = "deck-editor-crop-dim";
    dim.dataset.action = "pointerdown->deck-editor#swallowCropClick click->deck-editor#swallowCropClick";
    surface.appendChild(dim);

    // The box window — the live preview of the cropped result. object-fit:cover +
    // the working transform, clipped by overflow:hidden, exactly as the render paths
    // draw a cropped image. Sits above the dim so the framed region reads as bright.
    const window_ = document.createElement("div");
    window_.className = "deck-editor-crop-window";
    window_.setAttribute(
      "style",
      `left:${x}px;top:${y}px;width:${w}px;height:${h}px;`
    );
    const img = document.createElement("img");
    img.dataset.deckEditorTarget = "cropImage";
    const fit = (element.options && element.options.fit) || "cover";
    img.setAttribute(
      "style",
      `display:block;width:100%;height:100%;object-fit:${fit};` +
        `transform:${this.cropTransform(crop) || "none"};transform-origin:center center;`
    );
    img.src = src;
    img.draggable = false;
    window_.appendChild(img);
    // Pan surface: capture the pointer and drag to pan the image behind the box.
    // The wheel action is marked non-passive (:!passive) so cropWheel's
    // preventDefault() actually suppresses page scroll while zooming.
    window_.dataset.action = "pointerdown->deck-editor#startCropPan wheel->deck-editor#cropWheel:!passive";
    surface.appendChild(window_);

    // Resize grips over the crop window (card #2130 follow-up): the author reshapes
    // the crop box from any edge WHILE cropping. Reuse the same eight-grip handle,
    // marked is-selected so the grips show, and is-cropping so it stacks ABOVE the
    // dim/window (the normal handle z-index sits below the overlay). startResize
    // detects _cropMode and resizes the cropped box, refreshing the preview.
    const cropHandle = this.handleNode(element, this._cropIndex);
    cropHandle.classList.add("is-selected", "is-cropping");
    surface.appendChild(cropHandle);

    // Floating toolbar in the box's top-left, inside the canvas so it scales with it.
    const bar = document.createElement("div");
    bar.className = "deck-editor-crop-bar";
    bar.setAttribute("style", `left:${x}px;top:${Math.max(0, y - 44)}px;`);

    const label = document.createElement("span");
    label.className = "deck-editor-crop-bar-label";
    label.textContent = "Zoom";
    bar.appendChild(label);

    const zoom = document.createElement("input");
    zoom.type = "range";
    zoom.min = "100";
    zoom.max = "400";
    zoom.step = "1";
    zoom.value = String(Math.round((parseFloat(crop.scale) || 1) * 100));
    zoom.dataset.deckEditorTarget = "cropZoom";
    zoom.dataset.action = "input->deck-editor#cropZoomInput";
    bar.appendChild(zoom);

    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "deck-editor-crop-bar-btn";
    reset.textContent = "Reset";
    reset.dataset.action = "click->deck-editor#resetCrop";
    bar.appendChild(reset);

    const done = document.createElement("button");
    done.type = "button";
    done.className = "deck-editor-crop-bar-btn deck-editor-crop-bar-btn--primary";
    done.textContent = "Done";
    done.dataset.action = "click->deck-editor#exitCropMode";
    bar.appendChild(done);

    surface.appendChild(bar);
  }

  // Live preview <img> refresh — repaint just the transform (and the zoom slider)
  // during a gesture without a full renderCanvas, so pan/zoom stays smooth.
  refreshCropPreview() {
    const slide = this.currentSlide();
    const element = this._cropIndex !== null && slide && slide.elements[this._cropIndex];
    if (!element || !element.crop) return;
    if (this.hasCropImageTarget) {
      const fit = (element.options && element.options.fit) || "cover";
      this.cropImageTarget.setAttribute(
        "style",
        `display:block;width:100%;height:100%;object-fit:${fit};` +
          `transform:${this.cropTransform(element.crop) || "none"};transform-origin:center center;`
      );
    }
    if (this.hasCropZoomTarget) {
      this.cropZoomTarget.value = String(Math.round((parseFloat(element.crop.scale) || 1) * 100));
    }
  }

  // Pan gesture: drag the image behind the box. The delta is converted from screen
  // pixels to canvas pixels (÷ this.scale, same as move/resize), then to a PERCENT of
  // the box (the transform's translate unit). Pan is clamped so the scaled image can
  // never be dragged off the box (no empty gap shows through the window).
  startCropPan(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const slide = this.currentSlide();
    const element = slide.elements[this._cropIndex];
    if (!element || !element.crop) return;

    const w = this.int(element.w), h = this.int(element.h);
    const startX = event.clientX, startY = event.clientY;
    const originXPct = parseFloat(element.crop.x) || 0;
    const originYPct = parseFloat(element.crop.y) || 0;

    const onMove = (e) => {
      const dxPx = (e.clientX - startX) / this.scale;   // screen → canvas px
      const dyPx = (e.clientY - startY) / this.scale;
      // px → % of box (translate is relative to the transformed element's own size).
      const dxPct = w ? (dxPx / w) * 100 : 0;
      const dyPct = h ? (dyPx / h) * 100 : 0;
      element.crop.x = this.clampPan(originXPct + dxPct, element.crop.scale);
      element.crop.y = this.clampPan(originYPct + dyPct, element.crop.scale);
      this.refreshCropPreview();
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Zoom slider: set crop.scale from the range input (100–400 → 1.0–4.0), re-clamp the
  // pan to the new scale so a zoom-out can't reveal an empty edge, and repaint.
  cropZoomInput(event) {
    const slide = this.currentSlide();
    const element = slide.elements[this._cropIndex];
    if (!element || !element.crop) return;
    const scale = Math.max(1, (parseInt(event.target.value, 10) || 100) / 100);
    element.crop.scale = scale;
    element.crop.x = this.clampPan(element.crop.x, scale);
    element.crop.y = this.clampPan(element.crop.y, scale);
    this.refreshCropPreview();
  }

  // Wheel zoom over the box: a small step per notch, clamped to 1.0–4.0. Convenience
  // gesture; the slider is the primary (and testable) control.
  cropWheel(event) {
    const slide = this.currentSlide();
    const element = slide.elements[this._cropIndex];
    if (!element || !element.crop) return;
    event.preventDefault();
    const step = event.deltaY < 0 ? 0.05 : -0.05;
    const scale = this.clamp((parseFloat(element.crop.scale) || 1) + step, 1, 4);
    element.crop.scale = scale;
    element.crop.x = this.clampPan(element.crop.x, scale);
    element.crop.y = this.clampPan(element.crop.y, scale);
    this.refreshCropPreview();
  }

  // Clamp a pan percentage so the scaled image always covers the box. With
  // object-fit:cover the image already fills the box at scale 1; scaling by S grows
  // it by (S−1) on each side, so the pan can range ±((S−1)/2 × 100)% before an empty
  // edge would show. At scale 1 the only valid pan is 0.
  clampPan(pct, scale) {
    const s = parseFloat(scale) || 1;
    const limit = ((s - 1) / 2) * 100;
    return this.clamp(parseFloat(pct) || 0, -limit, limit);
  }

  // Eligibility for the PLAIN inline canvas editor (card #1977). Gates
  // startInlineEdit only. Only HEADLINES qualify: text is authored as rich text in
  // the Lexxy properties panel (card #2043) — letting the plain textarea edit it
  // would surface raw HTML and flatten the content on commit. Stat visuals and
  // slot-bound elements draw resolved data, not free text, so they opt out too.
  inlineEditable(element) {
    if (!element) return false;
    if (element.kind !== "headline") return false;
    if (this.isStatVisual(element)) return false;
    if (element.slot) return false;
    return true;
  }

  // Eligibility for the right-click "Edit text" menu item (card #1977). Broader
  // than inlineEditable: it covers headlines (→ plain inline canvas textarea), text
  // (→ rich-text Lexxy panel via editPanelContent) and html (→ the plain content
  // textarea in the panel via editPanelContent). The menu item is the single way to
  // start editing now that the double-click trigger is gone. Stat visuals and
  // slot-bound elements render resolved data, not free text, so they opt out.
  textEditable(element) {
    if (!element) return false;
    if (!["headline", "text", "html"].includes(element.kind)) return false;
    if (this.isStatVisual(element)) return false;
    if (element.slot) return false;
    return true;
  }

  startInlineEdit(event) {
    const element = this.currentSlide()?.elements?.[this.selectedElement];
    if (!this.inlineEditable(element)) return;
    if (this._inlineEditorEl) return;

    this._flushBurst();

    const options = element.options || {};
    const isHeadline = element.kind === "headline";
    const fontSize = this.int(options.size) || (isHeadline ? 48 : 24);
    const fontWeight = options.weight || (isHeadline ? 800 : 400);
    const color = options.color || "#000000";
    const align = options.align || "left";

    const ta = document.createElement("textarea");
    ta.value = element.content || "";
    ta.style.cssText = [
      "position:absolute",
      `left:${this.int(element.x)}px`,
      `top:${this.int(element.y)}px`,
      `width:${this.int(element.w)}px`,
      `height:${this.int(element.h)}px`,
      `font-size:${fontSize}px`,
      `font-weight:${fontWeight}`,
      `color:${color}`,
      `text-align:${align}`,
      "font-family:inherit",
      "line-height:1.2",
      "background:rgba(255,255,255,0.92)",
      "border:2px dashed #6366f1",
      "border-radius:2px",
      "outline:none",
      "resize:none",
      "overflow:hidden",
      "padding:4px 6px",
      "margin:0",
      "box-sizing:border-box",
      "z-index:200",
    ].join(";");

    this._inlineEditorEl = ta;
    this.canvasMountTarget.appendChild(ta);
    ta.focus();
    ta.select();

    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      // Write to the element-id override map, NOT element.content (ADR-0094, card
      // #1978). deck_json stays the stable authored template; result_json's
      // _manual_overrides is the ONE place any live edit lives, matching the
      // slot-bound override's shape (result_json["_manual_overrides"][id]). The id
      // is minted here, lazily, the first time this element is ever edited.
      const id = this.ensureElementId(element);
      this.result = this.result || {};
      // Guard the pre-ADR-0094 flat-array shape (e.g. deck #23 before its one-off
      // data fix runs): spreading an array would produce numeric-key junk, so an
      // existing non-map value is dropped rather than corrupted. Any decks still
      // on the array shape are migrated server-side (Maintenance::
      // ConvertDeckManualOverridesToMapTask), not by this client-side write path.
      const existingOverrides = this.result["_manual_overrides"];
      const overrides = (existingOverrides && typeof existingOverrides === "object" && !Array.isArray(existingOverrides))
        ? { ...existingOverrides }
        : {};
      overrides[id] = { content: ta.value };
      this.result = { ...this.result, _manual_overrides: overrides };
      const stillInDom = ta.isConnected;
      if (stillInDom) ta.remove();
      this._inlineEditorEl = null;
      this.markDirty();
      requestAnimationFrame(() => {
        this.renderCanvas();
        this.renderProperties();
      });
    };
    const cancel = () => {
      if (committed) return;
      committed = true;
      const stillInDom = ta.isConnected;
      if (stillInDom) ta.remove();
      this._inlineEditorEl = null;
    };

    ta.addEventListener("blur", commit);
    ta.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        cancel();
      }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        ta.blur();
      }
    });
  }

  // Panel-content edit path for TEXT and HTML elements (card #1977). Their content
  // is authored in the right Properties panel, NOT in the plain inline canvas
  // textarea — text in a Lexxy rich-text editor (card #2043, raw HTML would be
  // surfaced and flattened by the canvas editor), html in a plain content textarea
  // (raw markup the author types verbatim). "Edit text" on these kinds routes here.
  // The element is already selected (openContextMenu selects it before building the
  // menu), so renderProperties has already drawn the field into propertiesTarget.
  // We (1) surface the Properties panel in case the author was on Bindings/Activity,
  // then (2) focus the field's edit surface with the caret at the end, and (3) glow
  // the field so the author's eye — still on the canvas where they right-clicked —
  // is drawn to the panel. Lexxy mounts its ProseMirror surface a frame or two after
  // the custom element connects, so we poll a few frames before giving up.
  editPanelContent() {
    const element = this.currentSlide()?.elements?.[this.selectedElement];
    if (!element || (element.kind !== "text" && element.kind !== "html")) return;

    this.activateRightPanel("properties");

    if (element.kind === "html") {
      // An html element's RENDERED markup lives in options.html (the canvas injects
      // it as innerHTML; the slide partial emits raw(options["html"])). The panel
      // renders TWO textareas: a generic "Content" field (element.content, unused for
      // html — defaults to null and is not rendered) and the "HTML" field bound to
      // options.html. We must focus the HTML field, not the first textarea. Each
      // field is a <label> from fieldWrap whose leading <span> is the label text, so
      // match the span text "HTML" and focus that label's textarea.
      const htmlField = Array.from(this.propertiesTarget.querySelectorAll("label"))
        .find((lbl) => lbl.querySelector("span")?.textContent === "HTML");
      const textarea = htmlField?.querySelector("textarea");
      if (!textarea) return;
      this.pulseRichTextEditor(textarea);
      textarea.focus();
      const len = textarea.value.length;
      textarea.setSelectionRange(len, len);
      return;
    }

    // Text → Lexxy. The ProseMirror surface mounts asynchronously; retry across a
    // few animation frames so the focus lands once it exists rather than racing it.
    const editorEl = this.propertiesTarget.querySelector("lexxy-editor");
    if (!editorEl) return;
    let attempts = 0;
    const focusWhenReady = () => {
      const editable = editorEl.querySelector("[contenteditable='true']") ||
        editorEl.querySelector(".ProseMirror");
      if (editable) {
        this.pulseRichTextEditor(editorEl);
        editable.focus();
        this.placeCaretAtEnd(editable);
        return;
      }
      if (attempts++ < 5) requestAnimationFrame(focusWhenReady);
    };
    requestAnimationFrame(focusWhenReady);
  }

  // Move the caret to the end of a contenteditable surface (card #1977). The author
  // right-clicked an existing text element to keep writing, so the caret belongs at
  // the end of the content, not selecting/replacing it.
  placeCaretAtEnd(editable) {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(editable);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // Brief focus cue on the panel content field (card #1977) — the Lexxy editor for
  // a text element, or the plain content textarea for an html element. When "Edit
  // text" is chosen the author's eye is on the canvas where they right-clicked, but
  // the field is over in the right panel — non-technical users need their eye drawn
  // there. We scroll the field into view and toggle a Tailwind-layer pulse class (a
  // ~700ms ring/glow keyframe), removing it after the animation so it fires only on
  // this path, never on a plain panel render. No inline styles or inline JS — the
  // keyframe lives in the CSS layer, the toggle is here.
  pulseRichTextEditor(editorEl) {
    editorEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    editorEl.classList.remove("deck-editor__rich-text--pulse");
    // Force reflow so re-adding the class restarts the animation if it's still on
    // from a previous, very recent call.
    void editorEl.offsetWidth;
    editorEl.classList.add("deck-editor__rich-text--pulse");
    editorEl.addEventListener("animationend", () => {
      editorEl.classList.remove("deck-editor__rich-text--pulse");
    }, { once: true });
  }

  // ---- Hit-testing / click-cycling (card #1916) --------------------------

  // Convert a pointer event to a point in CANVAS (1280x720) coordinates. The
  // surface is rendered at `this.scale`, so screen offsets from the surface's
  // top-left divide by scale to land in canvas space — the same scale conversion
  // the drag handlers use for deltas.
  canvasPointFromEvent(event) {
    const surface = this.canvasMountTarget.querySelector(".airdrop-slide");
    if (!surface) return { x: 0, y: 0 };
    const rect = surface.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / this.scale,
      y: (event.clientY - rect.top) / this.scale
    };
  }

  // Indices of every element whose bbox contains the point, ordered FRONT to BACK
  // (descending array index, since the last element draws on top). The returned
  // order is the cycle order: clicking walks down this list.
  elementsAtPoint(point) {
    const slide = this.currentSlide();
    if (!slide || !Array.isArray(slide.elements)) return [];
    const hits = [];
    slide.elements.forEach((element, index) => {
      if (this.pointInElement(point, element)) hits.push(index);
    });
    return hits.reverse();
  }

  pointInElement(point, element) {
    const x = this.int(element.x);
    const y = this.int(element.y);
    const w = this.int(element.w);
    const h = this.int(element.h);
    return point.x >= x && point.x <= x + w && point.y >= y && point.y <= y + h;
  }

  // Given the front-to-back candidate stack and the handle that fired (the
  // front-most candidate, the fallback), pick which element this click selects:
  //   - empty stack → the handle's own element
  //   - selected element absent from the stack → the front-most candidate
  //   - selected element present → the NEXT one below it, wrapping front after back
  // so repeated clicks cycle down the stack and loop.
  cycleTargetIndex(stack, handleIndex) {
    if (!stack.length) return handleIndex;
    const at = stack.indexOf(this.selectedElement);
    if (at === -1) return stack[0];
    return stack[(at + 1) % stack.length];
  }

  // ---- Right-click context menu (card #1916) -----------------------------

  // Right-click over an element handle: build a small DOM menu at the cursor with
  // the z-order actions + delete, acting on the element the menu was opened over.
  // The DOM-built menu is the documented canvas-editor carve-out (no <script>, no
  // server HTML — same exception the canvas handles already use). It matches the
  // editor's modal styling (white panel, shadow-xl, border) and closes on
  // outside-click and Escape, mirroring the kind/asset picker idiom.
  //
  // The handle that fires this is the front-most element at the point; if more
  // elements sit under the cursor, the menu also offers "Select element below" so
  // a buried element can be reached without the click-cycle gesture.
  openContextMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    // Crop-mode guard (card #2130): the crop overlay's own contextmenu handling
    // covers the image; a right-click that reaches here must not open the element
    // menu and re-render the canvas out from under the crop session.
    if (this._cropMode) return;
    // Inline-edit guard (card #1977): a right-click while the textarea is open
    // selects → renderCanvas and tears the editor out. Stand down.
    if (this._inlineEditorEl) return;
    this.closeContextMenu();

    const handle = event.currentTarget;
    const handleIndex = parseInt(handle.dataset.index, 10);
    const slide = this.currentSlide();
    if (!slide || !Array.isArray(slide.elements) || !slide.elements[handleIndex]) return;

    // Candidates under the cursor, front to back. The handle that fired is always
    // the front-most candidate.
    const point = this.canvasPointFromEvent(event);
    const stack = this.elementsAtPoint(point);

    // Right-click acts on the element in focus, not the front-most one: if the
    // cursor is inside the currently-selected element, keep it focused; otherwise
    // fall back to the front-most handle. So right-clicking a buried-but-selected
    // element operates on it rather than jumping focus to the element on top
    // (#1916). Hit-test the selected element directly rather than checking stack
    // membership: the predicate is "is the cursor over the focused element", which
    // pointInElement answers without depending on the index surviving in `stack`.
    const selected = this.selectedElement !== null ? slide.elements[this.selectedElement] : null;
    const elIndex = (selected && this.pointInElement(point, selected))
      ? this.selectedElement
      : handleIndex;

    // Select the element the menu acts on, so the panel + canvas track it and the
    // shared layering helpers (which act on selectedElement) operate on the right
    // element when a menu item is chosen.
    this.selectElement(elIndex);

    // "Select element below" targets the element beneath the focused one in the
    // stack (if any).
    const at = stack.indexOf(elIndex);
    const belowIndex = at !== -1 && at < stack.length - 1 ? stack[at + 1] : null;

    const count = slide.elements.length;
    const atFront = elIndex >= count - 1;
    const atBack = elIndex <= 0;

    // Header: name the element the menu acts on, reusing layerLabel so it reads the
    // same as the Layers list. layerLabel already falls back to the kind when there
    // is no content/slot, so prefix the kind only when it adds information (avoids a
    // "shape · shape" repeat). Layer position (1-based, front-most last) for context.
    const target = slide.elements[elIndex];
    const label = this.layerLabel(target);
    const headerLabel = label === target.kind ? label : `${target.kind} · ${label}`;

    const items = [
      { header: true, label: headerLabel },
      { subheader: true, label: `layer ${elIndex + 1} of ${count}` },
      { separator: true }
    ];

    // "Edit text" (card #1977): the single entry point for editing text now that
    // the double-click trigger is gone (it never fired with a real mouse). The
    // element is already selected above, so the action edits the current selection.
    // Branch by kind: headlines open the plain inline canvas textarea; text and html
    // open their field in the right Properties panel (text → Lexxy, html → its plain
    // content textarea) so rich text is never flattened and raw HTML is edited verbatim.
    if (this.textEditable(target)) {
      const editAction = target.kind === "headline"
        ? () => this.startInlineEdit()
        : () => this.editPanelContent();
      items.push({ label: "Edit text", action: editAction });
      items.push({ separator: true });
    }

    // "Crop" (card #2130): enter non-destructive crop mode on a placed image element,
    // mirroring the "Edit text" entry-point pattern (#1977). The element is already
    // selected above, so crop mode acts on the current selection. A cropped image also
    // gets "Reset crop" to clear the pan/zoom back to the plain cover render.
    if (this.cropEditable(target)) {
      items.push({ label: "Crop", action: () => this.enterCropMode() });
      if (this.cropTransform(target.crop)) {
        items.push({ label: "Reset crop", action: () => this.resetCrop() });
      }
      items.push({ separator: true });
    }

    items.push(
      { label: "Bring to front", method: "bringToFront", disabled: atFront },
      { label: "Bring forward", method: "bringForward", disabled: atFront },
      { label: "Send backward", method: "sendBackward", disabled: atBack },
      { label: "Send to back", method: "sendToBack", disabled: atBack }
    );
    if (belowIndex !== null) {
      items.push({ separator: true });
      items.push({ label: "Select element below", action: () => this.selectElement(belowIndex) });
    }
    items.push({ separator: true });
    items.push({ label: "Duplicate", method: "duplicateElement" });
    items.push({ label: "Delete", method: "deleteElement", danger: true });

    this.presentContextMenu(this.buildContextMenu(items, event.clientX, event.clientY));
  }

  // Right-click on EMPTY canvas (not over an element handle — those stopPropagation
  // in openContextMenu) opens a slide-level menu to insert a new element at the
  // cursor. Canvas slides only; the title slide is read-only (ADR-0065). (#1916)
  openSlideContextMenu(event) {
    // Inline-edit guard (card #1977): a right-click on the canvas while the
    // textarea is open must not open a slide menu that re-renders the canvas.
    if (this._inlineEditorEl) return;
    const slide = this.currentSlide();
    if (!this.editableSlide(slide)) return;
    // Only act on a genuine empty-canvas target — the mount or the slide surface —
    // mirroring canvasBackgroundClick. A handle's own contextmenu is handled by
    // openContextMenu and never bubbles here.
    const onSurface = event.target === this.canvasMountTarget ||
      event.target.classList.contains("airdrop-slide");
    if (!onSurface) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeContextMenu();

    const point = this.canvasPointFromEvent(event);
    const items = [
      { header: true, label: "Insert element" },
      { separator: true },
      { label: "Headline", action: () => this.insertElementAt("headline", point) },
      { label: "Text",     action: () => this.insertElementAt("text", point) },
      { label: "Image",    action: () => this.insertElementAt("image", point) },
      { label: "Shape",    action: () => this.insertElementAt("shape", point) },
      { label: "HTML",      action: () => this.insertElementAt("html", point) },
      { label: "Analytics", action: () => this.insertElementAt("analytics", point) },
      { label: "Clock",     action: () => this.insertElementAt("clock", point) },
      ...this.liveFramesValue.map((frame) => (
        { label: `Live frame: ${frame.label}`, action: () => this.insertElementAt("janela_frame", point, { frame: frame.key }) }
      )),
      { separator: true },
      // Object — bind a WIN record at insert (ADR-0070 §2/D1). Captures the cursor
      // point so the resolved concrete element drops where the author right-clicked.
      { label: "Object…",  action: () => this.openObjectChooser(point) }
    ];
    this.presentContextMenu(this.buildContextMenu(items, event.clientX, event.clientY));
  }

  // Right-click on a SLIDES sidebar row opens a slide-level menu: duplicate, add
  // after, reorder, delete. Index is read from the row's dataset (set in
  // renderSlideList). The menu mounts on document.body so it survives the
  // renderAll() that the selection triggers.
  openSlideRowContextMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeContextMenu();

    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index) || index < 0 || index >= this.deck.slides.length) return;

    // Select the right-clicked slide for clear UX. Index is already captured, so the
    // re-render is safe to run before the menu is presented.
    this.selectedSlide = index;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.renderAll();

    const items = [
      { header: true, label: this.slideLabel(this.deck.slides[index]) },
      { separator: true },
      { label: "Duplicate slide", action: () => this.duplicateSlideAt(index) },
      { label: "Add slide after", action: () => this.addSlideAfter(index) },
      { separator: true },
      { label: "Move up",   action: () => this.swapSlides(index, index - 1), disabled: index <= 0 },
      { label: "Move down", action: () => this.swapSlides(index, index + 1), disabled: index >= this.deck.slides.length - 1 }
    ];

    // "Apply template…" reuses the kind-picker modal in apply mode to swap this
    // slide's layout in place (see openKindPickerForApply / applyTemplateToSlide).
    items.push({ separator: true }, { label: "Apply template…", action: () => this.openKindPickerForApply(index) });

    // Destructive action stays last.
    items.push({ separator: true }, { label: "Delete slide", danger: true, action: () => this.deleteSlideAt(index) });

    this.presentContextMenu(this.buildContextMenu(items, event.clientX, event.clientY));
  }

  // Mount a built context menu and wire its outside-click + Escape dismissal.
  // Shared by the element menu (openContextMenu) and the slide menu
  // (openSlideContextMenu). Mirrors the modal dismissal idiom (#1916).
  presentContextMenu(menu) {
    this.contextMenuEl = menu;
    document.body.appendChild(menu);
    this.boundContextMenuDismiss = (e) => {
      if (this.contextMenuEl && !this.contextMenuEl.contains(e.target)) this.closeContextMenu();
    };
    this.boundContextMenuKeydown = (e) => {
      if (e.key === "Escape") this.closeContextMenu();
    };
    document.addEventListener("pointerdown", this.boundContextMenuDismiss, true);
    document.addEventListener("keydown", this.boundContextMenuKeydown);
  }

  // Build the positioned menu panel from the item list. Each item runs either a
  // named controller method (the layering/delete helpers, which act on the
  // selected element) or an inline action (Select element below), then closes.
  buildContextMenu(items, clientX, clientY) {
    const menu = document.createElement("div");
    menu.className = "fixed z-[60] min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 text-sm shadow-xl";
    menu.style.left = `${clientX}px`;
    menu.style.top = `${clientY}px`;

    items.forEach((item) => {
      if (item.separator) {
        const hr = document.createElement("div");
        hr.className = "my-1 border-t border-gray-100";
        menu.appendChild(hr);
        return;
      }

      // Header: a non-interactive caption naming the target element. Styled like the
      // section labels elsewhere; not a <button> so it carries no hover/click/focus.
      if (item.header) {
        const caption = document.createElement("div");
        caption.className = "px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 truncate max-w-[16rem]";
        caption.textContent = item.label;
        menu.appendChild(caption);
        return;
      }

      // Subheader: a secondary caption row, lighter weight, no uppercase.
      if (item.subheader) {
        const sub = document.createElement("div");
        sub.className = "px-3 pb-1.5 text-xs text-gray-400";
        sub.textContent = item.label;
        menu.appendChild(sub);
        return;
      }

      const button = document.createElement("button");
      button.type = "button";
      const tone = item.danger ? "text-red-600 hover:bg-red-50" : "text-gray-700 hover:bg-gray-50";
      button.className = `block w-full px-3 py-1.5 text-left ${tone} disabled:cursor-not-allowed disabled:opacity-40`;
      button.textContent = item.label;
      button.disabled = !!item.disabled;
      button.addEventListener("click", () => {
        this.closeContextMenu();
        if (item.action) item.action();
        else if (item.method) this[item.method]();
      });
      menu.appendChild(button);
    });

    // Nudge back on-screen if the menu would overflow the viewport edges.
    requestAnimationFrame(() => this.constrainContextMenu(menu, clientX, clientY));
    return menu;
  }

  // Keep the menu inside the viewport: if its measured box runs past the right or
  // bottom edge, flip it to open above/left of the cursor instead.
  constrainContextMenu(menu, clientX, clientY) {
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) menu.style.left = `${Math.max(0, clientX - rect.width)}px`;
    if (rect.bottom > window.innerHeight) menu.style.top = `${Math.max(0, clientY - rect.height)}px`;
  }

  closeContextMenu() {
    if (this.contextMenuEl) {
      this.contextMenuEl.remove();
      this.contextMenuEl = null;
    }
    if (this.boundContextMenuDismiss) {
      document.removeEventListener("pointerdown", this.boundContextMenuDismiss, true);
      this.boundContextMenuDismiss = null;
    }
    if (this.boundContextMenuKeydown) {
      document.removeEventListener("keydown", this.boundContextMenuKeydown);
      this.boundContextMenuKeydown = null;
    }
  }

  // ---- Slides: add / delete / reorder ------------------------------------

  addSlide() {
    this.deck.slides.push({
      id: crypto.randomUUID(),
      type: "canvas",
      variant: "freeform",
      options: { background: "white" },
      elements: []
    });
    this.selectedSlide = this.deck.slides.length - 1;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.fitStage();
    this.renderAll();
  }

  // Insert a COLLECTION slide (ADR-0070 §4b). A collection slide binds to a WIN
  // collection (v1: an Album) and prints as N concrete canvas pages at render time
  // (Airdrop::CollectionExpander) — the deck never stores the expanded copies. It
  // is authored ONCE: the static chrome on `elements`, the binding + grid on
  // `collection`. The album is chosen in the inspector (renderCollectionProperties),
  // so the insert needs no modal — it drops an unbound slide (source_id null) with
  // the expander's defaults (page_size 6, columns 3), selects it, and redraws.
  addCollectionSlide() {
    this.deck.slides.push({
      id: crypto.randomUUID(),
      type: "collection",
      collection: { source: "album", source_id: null, page_size: 6, columns: 3 },
      options: { background: "sapphire" },
      elements: []
    });
    this.selectedSlide = this.deck.slides.length - 1;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.fitStage();
    this.renderAll();
  }

  // Deep-clone the slide at `index` and insert the copy directly after it, then
  // select the copy. Note: a literal clone copies any bound slot names verbatim,
  // so both slides reference the same slot — that's acceptable; the author renames
  // bindings on the new slide as needed.
  duplicateSlideAt(index) {
    const copy = JSON.parse(JSON.stringify(this.deck.slides[index]));
    // The deep-clone copies the source slide's id; mint a FRESH one so the copy
    // is its own slide — two slides must never share a stable id (card #2120).
    copy.id = crypto.randomUUID();
    this.deck.slides.splice(index + 1, 0, copy);
    this.selectedSlide = index + 1;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.fitStage();
    this.renderAll();
  }

  // Insert a fresh blank canvas slide (identical to addSlide's default) directly
  // after `index`, then select it.
  addSlideAfter(index) {
    this.deck.slides.splice(index + 1, 0, {
      id: crypto.randomUUID(),
      type: "canvas",
      variant: "freeform",
      options: { background: "white" },
      elements: []
    });
    this.selectedSlide = index + 1;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.fitStage();
    this.renderAll();
  }

  deleteSlide(event) {
    event.stopPropagation();
    const index = parseInt(event.currentTarget.closest("[data-deck-editor-target='slideRow']").dataset.index, 10);
    this.deleteSlideAt(index);
  }

  // "Hidden from client" sidebar toggle (card #3204) -- the build editor's
  // own copy of the show page's toolbar toggle. Flips slide.hidden on the
  // in-memory working slide (same as any other row mutation here), marks the
  // deck dirty, and re-renders the sidebar so the row's grey treatment and
  // icon state (renderSlideList above) reflect the new value immediately.
  // Persisted through the SAME explicit-save path (#persistDeck) every other
  // editor change goes through -- no separate write, no autosave.
  // UN-hiding only (not hiding) gets a confirm() guard -- card #3204 follow-
  // up, same rule and wording as deck_slide_hidden_toggle_controller.js's
  // show-page button. Cancelling is a true no-op: return before the
  // slide.hidden flip, so nothing is mutated, nothing marked dirty, no
  // re-render.
  toggleSlideHiddenFromRow(event) {
    event.stopPropagation();
    const index = parseInt(event.currentTarget.closest("[data-deck-editor-target='slideRow']").dataset.index, 10);
    const slide = this.deck.slides[index];
    if (!slide) return;

    const next = !slide.hidden;
    if (!next) {
      const confirmed = confirm(
        "Unhide this slide? It will become visible again in Present, the public share link, and PDF/PPTX export."
      );
      if (!confirmed) return;
    }

    slide.hidden = next;
    this.markDirty();
    this.renderSlideList();
  }

  // Index-based delete. Shared by the × button (deleteSlide) and the slide-row
  // context menu (openSlideRowContextMenu) so both go through one code path.
  deleteSlideAt(index) {
    this.deck.slides.splice(index, 1);
    if (this.deck.slides.length === 0) {
      this.selectedSlide = null;
    } else if (this.selectedSlide >= this.deck.slides.length) {
      this.selectedSlide = this.deck.slides.length - 1;
    }
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.renderAll();
  }

  moveSlideUp(event) {
    event.stopPropagation();
    const index = parseInt(event.currentTarget.closest("[data-deck-editor-target='slideRow']").dataset.index, 10);
    if (index <= 0) return;
    this.swapSlides(index, index - 1);
  }

  moveSlideDown(event) {
    event.stopPropagation();
    const index = parseInt(event.currentTarget.closest("[data-deck-editor-target='slideRow']").dataset.index, 10);
    if (index >= this.deck.slides.length - 1) return;
    this.swapSlides(index, index + 1);
  }

  swapSlides(a, b) {
    const slides = this.deck.slides;
    [slides[a], slides[b]] = [slides[b], slides[a]];
    if (this.selectedSlide === a) this.selectedSlide = b;
    else if (this.selectedSlide === b) this.selectedSlide = a;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.renderAll();
  }

  // Drag-and-drop counterpart to swapSlides (card #2770). A drag MOVES a slide
  // to a new position rather than swapping two -- every slide between the old
  // and new spot shifts by one, so selectedSlide is re-derived from oldIndex/
  // newIndex rather than reused from the two-index swap logic above.
  // oldIndex/newIndex come from Sortable.js's own onEnd event, NOT
  // row.dataset.index -- that only reflects the index at last render and goes
  // stale the instant Sortable starts moving DOM nodes mid-drag.
  moveSlideByDrag(oldIndex, newIndex) {
    if (oldIndex == null || newIndex == null) return;
    if (oldIndex === newIndex) {
      // A drag that settles back where it started -- e.g. released outside any
      // valid drop target and snapped back -- still needs the multi-select
      // checkboxes/highlight re-synced from multiSelectedIndices (card #2788
      // revision: a missed drop must never uncheck a row). Sortable's own
      // MultiDrag plugin manages its own internal DOM-side selection state
      // during the drag lifecycle and may have altered it along the way;
      // renderSlideList() reapplies our Set as the single source of truth.
      // Nothing in the underlying data changed, so a full renderAll (and the
      // markDirty it would imply) isn't warranted -- just the slide list.
      this.renderSlideList();
      return;
    }
    const slides = this.deck.slides;
    const [moved] = slides.splice(oldIndex, 1);
    slides.splice(newIndex, 0, moved);

    const remap = (index) => {
      if (index === oldIndex) return newIndex;
      if (oldIndex < newIndex) {
        return (index > oldIndex && index <= newIndex) ? index - 1 : index;
      }
      return (index >= newIndex && index < oldIndex) ? index + 1 : index;
    };

    if (this.selectedSlide !== null && this.selectedSlide !== undefined) {
      this.selectedSlide = remap(this.selectedSlide);
    }
    // Re-map any lingering multi-selection too (card #2788) -- covers dragging
    // a row that ISN'T part of the active multi-selection while other rows
    // stay selected; without this, their highlighted indices would point at
    // the wrong slides once the array shifts underneath them.
    if (this.multiSelectedIndices.size) {
      this.multiSelectedIndices = new Set(Array.from(this.multiSelectedIndices, remap));
    }

    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.renderAll();
  }

  // Drag counterpart for a STACKED multi-selection (card #2788), generalising
  // moveSlideByDrag above to move several slides as one contiguous block while
  // preserving their relative order. `oldIndices` are the ORIGINAL (pre-drag)
  // indices of every actively multi-selected row; `newIndex` is the settled
  // index the moved block's FIRST (lowest original-index) slide should land
  // at, in the same "index in the final list" sense moveSlideByDrag already
  // uses for a single slide (handleSlideDragEnd derives this from Sortable's
  // own evt.newIndex).
  //
  // Rather than hand-deriving shift arithmetic for every OTHER index that
  // might be watching (selectedSlide, the rest of multiSelectedIndices), this
  // replays the exact same splice-out/splice-in pair against a parallel array
  // of plain index labels (0..n-1) instead of the real slide objects, then
  // reads off where each original index landed. Whatever happens to labels[i]
  // is, by construction, exactly what happens to slides[i].
  //
  // A selection of one (or zero) is byte-for-byte the card #2770 path -- the
  // "no multi-select active, nothing changes" guarantee from the plan.
  moveSlidesByDrag(oldIndices, newIndex) {
    const uniqueSorted = Array.from(new Set(oldIndices)).sort((a, b) => a - b);
    if (!uniqueSorted.length || newIndex == null) return;

    if (uniqueSorted.length === 1) {
      this.moveSlideByDrag(uniqueSorted[0], newIndex);
      return;
    }

    const slides = this.deck.slides;
    const labels = slides.map((_, i) => i);

    const movedSlides = uniqueSorted.map((i) => slides[i]);
    const movedLabels = uniqueSorted.map((i) => labels[i]);
    for (let i = uniqueSorted.length - 1; i >= 0; i--) {
      slides.splice(uniqueSorted[i], 1);
      labels.splice(uniqueSorted[i], 1);
    }

    const clampedIndex = Math.max(0, Math.min(newIndex, slides.length));
    slides.splice(clampedIndex, 0, ...movedSlides);
    labels.splice(clampedIndex, 0, ...movedLabels);

    const newPositionOf = new Array(labels.length);
    labels.forEach((originalIndex, newPos) => { newPositionOf[originalIndex] = newPos; });

    if (this.selectedSlide !== null && this.selectedSlide !== undefined) {
      this.selectedSlide = newPositionOf[this.selectedSlide];
    }
    this.multiSelectedIndices = new Set(uniqueSorted.map((i) => newPositionOf[i]));

    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.renderAll();
  }

  // ---- Elements: add / delete --------------------------------------------

  addElement(event) {
    const kind = event.params.kind || event.currentTarget.dataset.kind;
    const def = this.elementDefault(kind);
    this.pushElement(kind, def.x, def.y);
  }

  // Push a new default element of `kind` at canvas (x,y), select it, redraw.
  // Shared by addElement (panel chips, default position) and insertElementAt
  // (right-click insert, cursor position). Carries the canvas-slide guard.
  pushElement(kind, x, y, options = {}) {
    const slide = this.currentSlide();
    if (!this.editableSlide(slide)) return;
    const def = this.elementDefault(kind, options);
    slide.elements = slide.elements || [];
    slide.elements.push({
      kind, x, y, w: def.w, h: def.h,
      options: { ...def.options, ...options }, slot: null, content: def.content
    });
    this.selectedElement = slide.elements.length - 1;
    this.selectionIndices = [this.selectedElement];
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // Insert at the cursor: top-left at the snapped point, clamped so the element's
  // full default footprint stays on canvas (#1916).
  insertElementAt(kind, point, options = {}) {
    const def = this.elementDefault(kind, options);
    const x = this.clamp(this.snapValue(point.x), 0, this.canvasW - def.w);
    const y = this.clamp(this.snapValue(point.y), 0, this.canvasH - def.h);
    this.pushElement(kind, x, y, options);
  }

  // Elements panel "Live frame" tile: a Porthole report frame at the default spot.
  addLiveFrame(event) {
    const frame = event.params.frame;
    const def = this.elementDefault("janela_frame", { frame });
    this.pushElement("janela_frame", def.x, def.y, { frame });
  }

  // Clipboard paste — image files only (ADR-0073). Skips when focus is in a
  // text field so normal text paste is not intercepted.
  handlePaste(event) {
    const active = document.activeElement;
    if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)) return;

    const slide = this.currentSlide();
    if (!this.editableSlide(slide)) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) this.uploadPastedImage(file);
        break;
      }
    }
  }

  // Upload a pasted image file via Rails ActiveStorage direct upload, then
  // insert it as an image element at canvas centre. ADR-0073.
  uploadPastedImage(file) {
    const directUploadUrl = document.querySelector('meta[name="direct-upload-url"]')?.content;
    if (!directUploadUrl) {
      console.warn("[deck-editor] No direct-upload-url meta tag found");
      return;
    }

    const upload = new DirectUpload(file, directUploadUrl);
    upload.create((error, blob) => {
      if (error) {
        console.error("[deck-editor] Paste upload failed:", error);
        return;
      }

      const src = `/rails/active_storage/blobs/redirect/${blob.signed_id}/${encodeURIComponent(blob.filename)}`;
      const slide = this.currentSlide();
      if (!this.editableSlide(slide)) return;

      slide.elements = slide.elements || [];
      slide.elements.push({
        kind: "image",
        x: 340, y: 180, w: 600, h: 360,
        options: { fit: "contain", alt: "Pasted image", opacity: 100 },
        slot: null,
        content: src
      });
      this.selectedElement = slide.elements.length - 1;
      this.selectionIndices = [this.selectedElement];
      this.markDirty();
      this.renderCanvas();
      this.renderProperties();
    });
  }

  deleteElement(event) {
    const slide = this.currentSlide();
    if (!slide || this.selectedElement === null) return;
    slide.elements.splice(this.selectedElement, 1);
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // Clone the selected element and drop the copy one layer above the original
  // (card #1916). Deep-clone via JSON round-trip since elements are plain data —
  // this stops nested options sharing references with the source. The copy is
  // nudged ONE grid cell down-right so it reads as a distinct element AND lands
  // on-grid, clamped to keep it on canvas, then selected so the user can
  // immediately drag or edit it.
  duplicateElement() {
    const elements = this.selectedElements();
    if (!elements) return;
    const original = elements[this.selectedElement];
    const clone = JSON.parse(JSON.stringify(original));
    const offset = this.gridSize();
    clone.x = this.clamp(this.int(clone.x) + offset, 0, this.canvasW - this.int(clone.w));
    clone.y = this.clamp(this.int(clone.y) + offset, 0, this.canvasH - this.int(clone.h));
    const copyIndex = this.selectedElement + 1;
    elements.splice(copyIndex, 0, clone);
    this.selectedElement = copyIndex;
    this.selectionIndices = [copyIndex];
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // ---- Layering / z-order (card #1916) -----------------------------------

  // Render order equals array order in both render paths, so layering is purely
  // reordering slide.elements and keeping selectedElement pointed at the moved
  // element. Each guards at the array boundary (no-op when already there).

  // Move the selected element to the end of the array (drawn last, on top).
  bringToFront() {
    const elements = this.selectedElements();
    if (!elements || this.selectedElement >= elements.length - 1) return;
    const [element] = elements.splice(this.selectedElement, 1);
    elements.push(element);
    this.afterReorder(elements.length - 1);
  }

  // Swap the selected element with the one after it (one step up the stack).
  bringForward() {
    const elements = this.selectedElements();
    if (!elements || this.selectedElement >= elements.length - 1) return;
    const next = this.selectedElement + 1;
    [elements[this.selectedElement], elements[next]] = [elements[next], elements[this.selectedElement]];
    this.afterReorder(next);
  }

  // Swap the selected element with the one before it (one step down the stack).
  sendBackward() {
    const elements = this.selectedElements();
    if (!elements || this.selectedElement <= 0) return;
    const prev = this.selectedElement - 1;
    [elements[this.selectedElement], elements[prev]] = [elements[prev], elements[this.selectedElement]];
    this.afterReorder(prev);
  }

  // Move the selected element to the start of the array (drawn first, behind all).
  sendToBack() {
    const elements = this.selectedElements();
    if (!elements || this.selectedElement <= 0) return;
    const [element] = elements.splice(this.selectedElement, 1);
    elements.unshift(element);
    this.afterReorder(0);
  }

  // The selected element's containing array, or null when nothing is selectable.
  selectedElements() {
    const slide = this.currentSlide();
    if (!this.editableSlide(slide) || this.selectedElement === null) return null;
    if (!Array.isArray(slide.elements) || !slide.elements[this.selectedElement]) return null;
    return slide.elements;
  }

  // Shared tail for every layer move: track the element's new index, mark dirty,
  // and redraw the canvas + properties so the panel buttons re-evaluate boundaries.
  afterReorder(newIndex) {
    this.selectedElement = newIndex;
    this.selectionIndices = [newIndex];
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // ---- Properties panel (two-way: panel <-> state <-> canvas) ------------

  renderProperties() {
    if (!this.hasPropertiesTarget) return;
    this.renderBindings();
    this.propertiesTarget.replaceChildren();

    const slide = this.currentSlide();
    if (!slide) {
      this.propertiesTarget.appendChild(this.hint("Add a slide to begin."));
      return;
    }

    // Collection slide (ADR-0070 §4b): its chrome is fully editable, the same path
    // a canvas slide uses, so the panel switches the SAME way (mirroring the canvas
    // branch below). When a chrome element IS selected → element properties (the
    // canvas behaviour). When NO element is selected → the collection inspector
    // (album picker + grid / images-per-page / columns / badge) instead of plain
    // slide properties — the album is bound HERE, not via a modal. So selecting a
    // chrome element shows its props; deselecting returns to the collection inspector.
    if (slide.type === "collection") {
      if (this.selectedElement !== null && slide.elements && slide.elements[this.selectedElement]) {
        this.renderElementProperties(slide.elements[this.selectedElement]);
      } else {
        this.renderCollectionProperties(slide);
      }
      return;
    }

    if (slide.type !== "canvas") {
      this.propertiesTarget.appendChild(this.hint("This slide type is preview-only. Add a canvas slide to author elements."));
      return;
    }

    // Element selected → element properties; else slide properties.
    if (this.selectedElement !== null && slide.elements[this.selectedElement]) {
      this.renderElementProperties(slide.elements[this.selectedElement]);
    } else {
      this.renderSlideProperties(slide);
    }
  }

  renderSlideProperties(slide) {
    // Deck-level design (the font picker) lives on the Themes tab (card #1901),
    // promoted out of slide-properties. Slide-properties is now slide-scoped only.
    slide.options = slide.options || {};

    // Template source info + drift re-apply (card #2007). Shown near the top of
    // slide properties so authors see provenance immediately on selection.
    if (slide.template_source) {
      this.propertiesTarget.appendChild(this.sectionLabel("Template"));
      const info = document.createElement("p");
      info.className = "px-3 py-1 text-xs text-gray-500";
      info.textContent = `Based on: ${slide.template_source.label}`;
      this.propertiesTarget.appendChild(info);

      if (this._isSlideDrifted(slide)) {
        const reapplyBtn = document.createElement("button");
        reapplyBtn.type = "button";
        reapplyBtn.className = "mx-3 mb-2 flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100";
        reapplyBtn.innerHTML = '<span class="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0"></span> Template has changed · Re-apply';
        reapplyBtn.addEventListener("click", () => this._reapplyTemplate(slide));
        this.propertiesTarget.appendChild(reapplyBtn);
      }
    }

    this.propertiesTarget.appendChild(this.sectionLabel("Slide"));

    // Slide name (card #1825 follow-on): the human kind name, stored ON the slide
    // fragment as slide.name (ADR-0068 — kinds ARE slides). This is the rename
    // surface and the label the slide-list / Insert-kind gallery prefer. An empty
    // value clears back to undefined (falls back to the capitalised type). Only
    // refresh the slide list on input — re-rendering properties would steal focus.
    this.propertiesTarget.appendChild(
      this.textField("Slide name", slide.name || "", (v) => {
        slide.name = v.trim() === "" ? undefined : v.trim();
        this.markDirty();
        this.renderSlideList();
      })
    );

    // Slide kind (ADR-0078): the slide's semantic classification, from the closed
    // SLIDE_KINDS vocabulary (sourced server-side from Deck.slide_kind_options).
    // Stored as slide.kind in deck_json so it travels on copy/save-as-kind. A
    // blank value clears back to undefined ("Uncategorised") and is valid. The
    // library picker groups/filters by this. Re-render the slide list so its
    // label can reflect any kind-derived naming in future, but not properties
    // (that would steal focus mid-select).
    const kindKeys = [""].concat(Object.keys(this.slideKindsValue));
    const kindLabels = ["Uncategorised"].concat(Object.values(this.slideKindsValue));
    this.propertiesTarget.appendChild(
      this.selectFieldKeyed("Kind", slide.kind || "", kindKeys, kindLabels, (v) => {
        slide.kind = v === "" ? undefined : v;
        this.markDirty();
        this.renderSlideList();
      })
    );

    // Slide background (card #1916): the SAME freeform colour picker a shape fill
    // uses, so it gets the RT brand quick-pick chips for free. An empty value
    // clears back to undefined (resolves to the sapphire+white ground default).
    this.propertiesTarget.appendChild(
      this.colorField("Background", slide.options.background || "", (v) => {
        slide.options.background = (v.trim() === "" ? undefined : v.trim());
        this.markDirty();
        this.renderCanvas();
      })
    );

    // Per-slide page-number override (card #2093). Default/Always/Never against the
    // deck-wide master switch — see pageNumberControl. A cover/title is off by default
    // and can be forced on here; any slide can be forced off.
    this.propertiesTarget.appendChild(this.pageNumberControl(slide));

    // Layer list (card #1916): one row per element, TOP layer first (array end).
    // Clicking a row selects that element — the way to reach an element hidden
    // beneath another in the stack.
    this.propertiesTarget.appendChild(this.sectionLabel("Layers"));
    this.propertiesTarget.appendChild(this.renderLayerList(slide));

    // Save this slide as a reusable kind (card #1823).
    this.propertiesTarget.appendChild(this.sectionLabel("Reusable kind"));
    const saveKind = document.createElement("button");
    saveKind.type = "button";
    saveKind.className = "rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100";
    saveKind.textContent = "Save slide as kind";
    saveKind.dataset.action = "click->deck-editor#saveSlideAsKind";
    this.lastSaveKindButton = saveKind;
    this.propertiesTarget.appendChild(saveKind);
  }

  // The slide's elements as a clickable layer list, top layer first (the array is
  // iterated in reverse since the last element draws on top). Each row carries its
  // real array index and selects that element on click — this is how overlapping
  // elements stay reachable. Per-row ▲/▼ controls reorder THAT element directly
  // (card #1916): list order is reversed, so in the list "up" = toward front =
  // higher array index, "down" = toward back = lower array index. Empty slides
  // show a hint instead.
  renderLayerList(slide) {
    const elements = Array.isArray(slide.elements) ? slide.elements : [];
    if (!elements.length) return this.hint("No elements on this slide yet.");

    const list = document.createElement("div");
    list.className = "flex flex-col gap-1";
    for (let index = elements.length - 1; index >= 0; index--) {
      const element = elements[index];
      const row = document.createElement("div");
      row.className = "flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50";

      // The selectable face of the row (kind + label). A button so the whole face
      // is one click target that selects the element.
      const face = document.createElement("button");
      face.type = "button";
      face.className = "flex min-w-0 grow items-center gap-2 text-left";
      face.dataset.index = index;
      face.dataset.action = "click->deck-editor#selectElementFromLayer";

      const kind = document.createElement("span");
      kind.className = "shrink-0 text-2xs font-semibold uppercase tracking-wide text-gray-400";
      kind.textContent = element.kind;
      const label = document.createElement("span");
      label.className = "grow truncate";
      label.textContent = this.layerLabel(element);
      face.appendChild(kind);
      face.appendChild(label);

      // Per-row reorder controls. ▲ = toward front (index+1), ▼ = toward back
      // (index-1). Disabled at the boundaries. They act on this row's element via
      // the dataset index — no need to select it first.
      const up = document.createElement("button");
      up.type = "button";
      up.className = "shrink-0 px-1 text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30";
      up.textContent = "▲";
      up.title = "Move layer up (toward front)";
      up.dataset.index = index;
      up.dataset.action = "click->deck-editor#moveLayerUp";
      up.disabled = index >= elements.length - 1;

      const down = document.createElement("button");
      down.type = "button";
      down.className = "shrink-0 px-1 text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30";
      down.textContent = "▼";
      down.title = "Move layer down (toward back)";
      down.dataset.index = index;
      down.dataset.action = "click->deck-editor#moveLayerDown";
      down.disabled = index <= 0;

      row.appendChild(face);
      row.appendChild(up);
      row.appendChild(down);
      list.appendChild(row);
    }
    return list;
  }

  // Layer-list ▲: move the row's element one step toward the FRONT (higher index).
  moveLayerUp(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.swapElements(index, index + 1);
  }

  // Layer-list ▼: move the row's element one step toward the BACK (lower index).
  moveLayerDown(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.swapElements(index, index - 1);
  }

  // Swap two elements by array index, keeping selectedElement pointed at whichever
  // moved element it was tracking, then mark dirty + redraw. Guards out-of-range so
  // boundary clicks are safe even if a disabled button is somehow triggered.
  swapElements(a, b) {
    const slide = this.currentSlide();
    if (!slide || !Array.isArray(slide.elements)) return;
    const elements = slide.elements;
    if (a < 0 || b < 0 || a >= elements.length || b >= elements.length) return;

    [elements[a], elements[b]] = [elements[b], elements[a]];
    if (this.selectedElement === a) this.selectedElement = b;
    else if (this.selectedElement === b) this.selectedElement = a;
    // Keep the multi-selection invariant: this single-element layering op collapses
    // any stale set to the primary (or empty when nothing is selected).
    this.selectionIndices = this.selectedElement === null ? [] : [this.selectedElement];

    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  // A short, human-readable label for a layer row: the element's content, else its
  // slot name, else just the kind — whichever first carries meaning.
  layerLabel(element) {
    if (element.content && String(element.content).trim() !== "") return String(element.content);
    if (element.slot) return `{${element.slot}}`;
    return element.kind;
  }

  // Select the element a layer row points at (its real array index).
  selectElementFromLayer(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.selectElement(index);
  }

  // Bindings panel (right rail): slot name, data-source binding, and resource
  // link for the selected element. Split out of the Properties panel so styling
  // and data-binding live on separate tabs. Refreshed by renderProperties().
  renderBindings() {
    if (!this.hasBindingsTarget) return;
    this.bindingsTarget.replaceChildren();

    const slide = this.currentSlide();
    if (!this.editableSlide(slide) || this.selectedElement === null ||
        !slide.elements || !slide.elements[this.selectedElement]) {
      // No element selected: surface the deck's known data fields (slots) so the
      // author can discover them and drop one inline as a {slot} token (ADR-0080,
      // #2058), then the original "select an element" hint below.
      this.bindingsTarget.appendChild(this.dataFieldsSection());
      this.bindingsTarget.appendChild(this.hint("Select an element to bind it to live data."));
      this.slotInputEl = null;
      this.bindingPickerEl = null;
      return;
    }
    const element = slide.elements[this.selectedElement];

    // Slot + data-source binding — bindable kinds only.
    if (this.isBindableKind(element.kind)) {
      // Slot field — an EDITABLE OVERRIDE for the binding key (card #1916 follow-on,
      // ADR-0070 §1). The author no longer has to invent a slot name before binding:
      // selecting an objective in the picker below auto-names the slot. This field
      // lets the author rename that key, and a rename MIGRATES the binding so it
      // follows the new name rather than orphaning under the old key.
      //
      // Wired for SURGICAL update: re-rendering the whole panel on a keystroke
      // destroys the <input> and steals focus, so the slot handler NEVER calls
      // renderProperties(). The slot <input> stays in the DOM the whole time; the
      // binding picker is rendered once below and refreshed in place when needed.
      // We keep `this.slotInputEl` (the live <input>) so the picker's auto-name path
      // can write back into it without a re-render, preserving focus/cursor.
      const slotField = this.textField("Slot (binding name)", element.slot || "", (v) => {
        const oldSlot = element.slot;
        const newSlot = v.trim() === "" ? null : v.trim();
        element.slot = newSlot;

        // Migrate the binding so it follows the rename. If a binding lived under the
        // old key and the author has typed a new non-empty name, move it across so
        // the slot keeps its data instead of orphaning the binding (ADR-0070 §1).
        if (oldSlot && newSlot && oldSlot !== newSlot && this.deck.bindings && this.deck.bindings[oldSlot]) {
          this.deck.bindings[newSlot] = this.deck.bindings[oldSlot];
          delete this.deck.bindings[oldSlot];
        } else if (oldSlot && !newSlot && this.deck.bindings && this.deck.bindings[oldSlot]) {
          // Slot cleared entirely while bound → drop the now-keyless binding.
          delete this.deck.bindings[oldSlot];
        }

        this.markDirty();
        this.renderCanvas();
      });
      this.slotInputEl = slotField.querySelector("input");
      this.bindingsTarget.appendChild(slotField);

      // Slot → source binding picker (card #1826, ADR-0070 §1). Bindable element
      // kinds (headline, text, image — NOT shape) always show the picker, whether or
      // not a slot name exists yet: selecting an objective IS the act that creates
      // the slot. The picker reads/sets element.slot directly, writes the deck-level
      // `bindings` entry, and Pull resolves it.
      this.bindingPickerEl = this.renderBindingPicker(element);
      this.bindingsTarget.appendChild(this.bindingPickerEl);
    } else {
      this.slotInputEl = null;
      this.bindingPickerEl = null;
    }

    // Resource link (ADR-0070 §3): a navigation affordance on ANY element kind. A
    // link wraps the rendered element in <a href> at render time; it does NOT bind a
    // value (that is the picker above) and never touches result_json. The href is
    // resolved SERVER-SIDE (Deck#resolve_element_link) — the editor only stores
    // options.link and shows a badge, so there is no JS href resolution to keep in
    // parity with the partial.
    this.bindingsTarget.appendChild(this.renderLinkSection(element));
  }

  // "Data fields" discoverability section (ADR-0080, #2058). Lists the deck's known
  // binding slots as literal-token chips so an author can find a slot and drop its
  // live value inline by typing "{slot}" into any text box (mail-merge model).
  // Clicking a chip copies the token to the clipboard. Rendering of tokens is
  // server-side only (Deck#resolve_inline_tokens) — the editor canvas shows the
  // literal {slot} text, so there is nothing here to keep in parity with the render.
  dataFieldsSection() {
    const wrap = document.createElement("div");
    wrap.className = "flex flex-col gap-2";

    wrap.appendChild(this.sectionLabel("Data fields"));

    const slots = Object.keys(this.deck.bindings || {});
    if (!slots.length) {
      wrap.appendChild(this.hint("No data fields yet. Bind an element to a data source to create one."));
      return wrap;
    }

    wrap.appendChild(this.hint("Type a token like {slot} into any text box to drop its live value inline. Pull data to see resolved values."));

    // One row per slot: the click-to-copy {slot} chip, plus a muted "= value" when
    // the slot has a resolved value (card #2061). With no pulled value yet, the chip
    // shows alone. The chip keeps its copy behaviour; the value text is display-only.
    const list = document.createElement("div");
    list.className = "flex flex-col gap-1.5";
    slots.forEach((slot) => {
      const token = `{${slot}}`;
      const row = document.createElement("div");
      row.className = "flex items-center gap-1.5";

      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200";
      chip.textContent = token;
      chip.title = "Copy token to clipboard";
      chip.addEventListener("click", () => {
        navigator.clipboard.writeText(token);
        const original = chip.textContent;
        chip.textContent = "Copied";
        setTimeout(() => { chip.textContent = original; }, 1000);
      });
      row.appendChild(chip);

      if (slot in this.result) {
        const value = this.formatDisplay(this.scalarFrom(this.result[slot]));
        if (value !== null && value !== undefined) {
          const valueEl = document.createElement("span");
          valueEl.className = "text-xs text-gray-500";
          valueEl.textContent = `= ${value}`;
          row.appendChild(valueEl);
        }
      }

      list.appendChild(row);
    });
    wrap.appendChild(list);

    return wrap;
  }

  renderElementProperties(element) {
    element.options = element.options || {};

    // "← Back to slide" — the explicit deselect affordance (card #1916). Clears
    // the element selection so the panel falls back to slide properties; the same
    // path as clicking empty canvas or pressing Escape.
    const back = document.createElement("button");
    back.type = "button";
    back.className = "self-start text-xs font-medium text-gray-500 hover:text-gray-800";
    back.textContent = "← Back to slide";
    back.dataset.action = "click->deck-editor#deselectElement";
    this.propertiesTarget.appendChild(back);

    this.propertiesTarget.appendChild(this.sectionLabel(`Element — ${element.kind}`));

    // Content + slot (binding name only; live data picker is Slice 2').
    // Text elements get a Lexxy rich text editor (card #2043); every other kind
    // (image/html/analytics) keeps the plain textarea because its content is a
    // URL or plain string, not rich markup. Headline stays plain too: rich
    // formatting would conflict with its size/weight/align controls.
    if (element.kind === "text") {
      this.propertiesTarget.appendChild(
        this.richTextField("Content", element.content || "", (v) => {
          element.content = v;
          this.markDirty();
          this.renderCanvas();
        })
      );
    } else if (!["shape", "clock", "janela_frame"].includes(element.kind)) {
      this.propertiesTarget.appendChild(
        this.textareaField("Content", element.content || "", (v) => {
          element.content = v;
          this.markDirty();
          this.renderCanvas();
        })
      );
    }

    // Text style — typography controls live HERE, right under the Content field,
    // so Align/Colour/Size/Weight sit beside the text the author is writing rather
    // than buried at the bottom under Position & size → Style.
    if (element.kind === "headline" || element.kind === "text") {
      this.propertiesTarget.appendChild(this.sectionLabel("Text style"));
      this.propertiesTarget.appendChild(this.selectField("Align", element.options.align || "left", ["left", "center", "right"], (v) => {
        element.options.align = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.colorField("Colour", element.options.color || "", (v) => {
        element.options.color = v.trim() === "" ? undefined : v.trim(); this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.numberField("Size (px)", this.int(element.options.size) || (element.kind === "headline" ? 48 : 24), (v) => {
        element.options.size = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.selectField("Weight", String(element.options.weight || (element.kind === "headline" ? 800 : 400)), ["300", "400", "600", "700", "800"], (v) => {
        element.options.weight = parseInt(v, 10); this.markDirty(); this.renderCanvas();
      }));
      // Per-element font override (card #2081). A closed list from the SAME curated
      // registry the deck-wide picker uses (#1825) — the KEY is stored on element.font
      // (never a raw stack). A leading "Default (deck font)" option (empty key) CLEARS
      // the override so the element inherits the deck-wide font. Shown for text AND
      // headline; stored on the element, not options, mirroring Deck.element_font_family.
      const fontKeys = Object.keys(this.fontsValue || {});
      if (fontKeys.length) {
        const optionKeys = [""].concat(fontKeys);
        const optionLabels = ["Default (deck font)"].concat(
          fontKeys.map((key) => this.fontsValue[key].label || key)
        );
        this.propertiesTarget.appendChild(
          this.selectFieldKeyed("Font", element.font || "", optionKeys, optionLabels, (key) => {
            if (key === "") delete element.font; else element.font = key;
            this.markDirty(); this.renderCanvas();
          })
        );
      }
    }

    // Geometry.
    this.propertiesTarget.appendChild(this.sectionLabel("Position & size"));
    const geo = document.createElement("div");
    geo.className = "grid grid-cols-2 gap-2";
    ["x", "y", "w", "h"].forEach((key) => {
      geo.appendChild(this.numberField(key.toUpperCase(), this.int(element[key]), (v) => {
        element[key] = this.clamp(v, 0, key === "x" || key === "w" ? this.canvasW : this.canvasH);
        this.markDirty();
        this.renderCanvas();
      }));
    });
    this.propertiesTarget.appendChild(geo);

    // Per-kind options.
    this.propertiesTarget.appendChild(this.sectionLabel("Style"));
    // Opacity applies to every kind (card #1916). 0–100; clamped so a stray value
    // can never leave the valid range. Stored on options.opacity.
    this.propertiesTarget.appendChild(
      this.numberField("Opacity (0–100)", this.int(element.options.opacity ?? 100), (v) => {
        element.options.opacity = this.clamp(v, 0, 100);
        this.markDirty();
        this.renderCanvas();
      })
    );
    if (element.kind === "headline" || element.kind === "text") {
      // Visual axis (ADR-0070 §2/D5): how a value DRAWS, independent of what it
      // binds. `text` = the value inline (default); `stat` = a stat card (big value
      // + label). Charts are a later slice. Shown for every text/headline element —
      // an objective binding is what makes `stat` meaningful, but the option is the
      // visualisation axis, not gated on the binding being present yet.
      this.propertiesTarget.appendChild(this.selectField("Visual", element.options.visual || "text", ["text", "stat"], (v) => {
        if (v === "text") delete element.options.visual; else element.options.visual = v;
        this.markDirty(); this.renderCanvas();
      }));
      // Hide label (card #2157 part 1): only meaningful on the stat visual — a
      // bound objective's name is often internal/verbose framing text not meant
      // for client display. PARITY: statNode / the published partial's stat
      // branch both skip the label row entirely when this is set.
      if (this.isStatVisual(element)) {
        this.propertiesTarget.appendChild(
          this.checkboxField("Hide label", element.options.hide_label === true, (checked) => {
            if (checked) element.options.hide_label = true; else delete element.options.hide_label;
            this.markDirty(); this.renderCanvas();
          })
        );
      }
    } else if (element.kind === "image") {
      this.propertiesTarget.appendChild(this.selectField("Fit", element.options.fit || "cover", ["cover", "contain", "fill"], (v) => {
        element.options.fit = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.textField("Alt text", element.options.alt || "", (v) => {
        element.options.alt = v; this.markDirty(); this.renderCanvas();
      }));
      // Pick from the client's existing imagery (card #1824). The gallery fills
      // the selected image element's content with the chosen asset URL.
      const pickAsset = document.createElement("button");
      pickAsset.type = "button";
      pickAsset.className = "mt-2 rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100";
      pickAsset.textContent = "Pick client asset";
      pickAsset.dataset.action = "click->deck-editor#openAssetPicker";
      this.propertiesTarget.appendChild(pickAsset);
      // Crop / reframe (card #2130). Enters non-destructive crop mode on the selected
      // image element — the same entry as the right-click "Crop" item. Only meaningful
      // once the element has a source; the button is always shown (crop mode no-ops
      // without a src) so the control is discoverable in the panel too.
      const cropBtn = document.createElement("button");
      cropBtn.type = "button";
      cropBtn.className = "mt-2 ml-2 rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100";
      cropBtn.textContent = "Crop image";
      cropBtn.dataset.action = "click->deck-editor#enterCropMode";
      this.propertiesTarget.appendChild(cropBtn);
      // Reset crop appears only when a crop is applied, so the author can clear the
      // pan/zoom back to the plain cover render without re-cropping to identity.
      if (this.cropTransform(element.crop)) {
        const resetBtn = document.createElement("button");
        resetBtn.type = "button";
        resetBtn.className = "mt-2 ml-2 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200";
        resetBtn.textContent = "Reset crop";
        resetBtn.dataset.action = "click->deck-editor#resetCrop";
        this.propertiesTarget.appendChild(resetBtn);
      }
      // Radius / border / shadow — the shared "make it pop" controls (card #2135).
      this.appendAppearanceControls(element);
    } else if (element.kind === "shape") {
      this.propertiesTarget.appendChild(this.colorField("Fill", element.options.fill || "var(--color-canary)", (v) => {
        element.options.fill = v; this.markDirty(); this.renderCanvas();
      }));
      // Radius / border / shadow — the shared "make it pop" controls (card #2135),
      // the same set an image gets, so a shape and an image style identically.
      this.appendAppearanceControls(element);
    } else if (element.kind === "html") {
      this.propertiesTarget.appendChild(this.textareaField("HTML", element.options.html || "", (v) => {
        element.options.html = v;
        this.markDirty();
        this.renderCanvas();
      }));
      const hint = document.createElement("p");
      hint.className = "mt-1 text-xs text-gray-400";
      hint.textContent = "Scripts run in the published slide. Chart.js CDN is blocked by CSP — use /assets/chart.min.js or inline the bundle.";
      this.propertiesTarget.appendChild(hint);
    } else if (element.kind === "analytics") {
      const analyticsKeys   = ["/analytics/main", "/analytics/one", "/analytics/map_simple", "/analytics/capcom", "/analytics/cycle_overview", "/analytics/client_objectives", "/analytics/mobile", "/analytics/stock_event_queue"];
      const analyticsLabels = ["Main dashboard", "Live Map", "Simple Map", "CAPCOM", "Cycle Overview", "Client Objectives", "Mobile", "Stock Event Queue"];
      this.propertiesTarget.appendChild(
        this.selectFieldKeyed("Analytics page", element.options.url || "/analytics/main", analyticsKeys, analyticsLabels, (v) => {
          element.options.url = v;
          this.markDirty();
          this.renderCanvas();
        })
      );
    } else if (element.kind === "janela_frame") {
      this.renderLiveFrameProperties(element);
    } else if (element.kind === "clock") {
      this.renderClockProperties(element);
    }

    // Layer (z-order) controls (card #1916). Four buttons reordering this element
    // within slide.elements; boundary buttons disable when the move is a no-op.
    this.propertiesTarget.appendChild(this.sectionLabel("Layer"));
    this.propertiesTarget.appendChild(this.renderLayerButtons());

    // Delete.
    const del = document.createElement("button");
    del.type = "button";
    del.className = "mt-2 rounded bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100";
    del.textContent = "Delete element";
    del.dataset.action = "click->deck-editor#deleteElement";
    this.propertiesTarget.appendChild(del);
  }

  // Live frame options (card #4008): how the embedded report is dressed, never
  // what it reads. Written as flat keys on element.options, validated again by
  // Insights::EmbedStyle on the server. A blank value deletes the key.
  renderLiveFrameProperties(element) {
    const options = element.options;
    const setOption = (key, value) => {
      if (value === "" || value == null) delete options[key]; else options[key] = value;
      this.markDirty();
      this.renderCanvas();
    };
    const entry = this.liveFramesValue.find((frame) => frame.key === options.frame);
    this.propertiesTarget.appendChild(this.sectionLabel("Live frame"));
    this.propertiesTarget.appendChild(
      this.selectFieldKeyed("Theme", options.theme || "light", ["light", "dark"],
        ["Light (dark text)", "Dark (light text, for a navy band)"], (v) => setOption("theme", v))
    );
    this.propertiesTarget.appendChild(
      this.colorField("Background (blank = transparent)", options.background || "", (v) => setOption("background", v))
    );
    if (entry && entry.tile) {
      this.propertiesTarget.appendChild(this.textField("Label (blank = report name)", options.label ?? "", (v) => {
        if (v === "") delete options.label; else options.label = v;
        this.markDirty();
        this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.colorField("Number colour", options.color || "", (v) => setOption("color", v)));
      this.propertiesTarget.appendChild(this.colorField("Label colour", options.label_color || "", (v) => setOption("label_color", v)));
    } else {
      this.propertiesTarget.appendChild(this.colorField("Bar colour", options.accent || "", (v) => setOption("accent", v)));
    }
  }

  // Clock options (card #4008). Time zone is the Beacon's unless set here.
  renderClockProperties(element) {
    const options = element.options;
    this.propertiesTarget.appendChild(this.sectionLabel("Clock"));
    this.propertiesTarget.appendChild(this.selectField("Align", options.align || "right", ["left", "center", "right"], (v) => {
      options.align = v; this.markDirty(); this.renderCanvas();
    }));
    this.propertiesTarget.appendChild(this.numberField("Size (px)", this.int(options.size) || 28, (v) => {
      options.size = this.clamp(v, 8, 400); this.markDirty(); this.renderCanvas();
    }));
    this.propertiesTarget.appendChild(this.colorField("Colour", options.color || "", (v) => {
      if (v === "") delete options.color; else options.color = v;
      this.markDirty(); this.renderCanvas();
    }));
    this.propertiesTarget.appendChild(this.checkboxField("Show date", options.show_date !== false, (checked) => {
      options.show_date = checked; this.markDirty(); this.renderCanvas();
    }));
    this.propertiesTarget.appendChild(this.checkboxField("Show seconds", options.show_seconds !== false, (checked) => {
      options.show_seconds = checked; this.markDirty(); this.renderCanvas();
    }));
    this.propertiesTarget.appendChild(this.textField("Time zone (blank = the Beacon's)", options.time_zone || "", (v) => {
      if (v === "") delete options.time_zone; else options.time_zone = v;
      this.markDirty(); this.renderCanvas();
    }));
  }

  // Shared "make it pop" appearance controls (card #2135) — radius, border, and
  // shadow — appended for BOTH image and shape elements so the two style identically.
  // Everything writes flat keys on element.options (the existing convention: radius,
  // border_width/style/color, shadow + shadow_* for custom), keeping backward-compat
  // with pre-#2135 decks and the crop clip-wrapper, which read the same keys. Each
  // control routes through markDirty + renderCanvas, so the canvas repaints live.
  appendAppearanceControls(element) {
    const options = element.options;

    // --- Corners -----------------------------------------------------------
    this.propertiesTarget.appendChild(this.sectionLabel("Corners"));
    const radiusRow = document.createElement("div");
    radiusRow.className = "flex items-end gap-2";
    const radiusField = this.numberField("Radius (px)", this.int(options.radius), (v) => {
      options.radius = this.clamp(v, 0, 9999); this.markDirty(); this.renderCanvas();
    });
    radiusField.classList.add("grow");
    radiusRow.appendChild(radiusField);
    // Pill / circle: max the radius so a box becomes a pill (or a square becomes a
    // circle). 9999px is the sanctioned "fully rounded" value (same as the text pill).
    const pillBtn = document.createElement("button");
    pillBtn.type = "button";
    pillBtn.className = "mb-0.5 shrink-0 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200";
    pillBtn.textContent = "Pill / circle";
    pillBtn.title = "Fully round the corners (9999px)";
    pillBtn.addEventListener("click", () => {
      options.radius = 9999; this.markDirty(); this.renderCanvas(); this.renderProperties();
    });
    radiusRow.appendChild(pillBtn);
    this.propertiesTarget.appendChild(radiusRow);

    // --- Border ------------------------------------------------------------
    this.propertiesTarget.appendChild(this.sectionLabel("Border"));
    // 0 px = no border; every render path emits the border only when width > 0.
    this.propertiesTarget.appendChild(this.numberField("Width (px)", this.int(options.border_width), (v) => {
      options.border_width = this.clamp(v, 0, 200); this.markDirty(); this.renderCanvas(); this.renderProperties();
    }));
    // Style + colour only make sense once there's a width — hide them at 0 to keep
    // the panel lean, and re-render on width change (above) reveals/hides them.
    if (this.int(options.border_width) > 0) {
      this.propertiesTarget.appendChild(this.selectField("Style", options.border_style || "solid", ["solid", "dashed", "dotted"], (v) => {
        options.border_style = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.colorField("Colour", options.border_color || "#000000", (v) => {
        options.border_color = v; this.markDirty(); this.renderCanvas();
      }));
    }

    // --- Shadow ------------------------------------------------------------
    this.propertiesTarget.appendChild(this.sectionLabel("Shadow"));
    // Preset select drives the common case (none / soft / lifted / hard); "custom"
    // reveals the fine controls. A legacy boolean `shadow: true` reads as "soft" so
    // the select lands on the right option for an old deck.
    const shadowValue =
      options.shadow === true ? "soft"
      : (options.shadow || "none");
    this.propertiesTarget.appendChild(
      this.selectFieldKeyed(
        "Preset",
        shadowValue,
        ["none", "soft", "lifted", "hard", "custom"],
        ["None", "Soft", "Lifted", "Hard", "Custom…"],
        (v) => {
          if (v === "none") delete options.shadow; else options.shadow = v;
          this.markDirty(); this.renderCanvas(); this.renderProperties();
        }
      )
    );
    if (options.shadow === "custom") {
      const grid = document.createElement("div");
      grid.className = "grid grid-cols-2 gap-2";
      grid.appendChild(this.numberField("Offset X (px)", this.int(options.shadow_x), (v) => {
        options.shadow_x = v; this.markDirty(); this.renderCanvas();
      }));
      grid.appendChild(this.numberField("Offset Y (px)", this.int(options.shadow_y), (v) => {
        options.shadow_y = v; this.markDirty(); this.renderCanvas();
      }));
      grid.appendChild(this.numberField("Blur (px)", this.int(options.shadow_blur), (v) => {
        options.shadow_blur = this.clamp(v, 0, 200); this.markDirty(); this.renderCanvas();
      }));
      grid.appendChild(this.numberField("Spread (px)", this.int(options.shadow_spread), (v) => {
        options.shadow_spread = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(grid);
      this.propertiesTarget.appendChild(this.colorField("Colour", options.shadow_color || "#000000", (v) => {
        options.shadow_color = v; this.markDirty(); this.renderCanvas();
      }));
      this.propertiesTarget.appendChild(this.numberField("Opacity (0–100)", options.shadow_opacity == null ? 100 : this.int(options.shadow_opacity), (v) => {
        options.shadow_opacity = this.clamp(v, 0, 100); this.markDirty(); this.renderCanvas();
      }));
    }
  }

  // The four z-order buttons for the selected element. Top/bottom buttons disable
  // at the front/back boundary; forward/backward disable one step in.
  renderLayerButtons() {
    const slide = this.currentSlide();
    const count = slide && Array.isArray(slide.elements) ? slide.elements.length : 0;
    const index = this.selectedElement;
    const atFront = index >= count - 1;
    const atBack = index <= 0;

    const row = document.createElement("div");
    row.className = "grid grid-cols-2 gap-2";
    [
      ["Bring to front", "bringToFront", atFront],
      ["Bring forward", "bringForward", atFront],
      ["Send backward", "sendBackward", atBack],
      ["Send to back", "sendToBack", atBack]
    ].forEach(([label, method, disabled]) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40";
      btn.textContent = label;
      btn.disabled = disabled;
      btn.dataset.action = `click->deck-editor#${method}`;
      row.appendChild(btn);
    });
    return row;
  }

  // ---- Slide kinds: save / insert (card #1823) ---------------------------

  // Save the selected slide as a reusable kind. Opens the kindSaveModal so the
  // author can provide a name and optional description (card #2008 — replaces
  // window.prompt / window.confirm). The submit path is submitKindSave().
  saveSlideAsKind() {
    const slide = this.currentSlide();
    if (!slide) return;

    if (!this.hasKindSaveModalTarget) return;

    // Stash the slide and trigger button for use in submitKindSave / conflict
    // handlers (they can't re-derive these from context).
    this._kindSaveSlide  = slide;
    this._kindSaveButton = this.lastSaveKindButton;
    this._kindSaveConflictData = null;

    // Pre-fill name from slide.name, clear description, hide conflict notice.
    this.kindSaveModalNameTarget.value = (slide.name && slide.name.trim()) || "";
    this.kindSaveModalDescriptionTarget.value = "";
    this.kindSaveModalConflictTarget.classList.add("hidden");
    if (this.hasKindSaveModalSubmitTarget) this.kindSaveModalSubmitTarget.classList.remove("hidden");
    if (this.hasKindSaveModalActionsTarget) this.kindSaveModalActionsTarget.classList.remove("hidden");

    // Open modal, bind Escape.
    this.kindSaveModalTarget.classList.remove("hidden");
    this.kindSaveModalTarget.classList.add("flex");
    this.boundKindSaveKeydown = (event) => {
      if (event.key === "Escape") this.cancelKindSave();
    };
    document.addEventListener("keydown", this.boundKindSaveKeydown);

    // Focus name field (give browser a tick to paint first).
    requestAnimationFrame(() => this.kindSaveModalNameTarget.focus());
  }

  // Close the kindSaveModal without saving.
  cancelKindSave() {
    if (!this.hasKindSaveModalTarget) return;
    this.kindSaveModalTarget.classList.add("hidden");
    this.kindSaveModalTarget.classList.remove("flex");
    if (this.boundKindSaveKeydown) {
      document.removeEventListener("keydown", this.boundKindSaveKeydown);
      this.boundKindSaveKeydown = null;
    }
  }

  // Backdrop click closes the modal; only act when the overlay itself was the
  // click target (same pattern as kindModalBackdrop).
  kindSaveModalBackdrop(event) {
    if (event.target === this.kindSaveModalTarget) this.cancelKindSave();
  }

  // Execute the save after the author fills in name + description.
  // Wired to the "Save as Kind" submit button in the modal.
  submitKindSave() {
    const name = this.kindSaveModalNameTarget.value.trim();
    if (!name) {
      this.kindSaveModalNameTarget.focus();
      return;
    }
    const description = this.kindSaveModalDescriptionTarget.value.trim() || null;
    const slide = this._kindSaveSlide;
    const button = this._kindSaveButton;

    // Persist name onto slide so the deck remembers it.
    if (slide && slide.name !== name) {
      slide.name = name;
      this.markDirty();
      this.renderSlideList();
    }

    this.cancelKindSave();
    if (button) { button.disabled = true; button.textContent = "Saving…"; }

    const payload = { slide, name };
    if (description) payload.description = description;

    this._doKindSavePost(payload, button);
  }

  // Re-send with update_deck_id — wired to "Update existing" in conflict notice.
  kindSaveUpdateExisting() {
    if (!this._kindSaveConflictData) return;
    const name        = this.kindSaveModalNameTarget.value.trim();
    const description = this.kindSaveModalDescriptionTarget.value.trim() || null;
    const slide       = this._kindSaveSlide;
    const button      = this._kindSaveButton;

    this.cancelKindSave();
    if (button) { button.disabled = true; button.textContent = "Updating…"; }

    const payload = { slide, name, update_deck_id: this._kindSaveConflictData.id };
    if (description) payload.description = description;
    this._doKindSavePost(payload, button);
  }

  // Re-send with force_new suffix — wired to "Save as new" in conflict notice.
  kindSaveAsNew() {
    const baseName    = this.kindSaveModalNameTarget.value.trim();
    const description = this.kindSaveModalDescriptionTarget.value.trim() || null;
    const slide       = this._kindSaveSlide;
    const button      = this._kindSaveButton;
    const newName     = `${baseName} 2`;

    this.cancelKindSave();
    if (button) { button.disabled = true; button.textContent = "Saving…"; }

    const payload = { slide, name: newName };
    if (description) payload.description = description;
    this._doKindSavePost(payload, button);
  }

  // Shared POST helper used by submit / update-existing / save-as-new.
  // On conflict, re-opens the modal with the conflict notice visible.
  _doKindSavePost(payload, button) {
    this.postJson(this.saveKindUrlValue, payload)
      .then(async (response) => {
        const data = await response.json();

        if (data.status === "conflict") {
          // Store conflict data for the resolution handlers.
          this._kindSaveConflictData = data.existing;

          // Re-open modal showing conflict notice, hiding the normal submit.
          this.kindSaveModalConflictTarget.classList.remove("hidden");
          if (this.hasKindSaveModalSubmitTarget) this.kindSaveModalSubmitTarget.classList.add("hidden");
          this.kindSaveModalTarget.classList.remove("hidden");
          this.kindSaveModalTarget.classList.add("flex");
          this.boundKindSaveKeydown = (event) => {
            if (event.key === "Escape") this.cancelKindSave();
          };
          document.addEventListener("keydown", this.boundKindSaveKeydown);

          if (button) { button.disabled = false; button.textContent = "Save slide as kind"; }
          return;
        }

        if (button) {
          button.disabled = false;
          button.textContent = (data.status === "success" || data.status === "updated") ? "Saved as kind" : "Save failed";
          setTimeout(() => { if (button) button.textContent = "Save slide as kind"; }, 1500);
        }
      })
      .catch(() => {
        if (button) { button.disabled = false; button.textContent = "Save failed"; }
      });
  }

  // Open the kind picker and load the library (slides across template decks).
  // WIN modals also close on Escape and backdrop click, so bind a keydown
  // listener while the picker is open and tear it down on close (mirrors the
  // boundResize add/remove idiom in connect/disconnect).
  openKindPicker() {
    if (!this.hasKindModalTarget) return;
    this.kindModalTarget.classList.remove("hidden");
    this.kindModalTarget.classList.add("flex");
    if (this._applyTemplateTarget != null) {
      const slide = this.deck.slides[this._applyTemplateTarget];
      const label = slide ? this.slideLabel(slide) : "slide";
      if (this.hasKindModalTitleTarget) {
        this.kindModalTitleTarget.textContent = "Apply template to slide";
      }
      if (this.hasKindApplyBannerTarget) {
        this.kindApplyBannerTarget.textContent = `Applying to slide ${this._applyTemplateTarget + 1}: ${label}. Picking a template replaces this slide's layout.`;
        this.kindApplyBannerTarget.classList.remove("hidden");
      }
    } else {
      if (this.hasKindModalTitleTarget) {
        this.kindModalTitleTarget.textContent = "Insert a slide kind";
      }
      if (this.hasKindApplyBannerTarget) {
        this.kindApplyBannerTarget.classList.add("hidden");
        this.kindApplyBannerTarget.textContent = "";
      }
    }
    this.boundKindKeydown = (event) => {
      if (event.key === "Escape") this.closeKindPicker();
    };
    document.addEventListener("keydown", this.boundKindKeydown);
    this.loadKinds();
  }

  closeKindPicker() {
    if (!this.hasKindModalTarget) return;
    // Always clear apply mode so a cancelled/finished apply never leaks into the
    // next plain "Insert kind" (which would then wrongly swap in place).
    this._applyTemplateTarget = null;
    this.kindModalTarget.classList.add("hidden");
    this.kindModalTarget.classList.remove("flex");
    // Defensively reset the header so a stale apply banner never lingers.
    if (this.hasKindModalTitleTarget) {
      this.kindModalTitleTarget.textContent = "Insert a slide kind";
    }
    if (this.hasKindApplyBannerTarget) {
      this.kindApplyBannerTarget.classList.add("hidden");
    }
    if (this.boundKindKeydown) {
      document.removeEventListener("keydown", this.boundKindKeydown);
      this.boundKindKeydown = null;
    }
  }

  // Backdrop click closes the picker; clicks inside the panel bubble up to the
  // overlay too, so only act when the overlay itself was the click target.
  kindModalBackdrop(event) {
    if (event.target === this.kindModalTarget) this.closeKindPicker();
  }

  loadKinds() {
    if (!this.hasKindListTarget) return;
    this.kindListTarget.replaceChildren();

    fetch(this.kindsUrlValue, { headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => this.renderKinds(data.kinds || []))
      .catch(() => this.renderKinds([]));
  }

  renderKinds(kinds) {
    // Keep the master list; insertKind looks library slides up by their ORIGINAL
    // index here, so grouped/filtered rendering must stamp that index on cards.
    this.kinds = kinds;

    if (this.hasKindEmptyTarget) {
      this.kindEmptyTarget.classList.toggle("hidden", kinds.length > 0);
    }

    this._populateKindFilter(kinds);
    this._paintKinds("");
  }

  // Populate the picker's filter dropdown with the slide kinds actually present
  // in the library (ADR-0078). Uses the closed-vocabulary labels when known,
  // falling back to "Uncategorised" for unclassified slides. Reset to "All
  // kinds" each time the library loads.
  _populateKindFilter(kinds) {
    if (!this.hasKindFilterTarget) return;
    const select = this.kindFilterTarget;
    const present = new Set(kinds.map((k) => k.kind || ""));
    select.replaceChildren();

    const all = document.createElement("option");
    all.value = "";
    all.textContent = "All kinds";
    select.appendChild(all);

    // Keep the vocabulary's canonical order; append "Uncategorised" last when
    // any unclassified slide exists.
    Object.keys(this.slideKindsValue).forEach((key) => {
      if (!present.has(key)) return;
      const o = document.createElement("option");
      o.value = key;
      o.textContent = this.slideKindsValue[key];
      select.appendChild(o);
    });
    if (present.has("")) {
      const o = document.createElement("option");
      o.value = "__uncategorised__";
      o.textContent = "Uncategorised";
      select.appendChild(o);
    }
  }

  // The picker filter changed — repaint to the chosen kind (or all).
  filterKinds(event) {
    this._paintKinds(event.target.value);
  }

  // Render the library cards, optionally filtered to one kind, grouped by kind
  // with a header per group (ADR-0078). `activeKind` is a vocabulary key, the
  // sentinel "__uncategorised__" for unclassified slides, or "" for all kinds.
  _paintKinds(activeKind) {
    if (!this.hasKindListTarget) return;
    this.kindListTarget.replaceChildren();

    // Bucket entries by kind key ("" = uncategorised), preserving each entry's
    // ORIGINAL index so insertKind resolves the right library slide.
    const buckets = new Map();
    this.kinds.forEach((kind, index) => {
      const key = kind.kind || "";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({ kind, index });
    });

    // Group order: the closed vocabulary first (canonical), uncategorised last.
    const order = Object.keys(this.slideKindsValue).concat([""]);
    const wanted = activeKind === "__uncategorised__" ? "" : activeKind;

    order.forEach((key) => {
      if (wanted !== "" && key !== wanted) return;
      const entries = buckets.get(key);
      if (!entries || !entries.length) return;

      // Group header spans the full grid row. Omit it when a single kind is
      // filtered (the dropdown already names it) — keeps the grid clean.
      if (activeKind === "") {
        const header = document.createElement("h3");
        header.className = "col-span-full mt-1 text-2xs font-semibold uppercase tracking-wide text-gray-400";
        header.textContent = key === "" ? "Uncategorised" : (this.slideKindsValue[key] || key);
        this.kindListTarget.appendChild(header);
      }

      entries.forEach(({ kind, index }) => {
        this.kindListTarget.appendChild(this._kindCard(kind, index));
      });
    });
  }

  // Build one library card for the picker, stamped with the entry's original
  // index in this.kinds (insertKind reads it back).
  _kindCard(kind, index) {
    const frag = this.kindCardTemplateTarget.content.cloneNode(true);
    const card = frag.querySelector("[data-deck-editor-target='kindCard']");
    card.dataset.index = index;

    // Live thumbnail: the same slide node the Preview panel renders, dropped
    // into the .airdrop-slide-scale frame so the container-query CSS scales a
    // full 1280x720 slide down to the card width. Bound slots show `{slot}`
    // placeholders — kinds are valueless, that's expected.
    const thumb = card.querySelector("[data-role='thumb']");
    if (thumb && kind.slide) thumb.appendChild(this.previewSlideNode(kind.slide));

    card.querySelector("[data-role='label']").textContent = kind.label;

    // Source template name as a subtitle only when it adds information.
    const subtitle = card.querySelector("[data-role='subtitle']");
    if (subtitle && kind.report_name && kind.report_name !== kind.label) {
      subtitle.textContent = kind.report_name;
      subtitle.classList.remove("hidden");
    }
    return frag;
  }

  // Insert the picked kind's slide fragment into the working deck. The fragment
  // is a deep copy so the inserted slide and the library kind never share state.
  // Provenance (template_source) is stamped onto the slide so drift detection
  // can compare against the library version at any future point (card #2007).
  insertKind(event) {
    const cardIndex = parseInt(event.currentTarget.dataset.index, 10);

    // Apply mode (set by openKindPickerForApply): swap the target slide's layout
    // in place rather than pushing a new slide. applyTemplateToSlide closes the
    // picker, which clears _applyTemplateTarget so the next plain insert is normal.
    if (this._applyTemplateTarget != null) {
      this.applyTemplateToSlide(this._applyTemplateTarget, cardIndex);
      return;
    }

    const kind = this.kinds && this.kinds[cardIndex];
    if (!kind || !kind.slide) return;

    const slide = JSON.parse(JSON.stringify(kind.slide));
    // A library kind carries the SOURCE deck's slide id (or none). This is a NEW
    // slide in THIS deck, so mint a fresh stable id (card #2120).
    slide.id = crypto.randomUUID();
    slide.template_source = {
      deck_id: kind.report_id,
      slide_index: kind.slide_index,
      digest: kind.digest,
      label: kind.label
    };
    this.deck.slides.push(slide);
    this.selectedSlide = this.deck.slides.length - 1;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.closeKindPicker();
    this.fitStage();
    this.renderAll();
  }

  // Open the kind picker in "apply" mode targeting an existing slide. The flag is
  // read back by insertKind, which routes to applyTemplateToSlide instead of push.
  openKindPickerForApply(index) {
    this._applyTemplateTarget = index;
    this.openKindPicker();
  }

  // Apply a library template to an EXISTING slide in place. Swaps the slide's
  // layout (elements/options/variant/type) but preserves the author's own kind
  // (semantic label, ADR-0078) and name; drift tracking works via template_source.
  applyTemplateToSlide(slideIndex, kindIndex) {
    const kind = this.kinds && this.kinds[kindIndex];
    if (!kind || !kind.slide) return;

    const current = this.deck.slides[slideIndex];
    if (!current) return;

    const next = JSON.parse(JSON.stringify(kind.slide));

    // This is the SAME slide with its layout swapped — preserve its stable id so
    // slide-scope annotations stay attached (card #2120). Mint one if the slide
    // somehow had none (legacy slides pre-backfill).
    next.id = current.id || crypto.randomUUID();

    // Preserve the author's classification metadata, not the template's.
    if (current.kind !== undefined) next.kind = current.kind; else delete next.kind;
    if (current.name !== undefined) next.name = current.name; else delete next.name;

    next.template_source = {
      deck_id: kind.report_id,
      slide_index: kind.slide_index,
      digest: kind.digest,
      label: kind.label
    };

    this.deck.slides[slideIndex] = next;
    this.selectedSlide = slideIndex;
    this.selectedElement = null;
    this.selectionIndices = [];
    this.markDirty();
    this.closeKindPicker();
    this.fitStage();
    this.renderAll();
  }

  // ---- Drift detection (card #2007) --------------------------------------
  // The digest is computed SERVER-SIDE (Deck.slide_digest) and travels on
  // each kind's `digest` field in the `kinds` JSON payload — this controller
  // no longer computes its own (card #2007 follow-up). Ruby is the single
  // canonical source: deck_json is a jsonb column and Postgres does not
  // guarantee it preserves original key order, so a JS-side digest risked
  // flagging pure key-reordering as content drift. See Deck.slide_digest's
  // comment for the full reasoning.

  // Fetch the kind library in the background on connect; populate _driftDigests
  // and _kindLibrary so drift detection and re-apply are ready without opening
  // the picker. Silently swallows errors (network absent = no drift indicators).
  _fetchKindsForDrift() {
    if (!this.hasKindsUrlValue) return;
    fetch(this.kindsUrlValue, { headers: { Accept: "application/json" } })
      .then(r => r.json())
      .then(data => {
        this._kindLibrary = data.kinds || [];
        this._driftDigests = {};
        this._kindLibrary.forEach(kind => {
          const key = `${kind.report_id}:${kind.slide_index}`;
          this._driftDigests[key] = kind.digest;
        });
        this.renderSlideList();
      })
      .catch(() => {});
  }

  // Return true when the slide was inserted from a kind and the library's
  // version has a different digest (i.e. the template has changed since insert).
  _isSlideDrifted(slide) {
    if (!slide.template_source || !this._driftDigests) return false;
    const key = `${slide.template_source.deck_id}:${slide.template_source.slide_index}`;
    const current = this._driftDigests[key];
    if (current === undefined) return false;
    return current !== slide.template_source.digest;
  }

  // Re-apply the current template version onto a slide, preserving content
  // for elements whose slot name matches between old and new (Option A).
  _reapplyTemplate(slide) {
    const ts = slide.template_source;
    if (!ts || !this._kindLibrary) return;
    const kind = this._kindLibrary.find(k =>
      k.report_id === ts.deck_id && k.slide_index === ts.slide_index
    );
    if (!kind || !kind.slide) return;

    // Collect existing content keyed by slot name.
    const contentBySlot = {};
    (slide.elements || []).forEach(el => {
      if (el.slot && el.content !== undefined && el.content !== null) {
        contentBySlot[el.slot] = el.content;
      }
    });

    // Deep-copy template elements, re-apply matching slot content.
    const newElements = JSON.parse(JSON.stringify(kind.slide.elements || []));
    newElements.forEach(el => {
      if (el.slot && contentBySlot[el.slot] !== undefined) {
        el.content = contentBySlot[el.slot];
      }
    });

    slide.elements = newElements;
    slide.template_source = {
      ...ts,
      digest: kind.digest
    };

    this.markDirty();
    this.renderAll();
    this.renderSlideProperties(slide);
  }

  // ---- Slot → source binding picker (card #1826) -------------------------

  // Which element kinds can be bound to a live source. Shapes are pure chrome and
  // carry no data, so they never show the binding picker (ADR-0070 §1).
  isBindableKind(kind) {
    return kind === "text" || kind === "html";
  }

  // Auto-generated slot keys are dom_id-style: `<source>_<id>` with an optional
  // `_<n>` uniqueness suffix (e.g. `objective_42`, `objective_42_2`).
  slotKeyFor(source, id) {
    return `${source}_${id}`;
  }

  // True when a slot name looks machine-generated (matches slotKeyFor's shape).
  // Used to decide whether clearing a binding should also clear the slot: an
  // auto-name reverts to a plain unbound element; a human-authored name stays.
  isAutoSlotName(slot) {
    return typeof slot === "string" && /^[a-z]+_\d+(_\d+)?$/.test(slot);
  }

  // Pick a unique slot key for a freshly-bound (source, id). The candidate is
  // slotKeyFor(source, id). If that key is free, or already points at this very
  // record, reuse it. Otherwise suffix `_2`, `_3`, … until a free key is found.
  uniqueSlotKey(source, id) {
    this.deck.bindings = this.deck.bindings || {};
    const candidate = this.slotKeyFor(source, id);
    const pointsAtSame = (key) => {
      const b = this.deck.bindings[key];
      // Read the generalised `source_id`, falling back to the legacy `objective_id`
      // so a deck authored before D3 still de-dupes correctly (ADR-0070 §2/D3).
      return b && b.source === source && String(this.bindingSourceId(b)) === String(id);
    };
    if (!this.deck.bindings[candidate] || pointsAtSame(candidate)) return candidate;
    let n = 2;
    while (this.deck.bindings[`${candidate}_${n}`] && !pointsAtSame(`${candidate}_${n}`)) n += 1;
    return `${candidate}_${n}`;
  }

  // Build the binding section for an element. Lets the author bind the element to
  // an Objective (and pick display vs target_pct), writing the SAME deck-level
  // `bindings` entry the MCP authoring tools write — one shared contract for
  // human and AI authors (ADR-0067): bindings[slot] =
  //   { source: "objective", objective_id: <id>, value: "display" | "target_pct" }.
  //
  // Takes the element (not just a slot string) so selecting an objective can
  // auto-name a blank slot in place (ADR-0070 §1): the resource selection IS the
  // act that creates the slot. element.slot may be null on entry.
  renderBindingPicker(element) {
    this.deck.bindings = this.deck.bindings || {};
    const slot = element.slot;
    const binding = slot ? (this.deck.bindings[slot] || null) : null;

    const wrap = document.createElement("div");
    wrap.className = "flex flex-col gap-2 rounded border border-blue-100 bg-blue-50/50 p-2";

    const head = document.createElement("div");
    head.className = "text-2xs font-semibold uppercase tracking-wide text-blue-500";
    head.textContent = slot ? `Binding — ${slot}` : "Binding";
    wrap.appendChild(head);

    // Objective select. Loaded lazily from the report's reportable Mission; once
    // fetched the options are cached on the controller and reused across slots.
    const objectiveWrap = this.fieldWrap("Objective");
    const select = document.createElement("select");
    select.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    const none = document.createElement("option");
    none.value = "";
    none.textContent = this.objectives ? "— none —" : "Loading…";
    select.appendChild(none);
    const CHART_KINDS = ["doughnut", "pie", "bar", "column", "gauge", "table"];
    const objectives = this.objectives || [];
    const dataObjectives = objectives.filter((o) => !CHART_KINDS.includes(o.chart_kind));
    const chartObjectives = objectives.filter((o) => CHART_KINDS.includes(o.chart_kind));
    const appendGroup = (label, items) => {
      if (!items.length) return;
      const group = document.createElement("optgroup");
      group.label = label;
      items.forEach((objective) => {
        const option = document.createElement("option");
        option.value = String(objective.id);
        option.textContent = objective.name;
        if (binding && String(this.bindingSourceId(binding)) === String(objective.id)) option.selected = true;
        group.appendChild(option);
      });
      select.appendChild(group);
    };
    appendGroup("Data", dataObjectives);
    appendGroup("Charts", chartObjectives);
    select.addEventListener("change", (e) => this.selectObjectiveBinding(element, e.target.value));
    objectiveWrap.appendChild(select);
    wrap.appendChild(objectiveWrap);

    // value: display vs target_pct.
    wrap.appendChild(
      this.selectField("Value", binding ? (binding.value || "display") : "display", ["display", "target_pct"], (v) => {
        if (!element.slot) return; // no slot yet → nothing bound to revalue
        const sourceId = binding ? this.bindingSourceId(binding) : null;
        this.writeBinding(element.slot, sourceId, v, "objective");
      })
    );

    // Clear the binding (revert the element to a plain unbound element).
    if (binding) {
      const clear = document.createElement("button");
      clear.type = "button";
      clear.className = "rounded bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100";
      clear.textContent = "Clear binding";
      clear.addEventListener("click", () => this.clearBinding(element));
      wrap.appendChild(clear);
    }

    // Lazy-load the objective list the first time a binding picker is shown.
    if (!this.objectives && !this.objectivesLoading) this.loadObjectives();

    return wrap;
  }

  // Objective select → onChange. The selection drives slot creation: when an
  // objective is chosen and the element has no slot yet, auto-name one (a unique
  // `objective_<id>` key) and reflect it into the slot <input> in place before
  // writing the binding — no full re-render, so focus semantics hold. Choosing
  // "— none —" clears the binding (and the slot, if it was auto-named).
  selectObjectiveBinding(element, rawValue) {
    if (!rawValue) {
      this.clearBinding(element);
      return;
    }

    const id = parseInt(rawValue, 10);
    if (!element.slot) {
      const key = this.uniqueSlotKey("objective", id);
      element.slot = key;
      // Reflect the auto-name into the live slot <input> without re-rendering, so
      // the field shows the generated key and stays editable as an override.
      if (this.slotInputEl) this.slotInputEl.value = key;
    }
    this.writeBinding(element.slot, id, this.bindingValueFor(element.slot), "objective");
  }

  bindingValueFor(slot) {
    const binding = this.deck.bindings && this.deck.bindings[slot];
    return binding && binding.value ? binding.value : "display";
  }

  // The generic record id for a binding (ADR-0070 §2/D3). New entries write
  // `source_id`; entries authored before D3 only have `objective_id`. Read either
  // so the editor renders/de-dupes legacy decks correctly.
  bindingSourceId(binding) {
    if (!binding) return null;
    return binding.source_id != null ? binding.source_id : binding.objective_id;
  }

  // Write the deck-level binding entry for a slot, matching the MCP-tools contract
  // exactly (ADR-0070 §2/D3): { source, source_id, value }. An empty id clears the
  // binding. `source` defaults to "objective" so the existing objective picker path
  // is unchanged; the Object flow passes "image" explicitly.
  writeBinding(slot, sourceId, value, source = "objective") {
    this.deck.bindings = this.deck.bindings || {};
    if (!sourceId) {
      delete this.deck.bindings[slot];
    } else {
      this.deck.bindings[slot] = {
        source: source,
        source_id: parseInt(sourceId, 10),
        value: value || (source === "image" ? "url" : "display")
      };
    }
    this.markDirty();
    this.renderProperties();
  }

  // Clear the binding for an element's slot. If the slot was machine-named (an
  // auto-generated `objective_<id>` key), also clear element.slot so the element
  // reverts to a plain unbound element rather than leaving a dangling
  // `{objective_42}` placeholder. A human-authored slot name is left intact
  // (the author may want to re-bind it). ADR-0070 §1.
  clearBinding(element) {
    const slot = element.slot;
    if (!slot) {
      this.renderProperties();
      return;
    }
    if (this.deck.bindings) delete this.deck.bindings[slot];
    if (this.isAutoSlotName(slot)) element.slot = null;
    this.markDirty();
    this.renderProperties();
  }

  // Build the Link section for an element (ADR-0070 §3). Available on EVERY kind,
  // unlike the binding picker (bindable kinds only): any element can carry a
  // navigation link. A link is EITHER an external URL (primary) OR a WIN record.
  //
  // PRIMARY control: a "URL" text input. Typing an http(s) URL and committing it
  // (change/blur — NOT per keystroke, so editing never steals focus or churns the
  // panel) sets element.options.link = { url } and REPLACES any record link.
  // Clearing the field (when the current link is a url link) removes the link.
  //
  // SECONDARY control: "Link to WIN record…" reuses the SAME object chooser the
  // Object element uses (parameterised into "link" mode); a picked record REPLACES
  // any url. "Clear link" removes whatever link is set, regardless of type.
  //
  // The href is never resolved here — that is server-side only
  // (Deck#resolve_element_link); this section only stores element.options.link and
  // the cached display label.
  renderLinkSection(element) {
    element.options = element.options || {};
    const link = element.options.link || null;

    const wrap = document.createElement("div");
    wrap.className = "flex flex-col gap-2 rounded border border-emerald-100 bg-emerald-50/50 p-2";

    const head = document.createElement("div");
    head.className = "text-2xs font-semibold uppercase tracking-wide text-emerald-600";
    head.textContent = "Link";
    wrap.appendChild(head);

    const current = document.createElement("div");
    current.className = "text-xs text-gray-700";
    current.textContent = link ? this.linkLabelFor(link) : "— no link";
    wrap.appendChild(current);

    // PRIMARY: external URL field. Commit on change/blur (not input) so editing
    // mid-value never re-renders the panel out from under the caret.
    const urlWrap = this.fieldWrap("URL");
    const urlInput = document.createElement("input");
    urlInput.type = "url";
    urlInput.value = (link && link.url) || "";
    urlInput.placeholder = "https://…";
    urlInput.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    urlInput.addEventListener("change", (e) => this.setElementLinkUrl(element, e.target.value));
    urlWrap.appendChild(urlInput);
    wrap.appendChild(urlWrap);

    const buttons = document.createElement("div");
    buttons.className = "flex flex-wrap gap-2";

    const setBtn = document.createElement("button");
    setBtn.type = "button";
    setBtn.className = "rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100";
    setBtn.textContent = link && link.source ? "Change WIN record…" : "Link to WIN record…";
    setBtn.addEventListener("click", () => this.openObjectChooserForLink());
    buttons.appendChild(setBtn);

    if (link) {
      const clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.className = "rounded bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100";
      clearBtn.textContent = "Clear link";
      clearBtn.addEventListener("click", () => this.clearElementLink(element));
      buttons.appendChild(clearBtn);
    }

    wrap.appendChild(buttons);
    return wrap;
  }

  // Commit an external URL from the Link section's URL field (ADR-0070 §3). A
  // non-empty value REPLACES the whole link with { url } (dropping any record link).
  // An empty value clears the link only when the current link is a url link (so
  // blurring an empty field next to a record link does not wipe the record). The
  // href is never validated here — the server (Deck#resolve_element_link) is the
  // XSS guard; the editor only stores the value. Re-renders bindings (label/clear
  // state) on commit, not per keystroke.
  setElementLinkUrl(element, rawValue) {
    element.options = element.options || {};
    const trimmed = (rawValue || "").trim();
    const currentLink = element.options.link || null;

    if (trimmed) {
      element.options.link = { url: trimmed };
    } else if (currentLink && currentLink.url && !currentLink.source) {
      delete element.options.link;
    } else {
      return; // empty field next to a record link — leave it be, nothing to commit
    }

    this.markDirty();
    this.renderCanvas();
    this.renderBindings();
  }

  // The display label for a stored link. A url link shows the URL (truncated for
  // display). A record link prefers the cached link_label written at pick time (so
  // we never re-fetch the record name), falling back to "<Source> #<id>".
  linkLabelFor(link) {
    if (link.url) {
      const url = String(link.url);
      return url.length > 40 ? `${url.slice(0, 39)}…` : url;
    }
    if (link.link_label) return String(link.link_label);
    const source = link.source ? link.source.charAt(0).toUpperCase() + link.source.slice(1) : "Link";
    return `${source} #${link.source_id}`;
  }

  // Open the SAME object chooser the Object element uses, but in "link" mode so a
  // picked record sets the selected element's link instead of inserting a new bound
  // element (ADR-0070 §3). The mode flag is read by the chooser's row handlers.
  openObjectChooserForLink() {
    this.objectChooserMode = "link";
    this.openObjectChooser();
  }

  // Set the selected element's resource link from a chosen (source, record). Stores
  // the ADR-0070 §3 reference { source, source_id } plus a cached display label, then
  // marks dirty, closes the chooser, and re-renders Properties (so the Link section
  // and badge reflect the new target). A link is render-only navigation — it never
  // writes a binding or result_json.
  setElementLink(source, recordId, label) {
    const slide = this.currentSlide();
    const element = slide && this.selectedElement !== null ? slide.elements[this.selectedElement] : null;
    if (!element) { this.closeObjectChooser(); return; }

    element.options = element.options || {};
    element.options.link = {
      source,
      source_id: parseInt(recordId, 10),
      link_label: label ? String(label) : undefined
    };

    this.markDirty();
    this.closeObjectChooser();
    this.renderCanvas();
    this.renderProperties();
  }

  // Remove an element's resource link (ADR-0070 §3), reverting it to an unwrapped,
  // non-navigating element. Drops the cached label with it.
  clearElementLink(element) {
    if (element.options) delete element.options.link;
    this.markDirty();
    this.renderCanvas();
    this.renderProperties();
  }

  loadObjectives() {
    if (!this.hasObjectivesUrlValue) return;
    this.objectivesLoading = true;
    fetch(this.objectivesUrlValue, { headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this.objectives = data.objectives || [];
        this.objectivesLoading = false;
        this.onObjectivesLoaded();
      })
      .catch(() => {
        this.objectives = [];
        this.objectivesLoading = false;
        this.onObjectivesLoaded();
      });
  }

  // The binding picker and the Measures tab both resolve objective names from the
  // cached list, and either can trigger the lazy load. Re-render Properties (the
  // picker's home) and, when it is the active panel, the Measures overview so a
  // load kicked off from one tab refreshes whichever view is on screen.
  onObjectivesLoaded() {
    this.renderProperties();
    if ((this.activeTabValue || "designer") === "measures") this.renderMeasures();
    this.renderAll(); // re-render canvas so chart skeletons appear
  }

  // ---- Collection sources / albums (ADR-0070 §4b) ------------------------

  // Lazily fetch the pickable collection sources (albums) once, caching on the
  // controller like objectives. Re-renders Properties + the canvas on land so the
  // album select fills and the WYSIWYG pager draws its preview tiles. Defensive:
  // any failure caches an empty list so the inspector still renders ("No albums").
  loadAlbums() {
    if (this.albums || this.albumsLoading) return;
    if (!this.hasAlbumsUrlValue) { this.albums = []; this.onAlbumsLoaded(); return; }
    this.albumsLoading = true;
    fetch(this.albumsUrlValue, { headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this.albums = data.albums || [];
        this.albumsLoading = false;
        this.onAlbumsLoaded();
      })
      .catch(() => {
        this.albums = [];
        this.albumsLoading = false;
        this.onAlbumsLoaded();
      });
  }

  // Re-render Properties (the album select's home) and the canvas (the WYSIWYG
  // pager, which needs the picked album's image urls) once the list lands.
  onAlbumsLoaded() {
    this.renderProperties();
    this.renderCanvas();
  }

  // The cached album hash for an id, or null. Used by both the inspector (to show
  // the picked album's name/count) and the WYSIWYG render (to tile image_urls).
  albumFor(sourceId) {
    if (sourceId === null || sourceId === undefined) return null;
    const id = parseInt(sourceId, 10);
    return (this.albums || []).find((a) => a.id === id) || null;
  }

  // ---- Page numbers (card #2093) -----------------------------------------

  // The number of PRINT pages a collection slide expands into — the SINGLE source of
  // truth the preview surfaces and the page-number sequence both read. Mirrors
  // CollectionExpander#expand_album: max(ceil(image_count / page_size), 1). A
  // non-collection slide is one surface.
  collectionPageCount(slide) {
    if (slide.type !== "collection") return 1;
    const collection = slide.collection || {};
    const album = this.albumFor(collection.source_id);
    const imageCount = album ? (album.image_count || 0) : 0;
    const pageSize = Math.max(1, this.int(collection.page_size) || 6);
    return Math.max(Math.ceil(imageCount / pageSize), 1);
  }

  // Whether a slide SHOWS a page number under the deck-wide rule — the byte-for-byte
  // twin of Deck#slide_shows_page_number?. Tri-state by presence:
  //   page_number === false → never; === true → always; absent → kind default
  //   (off for cover/title via PAGE_NUMBER_DEFAULT_OFF_KINDS, on otherwise).
  slideShowsPageNumber(slide) {
    if (slide.page_number === false) return false;
    if (slide.page_number === true) return true;
    return !PAGE_NUMBER_DEFAULT_OFF_KINDS.includes(String(slide.kind || ""));
  }

  // The ABSOLUTE "position / total" sequence over the deck, computed over the RAW
  // slide list with each collection slide expanded by collectionPageCount (so a
  // collection's N pages are N surfaces in the sequence). total = the count of ALL
  // expanded surfaces (every collection page counts, cover/title count, hidden
  // surfaces count) — the editor twin of expanded.size. Each surface's number is its
  // 1-based ABSOLUTE position in that full sequence; hidden surfaces STILL advance
  // (and occupy) the position but are absent from the map, so nothing renumbers when
  // a badge is hidden. Returns a Map keyed by a stable surface key
  // (`${slideIndex}:${page}`) → { k, total } for every SHOWN surface. This is the
  // editor twin of Deck#number_pages — same default-by-kind, same override
  // precedence, same absolute numbering.
  pageNumberSequence() {
    const map = new Map();
    if (this.deck?.design?.page_numbers !== true) return map;

    // total = count of ALL expanded surfaces across the whole deck, shown or hidden.
    let total = 0;
    (this.deck.slides || []).forEach((slide) => {
      total += this.collectionPageCount(slide);
    });

    // Assign each surface its 1-based ABSOLUTE position across the full sequence.
    // Hidden surfaces advance the counter and count toward total but store nothing;
    // shown surfaces store their absolute position.
    let position = 0;
    (this.deck.slides || []).forEach((slide, slideIndex) => {
      const pages = this.collectionPageCount(slide);
      const shown = this.slideShowsPageNumber(slide);
      for (let page = 1; page <= pages; page++) {
        position += 1;
        if (shown) map.set(`${slideIndex}:${page}`, { k: position, total });
      }
    });
    return map;
  }

  // The { k, total } for one surface, looked up in the (memoised-per-render) sequence
  // by its slide index + page, or null when the surface shows no number. The sequence
  // is rebuilt at the top of each render pass (renderCanvas / renderPreview) and held
  // on this._pageSeq so every per-surface draw reads the same map without recomputing.
  pageNumberInfo(slideIndex, page = 1) {
    if (!this._pageSeq) return null;
    return this._pageSeq.get(`${slideIndex}:${page}`) || null;
  }

  // The deck index of a slide object (by identity) — collection/preview render methods
  // hold the slide, not its index, so this resolves it back for the sequence lookup.
  slideIndexOf(slide) {
    return (this.deck.slides || []).indexOf(slide);
  }

  // The page-number node — editor mirror of Deck#page_number_element. Builds the
  // SAME element hash the server emits (BOTTOM-right white pill, x:1160 y:676 w:100
  // h:30, right-aligned, grey 14px on #fff, radius 999, z-index 50) and renders it
  // through the shared elementNode(), so the pill is byte-for-byte the published one
  // (PARITY — author sees exactly what publishes). Non-interactive editor chrome
  // (no handle); content is "k / M". The marker class tags it as page-number chrome.
  pageNumberNode(k, total) {
    const node = this.elementNode({
      kind: "text",
      x: 1160,
      y: 676,
      w: 100,
      h: 30,
      content: `${k} / ${total}`,
      options: {
        align: "right",
        size: 14,
        color: "#6b7c8c",
        background: "#ffffff",
        radius: 999,
        z_index: 50,
      },
    });
    node.classList.add("deck-editor-page-number");
    return node;
  }

  // Draw the page-number node onto a surface IF this slide/page shows one. Shared by
  // every render site (canvas, preview, collection pages) so the rule lives in one
  // place. slideIndex+page key the sequence; a hidden surface draws nothing.
  appendPageNumber(surface, slideIndex, page = 1) {
    const info = this.pageNumberInfo(slideIndex, page);
    if (info) surface.appendChild(this.pageNumberNode(info.k, info.total));
  }

  // The per-slide "Show page number" control (card #2093) — a tri-state select that
  // maps slide.page_number's presence to one of three options, mirroring the server
  // override semantics:
  //   "default" → key cleared (kind default: off for cover/title, on otherwise)
  //   "always"  → slide.page_number = true  (force show, e.g. a numbered cover)
  //   "never"   → slide.page_number = false (force hide)
  // Returns a fragment: the select + (when the master switch is OFF) a hint that the
  // control only takes effect once deck-wide page numbers are enabled on the Themes
  // tab. Appends to whichever inspector renders it (canvas slide or collection).
  pageNumberControl(slide) {
    const frag = document.createDocumentFragment();

    const current = slide.page_number === true ? "always"
      : slide.page_number === false ? "never"
        : "default";

    frag.appendChild(
      this.selectFieldKeyed(
        "Show page number",
        current,
        ["default", "always", "never"],
        ["Default", "Always", "Never"],
        (v) => {
          if (v === "always") slide.page_number = true;
          else if (v === "never") slide.page_number = false;
          else delete slide.page_number;
          this.markDirty();
          this.renderCanvas();
        }
      )
    );

    if (this.deck?.design?.page_numbers !== true) {
      frag.appendChild(this.hint("Deck-wide page numbers are off — turn them on in Themes for this to take effect."));
    }

    return frag;
  }

  // The collection-slide inspector (ADR-0070 §4b): album picker + page/column
  // counts + (secondary) grid geometry and a page-badge toggle. Each control writes
  // to slide.collection.*, marks dirty, and re-renders the canvas so the WYSIWYG
  // pager updates live. Lazy-loads the album list on first render.
  renderCollectionProperties(slide) {
    slide.collection = slide.collection || { source: "album" };
    slide.options = slide.options || {};
    const collection = slide.collection;

    // Kick the album fetch on first open (cached thereafter).
    if (!this.albums && !this.albumsLoading) this.loadAlbums();

    // Slide background (card: collection background control). The SAME freeform colour
    // picker renderSlideProperties uses for a canvas slide — so a collection slide gets
    // the RT brand quick-pick chips for free and writes to slide.options.background. The
    // expander already carries @slide["options"] onto every emitted page and _canvas
    // resolves it via Deck.resolve_background, so no render change is needed — this was
    // the only missing control. An empty value clears back to undefined (the default
    // sapphire+white ground).
    this.propertiesTarget.appendChild(this.sectionLabel("Slide"));
    this.propertiesTarget.appendChild(
      this.colorField("Background", slide.options.background || "", (v) => {
        slide.options.background = (v.trim() === "" ? undefined : v.trim());
        this.markDirty();
        this.renderCanvas();
      })
    );

    // Per-slide page-number override (card #2093). On a collection slide this applies
    // to ALL its expanded pages — Never hides numbers on every page of the stack,
    // Always forces them on. Mirrors the canvas-slide control.
    this.propertiesTarget.appendChild(this.pageNumberControl(slide));

    this.propertiesTarget.appendChild(this.sectionLabel("Collection"));

    if (this.albumsLoading && !this.albums) {
      this.propertiesTarget.appendChild(this.hint("Loading albums…"));
      return;
    }

    const albums = this.albums || [];
    if (!albums.length) {
      this.propertiesTarget.appendChild(this.hint("No albums available for this report's client."));
    } else {
      this.propertiesTarget.appendChild(this.albumSelectField(collection));
      if (collection.source_id === null || collection.source_id === undefined) {
        this.propertiesTarget.appendChild(this.hint("Pick an album to fill this collection."));
      }
    }

    // Page size + columns — the auto-grid layout knobs (CollectionExpander
    // DEFAULT_PAGE_SIZE 6 / DEFAULT_COLUMNS 3). Both write through positiveInt so a
    // blank/zero never produces a broken grid; floored at 1 to mirror the expander.
    this.propertiesTarget.appendChild(
      this.numberField("Images per page", collection.page_size || 6, (v) => {
        collection.page_size = Math.max(1, v);
        this.markDirty();
        this.renderCanvas();
      })
    );
    this.propertiesTarget.appendChild(
      this.numberField("Columns", collection.columns || 3, (v) => {
        collection.columns = Math.max(1, v);
        this.markDirty();
        this.renderCanvas();
      })
    );

    // Grid geometry — secondary/advanced. Collapsed in a <details> so the common
    // case (page size + columns) stays clean. Each axis writes to collection.grid.*,
    // overriding CollectionExpander DEFAULT_GRID. Blank inputs fall back to default.
    this.propertiesTarget.appendChild(this.renderCollectionGrid(collection));
  }

  // The album <select>, value = source_id (a number) or "" for unpicked. Options
  // show the album name + image count so the author can tell albums apart. Writing
  // sets collection.source_id to the parsed int (or null for the blank option),
  // marks dirty, and re-renders the canvas so the WYSIWYG swaps to the new album's
  // page-1 preview.
  albumSelectField(collection) {
    const wrap = this.fieldWrap("Album");
    const select = document.createElement("select");
    select.className = "rounded border border-gray-300 px-2 py-1 text-sm";

    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "— Pick an album —";
    if (collection.source_id === null || collection.source_id === undefined) blank.selected = true;
    select.appendChild(blank);

    (this.albums || []).forEach((album) => {
      const o = document.createElement("option");
      o.value = String(album.id);
      o.textContent = `${album.name} (${album.image_count})`;
      if (parseInt(collection.source_id, 10) === album.id) o.selected = true;
      select.appendChild(o);
    });

    select.addEventListener("change", (e) => {
      const v = e.target.value;
      collection.source_id = v === "" ? null : parseInt(v, 10);
      this.markDirty();
      this.renderProperties();
      this.renderCanvas();
    });

    wrap.appendChild(select);
    return wrap;
  }

  // The advanced grid-geometry controls, collapsed in a <details>. x/y/w/h/gap each
  // override CollectionExpander DEFAULT_GRID {x:64,y:120,w:1152,h:536,gap:16}; a
  // blank field clears the override back to the default. Writes mark dirty + redraw.
  renderCollectionGrid(collection) {
    const details = document.createElement("details");
    details.className = "mt-2 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600";
    const summary = document.createElement("summary");
    summary.className = "cursor-pointer select-none font-semibold text-gray-500";
    summary.textContent = "Grid geometry";
    details.appendChild(summary);

    const defaults = { x: 64, y: 120, w: 1152, h: 536, gap: 16 };
    collection.grid = collection.grid || {};
    const grid = collection.grid;

    const body = document.createElement("div");
    body.className = "mt-2 flex flex-col gap-2";
    ["x", "y", "w", "h", "gap"].forEach((key) => {
      const current = grid[key] !== undefined && grid[key] !== null ? grid[key] : defaults[key];
      body.appendChild(
        this.numberField(key.toUpperCase(), current, (v) => {
          grid[key] = Math.max(0, v);
          this.markDirty();
          this.renderCanvas();
        })
      );
    });
    details.appendChild(body);
    return details;
  }

  // A labelled checkbox field, matching the other field builders' look. Calls
  // onChange(checked) on toggle.
  checkboxField(label, checked, onChange) {
    const wrap = document.createElement("label");
    wrap.className = "flex items-center gap-2 text-xs text-gray-600";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = !!checked;
    input.className = "rounded border-gray-300";
    input.addEventListener("change", (e) => onChange(e.target.checked));
    const span = document.createElement("span");
    span.textContent = label;
    wrap.appendChild(input);
    wrap.appendChild(span);
    return wrap;
  }

  // ---- Client asset gallery (card #1824) ---------------------------------

  // Open the gallery for the currently-selected image element. Reuses the kind
  // picker idiom: show the overlay, bind Escape, lazy-load on open. The element
  // is captured so pickAsset writes back to the right element even if selection
  // changes are impossible while the modal is open (it sits over the editor).
  openAssetPicker() {
    if (!this.hasAssetModalTarget) return;
    const slide = this.currentSlide();
    const element = slide && this.selectedElement !== null ? slide.elements[this.selectedElement] : null;
    if (!element || element.kind !== "image") return;

    this.assetTargetElement = element;
    this.assetModalTarget.classList.remove("hidden");
    this.assetModalTarget.classList.add("flex");
    this.boundAssetKeydown = (event) => {
      if (event.key === "Escape") this.closeAssetPicker();
    };
    document.addEventListener("keydown", this.boundAssetKeydown);

    // Default to the client scope and the Documents tab, then load that scope's
    // assets once per open (cached per scope, so switching never refetches). The
    // "Deck Assets" folder is fixed by the deck's link (set on its edit form, not
    // here) — the picker only displays it, so there is nothing to seed or persist.
    this.activeAssetScope = this.activeAssetScope || "client";
    this.activeAssetSource = this.activeAssetSource || "documents";
    this.applyAssetScopeVisibility();
    this.loadAssets();
  }

  closeAssetPicker() {
    if (!this.hasAssetModalTarget) return;
    this.assetModalTarget.classList.add("hidden");
    this.assetModalTarget.classList.remove("flex");
    if (this.boundAssetKeydown) {
      document.removeEventListener("keydown", this.boundAssetKeydown);
      this.boundAssetKeydown = null;
    }
  }

  // Backdrop click closes; clicks inside the panel bubble up, so act only when the
  // overlay itself is the target (mirrors kindModalBackdrop).
  assetModalBackdrop(event) {
    if (event.target === this.assetModalTarget) this.closeAssetPicker();
  }

  selectAssetTab(event) {
    this.activeAssetSource = event.currentTarget.dataset.assetSource;
    this.renderAssets();
  }

  // Switch the gallery between the deck's own client (the three group tabs) and
  // the "Deck Assets" folder source (card #2080). Toggles the group-tabs row
  // (shown in client scope, hidden in folder scope — folder is a flat list, no
  // tabs), then loads that scope's assets. loadAssets serves cached results when
  // a scope has been fetched before, so toggling back and forth never refetches.
  selectAssetScope(event) {
    this.activeAssetScope = event.currentTarget.dataset.assetScope;
    this.applyAssetScopeVisibility();
    this.loadAssets();
  }

  // Show the group tabs in "client" scope, hide them in "folder" scope (the
  // folder source is a flat list with no tabs). The pill active styling is applied
  // in renderAssets (which always runs after); the read-only folder caption's
  // visibility is also handled there (it needs the resolved folder name).
  applyAssetScopeVisibility() {
    const folderScope = (this.activeAssetScope || "client") === "folder";
    if (this.hasAssetGroupTabsTarget) this.assetGroupTabsTarget.classList.toggle("hidden", folderScope);
  }

  // Lazy-load the active scope's assets once, then render. Results are cached PER
  // SCOPE (this.assetGroupsByScope) so switching scope or re-opening does not
  // refetch a scope already loaded. The "client" scope fetches the three groups;
  // the "folder" scope fetches source=folder (the deck's linked folder — set on
  // the edit form, never passed from here) and gets back a flat { assets } list
  // plus the folder's name (for the read-only caption), cached under "folder".
  loadAssets() {
    this.assetGroupsByScope = this.assetGroupsByScope || {};
    const scope = this.activeAssetScope || "client";
    const key = this.assetScopeKey(scope);

    if (this.assetGroupsByScope[key]) {
      this.renderAssets();
      return;
    }
    if (!this.hasAssetsUrlValue) {
      this.assetGroupsByScope[key] = this.emptyAssetGroups(scope);
      this.renderAssets();
      return;
    }

    let url = this.assetsUrlValue;
    if (scope === "folder") {
      url += (url.includes("?") ? "&" : "?") + "source=folder";
    }

    this.showAssetLoading(true);
    fetch(url, { headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this.assetGroupsByScope[key] = scope === "folder"
          ? { assets: data.assets || [], folderName: data.folder_name || null, folderId: data.folder_id || null }
          : { documents: data.documents || [], images: data.images || [], products: data.products || [] };
        this.showAssetLoading(false);
        this.renderAssets();
      })
      .catch(() => {
        this.assetGroupsByScope[key] = this.emptyAssetGroups(scope);
        this.showAssetLoading(false);
        this.renderAssets();
      });
  }

  // The cache key for the active scope. Both scopes are a single key now — the
  // folder source is fixed by the deck's link (no per-folder keying), so "folder"
  // caches once.
  assetScopeKey(scope) {
    if (scope === "folder") return "folder";
    return scope;
  }

  // The empty result for a scope, matching its shape (flat for folder, grouped for
  // client) so renderAssets reads it uniformly.
  emptyAssetGroups(scope) {
    return scope === "folder" ? { assets: [], folderName: null, folderId: null } : { documents: [], images: [], products: [] };
  }

  showAssetLoading(loading) {
    if (this.hasAssetLoadingTarget) this.assetLoadingTarget.classList.toggle("hidden", !loading);
    if (this.hasAssetGridTarget) this.assetGridTarget.classList.toggle("hidden", loading);
    if (loading && this.hasAssetEmptyTarget) this.assetEmptyTarget.classList.add("hidden");
  }

  renderAssets() {
    if (!this.hasAssetGridTarget) return;
    const scope = this.activeAssetScope || "client";
    const source = this.activeAssetSource || "documents";
    const cached = (this.assetGroupsByScope && this.assetGroupsByScope[this.assetScopeKey(scope)]) || {};
    // Folder scope is a flat list; client scope picks the active group's tab.
    const assets = scope === "folder" ? (cached.assets || []) : (cached[source] || []);

    // Active-scope styling (segmented pills): active = raised white pill, inactive
    // = muted text on the shared grey track.
    if (this.hasAssetScopeTarget) {
      this.assetScopeTargets.forEach((pill) => {
        const active = pill.dataset.assetScope === scope;
        pill.classList.toggle("bg-white", active);
        pill.classList.toggle("shadow-sm", active);
        pill.classList.toggle("text-gray-900", active);
        pill.classList.toggle("text-gray-500", !active);
      });
    }

    // Active-tab styling — only meaningful in client scope (the tabs are hidden in
    // folder scope), but harmless to keep in sync.
    this.assetTabTargets.forEach((tab) => {
      const active = tab.dataset.assetSource === source;
      tab.classList.toggle("border-blue-500", active);
      tab.classList.toggle("text-blue-600", active);
      tab.classList.toggle("border-transparent", !active);
      tab.classList.toggle("text-gray-500", !active);
    });

    // Read-only folder caption (card #2080): in folder scope, show which folder
    // the source is drawing from ("Showing: <name>") — the deck's link, resolved
    // server-side, or the RT default. The picker can't change it; it only displays
    // it. Hidden in client scope, and in folder scope when no name resolved.
    if (this.hasAssetFolderCaptionTarget) {
      const folderName = scope === "folder" ? (cached.folderName || null) : null;
      this.assetFolderCaptionTarget.textContent = folderName ? ("Showing: " + folderName) : "";
      this.assetFolderCaptionTarget.classList.toggle("hidden", !folderName);
    }

    this.assetGridTarget.replaceChildren();
    if (this.hasAssetEmptyTarget) {
      this.assetEmptyTarget.classList.toggle("hidden", assets.length > 0);
      if (assets.length === 0) this.fillAssetEmptyState(scope, cached);
    }

    assets.forEach((asset) => {
      const frag = this.assetTileTemplateTarget.content.cloneNode(true);
      const tile = frag.querySelector("[data-deck-editor-target='assetTile']");
      tile.dataset.url = asset.url;
      // The Image source (card #2412) carries a smaller `preview` variant
      // alongside the full-original `url` — pickAsset bakes THIS into the slide so
      // a hand-picked Gallery photo doesn't balloon the captured PDF/PPTX.
      // Documents/products assets carry no `preview` key; dataset.preview is simply
      // absent then, and pickAsset falls back to dataset.url.
      if (asset.preview) tile.dataset.preview = asset.preview;
      const img = tile.querySelector("[data-role='thumb']");
      img.src = asset.thumb_url || asset.url;
      img.alt = asset.label || "";
      const labelEl = tile.querySelector("[data-role='label']");
      labelEl.textContent = asset.label || "";
      labelEl.title = asset.label || "";
      this.assetGridTarget.appendChild(frag);
    });
  }

  // The empty-state copy for the asset gallery (card #2172). "Deck Assets" scope
  // resolves its folder server-side (Deck#effective_assets_folder — the deck's
  // own link, else the account's house default) and the assets endpoint always
  // returns folder_id alongside the flat list. That lets us tell apart:
  //   - no folder resolved at all (folderId null) — the deck has no link AND the
  //     account's house folder isn't configured/named correctly. Previously this
  //     rendered the exact same blank grid as a genuinely empty folder, so an
  //     author had no way to tell "nothing to pick" from "this is broken".
  //   - a folder DID resolve but simply holds no images yet.
  // Client scope keeps the original generic copy — it isn't part of this fix.
  fillAssetEmptyState(scope, cached) {
    const titleEl = this.assetEmptyTarget.querySelector("[data-role='title']");
    const subtitleEl = this.assetEmptyTarget.querySelector("[data-role='subtitle']");
    const unresolved = scope === "folder" && !cached.folderId;

    if (titleEl) {
      titleEl.textContent = unresolved
        ? "No Deck Assets folder configured for this client."
        : scope === "folder"
          ? "This folder has no images yet."
          : "No assets here.";
    }
    if (subtitleEl) {
      subtitleEl.textContent = unresolved
        ? "Pick one on the deck's edit form, or add images to “This client” instead."
        : "";
    }
    if (this.hasAssetEmptyActionTarget) this.assetEmptyActionTarget.classList.toggle("hidden", !unresolved);
  }

  // Fill the captured image element's content with the chosen asset URL so it
  // renders in the slide. A picked asset is a literal value, so any bound slot is
  // cleared — an image shows a hand-picked URL, not a result_json lookup.
  // Prefers the smaller `preview` variant over the full-original `url` (#2412) so a
  // newly authored Gallery placement doesn't balloon the captured PDF/PPTX; falls
  // back to `url` for asset kinds (documents/products) that carry no preview.
  pickAsset(event) {
    const url = event.currentTarget.dataset.preview || event.currentTarget.dataset.url;
    const element = this.assetTargetElement;
    if (!url || !element) return;

    element.content = url;
    element.slot = null;
    this.markDirty();
    this.closeAssetPicker();
    this.renderCanvas();
    this.renderProperties();
  }

  // ---- Object: the data-bound add-flow (ADR-0070 §2/D1) -------------------

  // Open the Object chooser. Two stages in one modal: pick a SOURCE TYPE
  // (Objective | Image), then pick the RECORD. On selection a concrete pre-bound
  // element is inserted (Objective → a bound text element, Image → a bound image
  // element) with an auto-named slot. v1 sources are Objective + Image only (D2);
  // Album/Document are deferred (additive later). The optional `point` is the
  // right-click cursor position — captured so the resolved element drops there;
  // omitted (panel chip) means default placement.
  openObjectChooser(point = null) {
    if (!this.hasObjectModalTarget) return;
    // The panel chip wires straight to this action, so a DOM Event leaks in as the
    // first arg. A MouseEvent carries a numeric `.x`, so guard on Event explicitly:
    // only a plain { x, y } canvas point (from the right-click menu) is a position.
    const isPoint = point && !(point instanceof Event) && typeof point.x === "number";
    this.objectInsertPoint = isPoint ? point : null;
    // Chooser mode (ADR-0070 §2/§3): the SAME chooser serves two flows. "insert"
    // (the default, panel chip + right-click) inserts a new pre-bound element;
    // "link" sets the selected element's resource link. openObjectChooserForLink
    // flips the flag BEFORE calling this, so this default only fires on the insert
    // entry points — never resetting a link-mode open back to insert.
    if (this.objectChooserMode !== "link") this.objectChooserMode = "insert";
    this.objectModalTarget.classList.remove("hidden");
    this.objectModalTarget.classList.add("flex");
    this.showObjectStep("source");
    this.boundObjectKeydown = (event) => {
      if (event.key === "Escape") this.closeObjectChooser();
    };
    document.addEventListener("keydown", this.boundObjectKeydown);
  }

  closeObjectChooser() {
    if (!this.hasObjectModalTarget) return;
    this.objectModalTarget.classList.add("hidden");
    this.objectModalTarget.classList.remove("flex");
    if (this.boundObjectKeydown) {
      document.removeEventListener("keydown", this.boundObjectKeydown);
      this.boundObjectKeydown = null;
    }
    // Reset to the default flow so a cancelled link-mode open never leaks into the
    // next insert-mode open (ADR-0070 §3).
    this.objectChooserMode = "insert";
  }

  objectModalBackdrop(event) {
    if (event.target === this.objectModalTarget) this.closeObjectChooser();
  }

  // Toggle the two stages. "source" shows the type buttons; "record" shows the
  // record list for the chosen type. Each step is a flex column when visible, so
  // toggle `flex` alongside `hidden` (the modal idiom — hidden wins display:none).
  showObjectStep(step) {
    if (this.hasObjectSourceStepTarget) {
      this.objectSourceStepTarget.classList.toggle("hidden", step !== "source");
      this.objectSourceStepTarget.classList.toggle("flex", step === "source");
    }
    if (this.hasObjectRecordStepTarget) {
      this.objectRecordStepTarget.classList.toggle("hidden", step !== "record");
      this.objectRecordStepTarget.classList.toggle("flex", step === "record");
    }
  }

  // Stage 1 → 2: the author picked a source TYPE. Capture it and load that type's
  // records into the list. Wired from the source-type buttons via data-object-source.
  selectObjectSource(event) {
    this.objectSource = event.currentTarget.dataset.objectSource;
    if (this.hasObjectRecordTitleTarget) {
      this.objectRecordTitleTarget.textContent =
        this.objectSource === "image" ? "Pick an image" : "Pick an objective";
    }
    this.showObjectStep("record");
    this.loadObjectRecords();
  }

  // Back to the source-type step from the record list.
  backToObjectSource() {
    this.showObjectStep("source");
  }

  // Load the records for the chosen source type, reusing the SAME endpoints the
  // binding picker and asset gallery already use: objectives from #objectives,
  // images from the #assets "images" group (the only group carrying a bindable
  // record id, D2). Both are cached on the controller, so re-opening is cheap.
  loadObjectRecords() {
    if (this.objectSource === "image") {
      if (this.assetGroups) { this.renderObjectRecords(this.assetGroups.images || []); return; }
      this.showObjectRecordLoading(true);
      this.fetchAssets().then((groups) => {
        this.showObjectRecordLoading(false);
        this.renderObjectRecords(groups.images || []);
      });
      return;
    }

    // objectives
    if (this.objectives) { this.renderObjectiveRecords(); return; }
    this.showObjectRecordLoading(true);
    this.loadObjectives();
    // loadObjectives → onObjectivesLoaded re-renders Properties; for the chooser we
    // poll the cache once it lands. A short delayed render keeps this simple without
    // re-plumbing the fetch callback through two consumers.
    const wait = () => {
      if (this.objectives) { this.showObjectRecordLoading(false); this.renderObjectiveRecords(); }
      else setTimeout(wait, 60);
    };
    setTimeout(wait, 60);
  }

  // Fetch the three asset groups once (shared cache with the asset gallery). Returns
  // a promise of the groups so the Object chooser can consume the images group.
  fetchAssets() {
    if (this.assetGroups) return Promise.resolve(this.assetGroups);
    if (!this.hasAssetsUrlValue) {
      this.assetGroups = { documents: [], images: [], products: [] };
      return Promise.resolve(this.assetGroups);
    }
    return fetch(this.assetsUrlValue, { headers: { "Accept": "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this.assetGroups = {
          documents: data.documents || [],
          images: data.images || [],
          products: data.products || []
        };
        return this.assetGroups;
      })
      .catch(() => {
        this.assetGroups = { documents: [], images: [], products: [] };
        return this.assetGroups;
      });
  }

  showObjectRecordLoading(loading) {
    if (this.hasObjectRecordLoadingTarget) this.objectRecordLoadingTarget.classList.toggle("hidden", !loading);
    if (this.hasObjectRecordListTarget) this.objectRecordListTarget.classList.toggle("hidden", loading);
    if (loading && this.hasObjectRecordEmptyTarget) this.objectRecordEmptyTarget.classList.add("hidden");
  }

  // Render the objective list as pickable rows (id + name). Selecting a row inserts
  // a bound TEXT element.
  renderObjectiveRecords() {
    const objectives = this.objectives || [];
    this.objectRecordListTarget.replaceChildren();
    // Reset to list layout (images use grid — reset here so switching source types is clean)
    this.objectRecordListTarget.className =
      "flex min-h-0 grow flex-col gap-1 overflow-y-auto px-4 pb-4";
    if (this.hasObjectRecordEmptyTarget) this.objectRecordEmptyTarget.classList.toggle("hidden", objectives.length > 0);

    objectives.forEach((objective) => {
      const frag = this.objectRecordTemplateTarget.content.cloneNode(true);
      const row = frag.querySelector("[data-deck-editor-target='objectRecordRow']");
      const thumb = row.querySelector("[data-role='thumb']");
      if (thumb) thumb.classList.add("hidden");
      row.querySelector("[data-role='label']").textContent = objective.name;
      row.addEventListener("click", () => this.pickObjectRecord("objective", objective.id, objective.name));
      this.objectRecordListTarget.appendChild(frag);
    });
  }

  // Render the image records (from the assets "images" group) as a 5-column grid of
  // thumbnail tiles. Selecting a tile inserts a bound IMAGE element.
  renderObjectRecords(images) {
    this.objectRecordListTarget.replaceChildren();
    // Switch to grid for image tiles
    this.objectRecordListTarget.className =
      "grid min-h-0 grow grid-cols-5 gap-2 overflow-y-auto px-4 pb-4";
    if (this.hasObjectRecordEmptyTarget) this.objectRecordEmptyTarget.classList.toggle("hidden", images.length > 0);

    const tmpl = this.hasObjectImageTemplateTarget
      ? this.objectImageTemplateTarget
      : this.objectRecordTemplateTarget;

    images.forEach((image) => {
      const frag = tmpl.content.cloneNode(true);
      const row = frag.querySelector("[data-deck-editor-target='objectRecordRow']");
      const thumb = row.querySelector("[data-role='thumb']");
      if (thumb) { thumb.src = image.thumb_url || image.url; thumb.alt = image.label || ""; }
      row.querySelector("[data-role='label']").textContent = image.label || `Image #${image.id}`;
      row.addEventListener("click", () => this.pickObjectRecord("image", image.id, image.label || `Image #${image.id}`));
      this.objectRecordListTarget.appendChild(frag);
    });
  }

  // Dispatch a picked record by chooser mode (ADR-0070 §2/§3). "insert" inserts a
  // new pre-bound element; "link" sets the selected element's resource link. The
  // SAME record rows serve both flows — only the mode flag (set by the entry point)
  // differs, so the chooser is reused, not duplicated.
  pickObjectRecord(source, recordId, label) {
    if (this.objectChooserMode === "link") {
      this.setElementLink(source, recordId, label);
    } else {
      this.insertObjectElement(source, recordId, label);
    }
  }

  // Resolve the chosen source+record to a CONCRETE pre-bound element and insert it
  // (ADR-0070 §2/D1). Objective → a text element (default visual "text"); Image →
  // an image element. The slot is auto-named via uniqueSlotKey and the deck-level
  // binding is written in one act — no separate "place then wire" step. The image
  // label is cached onto the binding so the Measures overview can name it without a
  // re-fetch. Drops at the captured right-click point when present, else default.
  insertObjectElement(source, recordId, label) {
    const kind = source === "image" ? "image" : "text";
    const def = this.elementDefault(kind);

    let x = def.x;
    let y = def.y;
    if (this.objectInsertPoint) {
      x = this.clamp(this.snapValue(this.objectInsertPoint.x), 0, this.canvasW - def.w);
      y = this.clamp(this.snapValue(this.objectInsertPoint.y), 0, this.canvasH - def.h);
    }

    const slide = this.currentSlide();
    if (!this.editableSlide(slide)) { this.closeObjectChooser(); return; }

    const slot = this.uniqueSlotKey(source, recordId);
    const value = source === "image" ? "url" : "display";

    slide.elements = slide.elements || [];
    slide.elements.push({
      kind, x, y, w: def.w, h: def.h,
      options: { ...def.options }, slot, content: null
    });
    this.selectedElement = slide.elements.length - 1;
    this.selectionIndices = [this.selectedElement];

    // Write the binding in the generalised shape (D3). Cache the image label so the
    // Measures overview can show it without a record lookup.
    this.deck.bindings = this.deck.bindings || {};
    const binding = { source, source_id: parseInt(recordId, 10), value };
    if (source === "image" && label) binding.label = String(label);
    this.deck.bindings[slot] = binding;

    this.markDirty();
    this.closeObjectChooser();
    this.renderCanvas();
    this.renderProperties();
  }

  // ---- Small field builders (editor chrome, not server data) -------------

  hint(text) {
    const p = document.createElement("p");
    p.className = "text-xs text-gray-500";
    p.textContent = text;
    return p;
  }

  sectionLabel(text) {
    const el = document.createElement("div");
    el.className = "mt-2 text-2xs font-semibold uppercase tracking-wide text-gray-400";
    el.textContent = text;
    return el;
  }

  fieldWrap(label) {
    const wrap = document.createElement("label");
    wrap.className = "flex flex-col gap-1 text-xs text-gray-600";
    const span = document.createElement("span");
    span.textContent = label;
    wrap.appendChild(span);
    return wrap;
  }

  textField(label, value, onInput) {
    const wrap = this.fieldWrap(label);
    const input = document.createElement("input");
    input.type = "text";
    input.value = value;
    input.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    input.addEventListener("input", (e) => onInput(e.target.value));
    wrap.appendChild(input);
    return wrap;
  }

  // Colour field (card #1916): a native colour swatch alongside a free-text input,
  // synced. Fill / colour values are not always hex — they can be CSS vars like
  // `var(--color-canary)` — so the TEXT field is the source of truth and the swatch
  // is a convenience. Typing updates the value (and the swatch when the text is a
  // parseable #hex); picking from the swatch writes the #hex into the text field.
  // Both paths call onInput(value).
  colorField(label, value, onInput) {
    const wrap = this.fieldWrap(label);

    const row = document.createElement("div");
    row.className = "flex items-center gap-2";

    const swatch = document.createElement("input");
    swatch.type = "color";
    // h-8 keeps the swatch the same height as the text input's px-2 py-1 box.
    swatch.className = "h-8 w-10 shrink-0 cursor-pointer rounded border border-gray-300";
    swatch.value = this.hexOrDefault(value);

    const text = document.createElement("input");
    text.type = "text";
    text.value = value || "";
    text.className = "grow rounded border border-gray-300 px-2 py-1 text-sm";

    // Text is authoritative. Sync the swatch only when the text parses as #hex, so
    // a CSS var leaves the swatch at its last sensible position without fighting.
    text.addEventListener("input", (e) => {
      const v = e.target.value;
      if (this.isHexColor(v)) swatch.value = v;
      onInput(v);
    });

    // Picking from the swatch writes the #hex back into the text field and value.
    swatch.addEventListener("input", (e) => {
      const v = e.target.value;
      text.value = v;
      onInput(v);
    });

    row.appendChild(swatch);
    row.appendChild(text);
    wrap.appendChild(row);

    // Quick-pick brand swatches (card #1916). The fixed Retail Tasker palette
    // arrives as a data attribute (single source of truth in Deck::
    // DECK_PALETTE). Each chip routes through the SAME path typing does — set the
    // text + swatch, then call onInput, which already carries markDirty/render.
    if (this.paletteValue?.length) {
      const chips = document.createElement("div");
      chips.className = "mt-1.5 flex flex-wrap items-center gap-1";

      this.paletteValue.forEach((entry) => {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "h-5 w-5 shrink-0 rounded border border-gray-300 cursor-pointer";
        // Data-driven swatch colour — the justified inline-style exception, same
        // as the native swatch / geometry code.
        chip.style.backgroundColor = entry.value;
        chip.title = `${entry.name} (${entry.value})`;
        chip.setAttribute("aria-label", `${entry.name} (${entry.value})`);
        // Highlight the chip matching the current value so the active swatch reads.
        if (typeof value === "string" && value.trim().toLowerCase() === entry.value.toLowerCase()) {
          chip.classList.add("ring-2", "ring-offset-1", "ring-sapphire");
        }

        chip.addEventListener("click", () => {
          text.value = entry.value;
          swatch.value = entry.value; // all palette values are hex
          onInput(entry.value);
        });

        chips.appendChild(chip);
      });

      wrap.appendChild(chips);
    }

    return wrap;
  }

  // True for a #RGB or #RRGGBB string the native colour input can adopt.
  isHexColor(value) {
    return typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
  }

  // The swatch's value when the current text is a parseable #hex, else a neutral
  // default — the swatch never drives a non-hex value, it just sits sensibly.
  hexOrDefault(value) {
    return this.isHexColor(value) ? value.trim() : "#000000";
  }

  // Resolve a slide's stored `background` value into a concrete CSS
  // background-color and a readable default ink (card #1916).
  //
  // PARITY WITH RUBY: Deck.resolve_background mirrors this exactly —
  // keep the two in lockstep.
  //
  // Returns { backgroundColor, ink } where `ink` is null for the "leave default"
  // cases — the base .airdrop-slide--canvas class already supplies white ink, so
  // the caller only sets surface.style.color when we have a DEFINITE readable ink.
  //   blank / "sapphire" -> var(--color-sapphire), null   (class supplies white)
  //   "white"            -> #ffffff, #003d68              (legacy white-ground)
  //   #hex               -> #hex, readable contrast ink   (luminance-based)
  //   anything else      -> value, null                   (can't measure — leave)
  resolveBackground(value) {
    const v = (value || "").trim();

    if (v === "" || v === "white") return { backgroundColor: "#ffffff", ink: "#003d68" };
    if (v === "sapphire") return { backgroundColor: "var(--color-sapphire)", ink: null };
    if (this.isHexColor(v)) return { backgroundColor: v, ink: this.readableInk(v) };

    // A CSS var / named colour we can't measure — apply it, leave the ink default.
    return { backgroundColor: v, ink: null };
  }

  // Readable ink (#003d68 dark / #ffffff white) for a #hex background, chosen by
  // perceived luminance (0.299R + 0.587G + 0.114B, 0-255). > 150 = light ground,
  // use dark ink; else white. PARITY WITH RUBY#readable_ink.
  readableInk(hex) {
    let h = hex.trim().replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
    return luminance > 150 ? "#003d68" : "#ffffff";
  }

  textareaField(label, value, onInput) {
    const wrap = this.fieldWrap(label);
    const input = document.createElement("textarea");
    input.value = value;
    input.rows = 3;
    input.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    input.addEventListener("input", (e) => onInput(e.target.value));
    wrap.appendChild(input);
    return wrap;
  }

  // Sanitise rich-text HTML for safe injection into the canvas (card #2043).
  // Lexxy already sanitises on input via its own DOMPurify config, but we
  // re-sanitise on render as defence-in-depth and to mirror the server partial.
  sanitizeRichText(html) {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
      ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR
    });
  }

  // Rich-text "Content" editor for unbound text elements (card #2043). Mounts a
  // standalone <lexxy-editor>: it is form-associated by design, but here the
  // content is a plain JSON string (element.content), NOT an ActionText
  // attribute, so we drive it directly — seed initial HTML via the `value`
  // attribute, then read editor.value (already Lexxy-sanitised HTML) on the
  // `lexxy:change` event and push it back through onInput. Attachments are
  // disabled (attachments="false"): there is no ActionText/Active Storage
  // backend behind this editor, so the upload button would have nowhere to post.
  //
  // The wrapper is a <div> (not the <label> from fieldWrap) so clicks on the
  // label text don't fight the contenteditable surface or the toolbar buttons
  // for focus.
  richTextField(label, value, onInput) {
    const wrap = document.createElement("div");
    wrap.className = "flex flex-col gap-1 text-xs text-gray-600";
    const span = document.createElement("span");
    span.textContent = label;
    wrap.appendChild(span);

    const editor = document.createElement("lexxy-editor");
    editor.className = "deck-editor__rich-text rounded border border-gray-300 text-sm";
    editor.setAttribute("attachments", "false");
    editor.setAttribute("placeholder", "Enter text…");
    // Seed initial HTML. #loadInitialValue() reads this `value` attribute on
    // connect; existing plain-string content is valid HTML and loads unchanged.
    editor.setAttribute("value", value || "");

    // lexxy:change fires whenever the editor's content actually changes. We read
    // .value (sanitised HTML) back out and run the same dirty/re-render path the
    // old textarea handler used, so the canvas updates live as the author types.
    editor.addEventListener("lexxy:change", () => onInput(editor.value));

    // Single-row scrolling toolbar (card #2043). The default Lexxy toolbar has
    // ~14 buttons and cannot fit the ~250px properties panel. Left alone, Lexxy's
    // ResizeObserver collapses the surplus into a floating "•••" popover that
    // covers the text area. Jay wants instead a single row with a thin horizontal
    // scrollbar showing every button.
    //
    // Lexxy decides to collapse when its toolbar's scrollWidth exceeds its
    // clientWidth (#toolbarIsOverflowing). We defeat that by moving the toolbar
    // into a scroll WRAPPER and letting the toolbar size to its content
    // (min-width:max-content, set in CSS) — then clientWidth === scrollWidth, the
    // overflow check is always false, no button ever moves, and the wrapper owns
    // the scrollbar. Reparenting is safe: the toolbar keeps its editor reference
    // and its own click handler, so command dispatch is unaffected (verified). We
    // wait for lexxy:initialize because the toolbar is created during the editor's
    // connect, one frame before that event fires.
    editor.addEventListener("lexxy:initialize", () => {
      const toolbar = editor.querySelector("lexxy-toolbar");
      if (!toolbar || toolbar.parentElement.classList.contains("deck-editor__rich-text-toolbar-scroll")) return;
      const scroller = document.createElement("div");
      scroller.className = "deck-editor__rich-text-toolbar-scroll";
      editor.insertBefore(scroller, toolbar);
      scroller.appendChild(toolbar);
    }, { once: true });

    wrap.appendChild(editor);
    return wrap;
  }

  numberField(label, value, onInput) {
    const wrap = this.fieldWrap(label);
    const input = document.createElement("input");
    input.type = "number";
    input.value = value;
    input.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    input.addEventListener("input", (e) => onInput(parseInt(e.target.value, 10) || 0));
    wrap.appendChild(input);
    return wrap;
  }

  selectField(label, value, options, onChange) {
    const wrap = this.fieldWrap(label);
    const select = document.createElement("select");
    select.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    options.forEach((opt) => {
      const o = document.createElement("option");
      o.value = opt;
      o.textContent = opt;
      if (opt === value) o.selected = true;
      select.appendChild(o);
    });
    select.addEventListener("change", (e) => onChange(e.target.value));
    wrap.appendChild(select);
    return wrap;
  }

  // Like selectField, but the option VALUE (a key) differs from its visible
  // LABEL — used for the font picker, whose value is a registry key.
  selectFieldKeyed(label, value, keys, labels, onChange) {
    const wrap = this.fieldWrap(label);
    const select = document.createElement("select");
    select.className = "rounded border border-gray-300 px-2 py-1 text-sm";
    keys.forEach((key, index) => {
      const o = document.createElement("option");
      o.value = key;
      o.textContent = labels[index] || key;
      if (key === value) o.selected = true;
      select.appendChild(o);
    });
    select.addEventListener("change", (e) => onChange(e.target.value));
    wrap.appendChild(select);
    return wrap;
  }

  // ---- Save --------------------------------------------------------------

  // Returns the fetch promise resolving to the Response (card #2061) so callers
  // such as pullData() can await the save and branch on its outcome. The UI
  // behaviour — disable button, spinner state, clearDirty, flash — is unchanged.
  save() {
    if (!this.hasSaveButtonTarget) return Promise.resolve(null);
    const button = this.saveButtonTarget;
    button.disabled = true;
    // State lives in the icon — swap to the spinner variant (card #1925).
    this.showSaveState("saving");

    // Live-editing overrides (card #1978, ADR-0094): send the CURRENT override map
    // alongside deck_json so an in-canvas edit (which writes here, not to
    // deck_json — see startInlineEdit's commit()) actually persists. Scoped to
    // just this one result_json key server-side (persist_deck), so a stale local
    // copy of OTHER pulled result_json values is never round-tripped.
    const overrides = (this.result && this.result["_manual_overrides"]) || {};

    return this.fetchJson(this.updateUrlValue, "PATCH", { deck_json: this.deck, canvas_w: this.canvasW, canvas_h: this.canvasH, overrides })
      .then((response) => {
        button.disabled = false;
        if (response.ok) {
          this.clearDirty();
          this.flashSaveIcon("done");
        } else {
          this.flashSaveIcon("fail");
        }
        return response;
      })
      .catch((error) => {
        button.disabled = false;
        this.flashSaveIcon("fail");
        throw error;
      });
  }

  // Toggle the Save button between its four icon variants. Hides all, then
  // reveals the one for `state` ("idle" | "saving" | "done" | "fail").
  // No HTML-in-JS — the variants are static ERB, this only flips `hidden`.
  showSaveState(state) {
    if (this.hasSaveIdleTarget) this.saveIdleTarget.classList.toggle("hidden", state !== "idle");
    if (this.hasSaveSpinnerTarget) this.saveSpinnerTarget.classList.toggle("hidden", state !== "saving");
    if (this.hasSaveDoneTarget) this.saveDoneTarget.classList.toggle("hidden", state !== "done");
    if (this.hasSaveFailTarget) this.saveFailTarget.classList.toggle("hidden", state !== "fail");
  }

  // Shared CSRF-aware JSON fetch — the single network primitive for save,
  // save-as-kind, etc. Returns the raw Response (callers branch on `.ok`).
  fetchJson(url, method, body) {
    const csrf = document.querySelector("[name='csrf-token']");
    return fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrf ? csrf.content : ""
      },
      body: JSON.stringify(body)
    });
  }

  postJson(url, body) {
    return this.fetchJson(url, "POST", body);
  }

  // Flash the result icon ("done" | "fail") for ~1.4s, then revert to idle.
  flashSaveIcon(state) {
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = false;
    this.showSaveState(state);
    setTimeout(() => this.showSaveState("idle"), 1400);
  }

  // ---- Pull data (card #2061) --------------------------------------------

  // Save the deck, then POST /decks/:id/pull as JSON to resolve the CURRENT
  // bindings into result_json, and refresh the surfaces that SHOW values — the
  // Bindings "Data fields" list always, and Preview when it is the active tab.
  // The Designer canvas is never resolved (it stays literal for authoring).
  async pullData() {
    if (!this.hasPullButtonTarget) return;

    // Persist first so the pull resolves what the author currently sees. A failed
    // or errored save means a stale pull, so we bail and tell them — never silently
    // pull against an old deck.
    try {
      const resp = await this.save();
      if (resp && !resp.ok) {
        this.flashPullStatus("Save failed — not pulled");
        return;
      }
    } catch (error) {
      this.flashPullStatus("Save failed — not pulled");
      return;
    }

    this.setPullBusy(true);
    try {
      const r = await this.postJson(this.pullUrlValue, {});
      if (!r.ok) {
        this.flashPullStatus("Pull failed");
        return;
      }
      const data = await r.json();
      this.result = data.result_json || {};

      let message = `Pulled ${data.resolved_count ?? 0} value(s)`;
      const skipped = data.skipped_count ?? 0;
      if (skipped > 0) message += `, skipped ${skipped}`;
      this.flashPullStatus(message);

      // Refresh the surfaces that show resolved values. The Data fields list reads
      // this.result; Preview re-renders against it when active (when it isn't,
      // switchTab re-renders it later and picks up this.result then).
      this.renderBindings();
      if (this.activeTabValue === "preview") this.renderPreview();
    } catch (error) {
      this.flashPullStatus("Pull failed");
    } finally {
      this.setPullBusy(false);
    }
  }

  // Toggle the Pull button between idle and spinner (card #2061). No HTML-in-JS —
  // the variants are static ERB, this only flips `hidden` and the disabled state.
  setPullBusy(busy) {
    if (this.hasPullButtonTarget) this.pullButtonTarget.disabled = busy;
    if (this.hasPullIconTarget) this.pullIconTarget.classList.toggle("animate-spin", busy);
  }

  // Flash a transient status next to the Pull button, then hide it (~2s). Mirrors
  // flashSaveIcon's idiom — show, set a timer to clear.
  flashPullStatus(message) {
    if (!this.hasPullStatusTarget) return;
    this.pullStatusTarget.textContent = message;
    this.pullStatusTarget.classList.remove("hidden");
    clearTimeout(this._pullStatusTimer);
    this._pullStatusTimer = setTimeout(() => {
      this.pullStatusTarget.classList.add("hidden");
    }, 2000);
  }

  // ---- Dirty flag --------------------------------------------------------

  markDirty() {
    if (!this._burstActive) {
      this._burstActive = true;
      if (this._historySnapshot !== undefined) {
        this.history.push(this._historySnapshot);
        if (this.history.length > 50) this.history.shift();
      }
      this.future = [];
      this._updateUndoRedoButtons();
    }
    clearTimeout(this._historyTimer);
    this._historyTimer = setTimeout(() => {
      this._historySnapshot = this._captureState();
      this._burstActive = false;
    }, 800);
    this.dirty = true;
    if (this.hasDirtyTarget) this.dirtyTarget.classList.remove("hidden");
  }

  clearDirty() {
    this.dirty = false;
    if (this.hasDirtyTarget) this.dirtyTarget.classList.add("hidden");
  }

  // ---- Helpers -----------------------------------------------------------

  // ---- Undo / redo (card #1976) -----------------------------------------

  // Capture the full editor state as a JSON string. selectedSlide and
  // selectedElement are included so undo restores the selection context too.
  _captureState() {
    return JSON.stringify({
      deck: this.deck,
      selectedSlide: this.selectedSlide,
      selectedElement: this.selectedElement
    });
  }

  // Flush any in-flight burst immediately. Call at the start of drag
  // operations (startMove, startResize) so each drag is a discrete
  // undo entry, not merged into the preceding property-panel burst.
  _flushBurst() {
    if (!this._burstActive) return;
    clearTimeout(this._historyTimer);
    this._historySnapshot = this._captureState();
    this._burstActive = false;
  }

  // Restore state from a JSON snapshot without touching the history stack.
  // Called by undo() and redo() — bypasses markDirty() to avoid
  // re-entering the history logic.
  _restoreState(snapshot) {
    const state = JSON.parse(snapshot);
    this.deck = state.deck;
    this.selectedSlide = state.selectedSlide;
    this.selectedElement = state.selectedElement;
    // Undo/redo restores a single primary (multi-selection is transient editor chrome,
    // never serialised into history) — keep the selection set in lockstep.
    this.selectionIndices = this.selectedElement === null ? [] : [this.selectedElement];
    this.dirty = true;
    if (this.hasDirtyTarget) this.dirtyTarget.classList.remove("hidden");
    this.renderAll();
  }

  // Reflect undo/redo availability on toolbar buttons.
  _updateUndoRedoButtons() {
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = this.history.length === 0;
    if (this.hasRedoButtonTarget) this.redoButtonTarget.disabled = this.future.length === 0;
  }

  undo() {
    if (!this.history.length) return;
    this._flushBurst();
    this.future.push(this._captureState());
    const snapshot = this.history.pop();
    this._restoreState(snapshot);
    this._historySnapshot = this._captureState();
    this._updateUndoRedoButtons();
  }

  redo() {
    if (!this.future.length) return;
    this._flushBurst();
    this.history.push(this._captureState());
    if (this.history.length > 50) this.history.shift();
    const snapshot = this.future.pop();
    this._restoreState(snapshot);
    this._historySnapshot = this._captureState();
    this._updateUndoRedoButtons();
  }

  currentSlide() {
    if (this.selectedSlide === null) return null;
    return this.deck.slides[this.selectedSlide] || null;
  }

  // True when a slide carries an editable chrome layer on slide.elements — both
  // CANVAS and COLLECTION slides do (ADR-0070 §4b). A collection slide is a canvas
  // slide PLUS a derived auto-grid overlay + pager, so its chrome elements go
  // through the exact same authoring path (select / drag / resize / right-click
  // insert / properties). This is the single guard the element-authoring sites
  // share — widen here, NOT the derived grid/title render guards.
  editableSlide(slide) {
    return !!slide && (slide.type === "canvas" || slide.type === "collection");
  }

  // ---- Deck font (card #1825) --------------------------------------------

  // The deck's chosen font key, falling back to the first registered key (the
  // brand default) when blank/unknown — mirrors Deck#font_key.
  deckFontKey() {
    const fonts = this.fontsValue || {};
    const key = this.deck && this.deck.design ? this.deck.design.fonts : null;
    if (key && fonts[key]) return key;
    const keys = Object.keys(fonts);
    return keys.length ? keys[0] : "";
  }

  // The resolved CSS font-family stack for the deck's chosen font. Empty string
  // when no registry is present, so the CSS default (.airdrop-slide) wins.
  deckFontFamily() {
    const fonts = this.fontsValue || {};
    const entry = fonts[this.deckFontKey()];
    return entry && entry.family ? entry.family : "";
  }

  // ---- Per-element font override (card #2081) ----------------------------

  // Resolve an ELEMENT's per-element font override into a CSS font-family stack,
  // or null when the element has no override. The element stores a KEY into the
  // curated font registry (the SAME closed list the deck-wide picker uses, #1825)
  // at element.font — never a raw font-family string. A blank or unknown key
  // returns null so the element INHERITS the deck-wide font already set on the
  // slide surface (do NOT emit a font-family). Byte-for-byte twin of
  // Deck.element_font_family — keep the two in lockstep.
  elementFontFamily(element) {
    const fonts = this.fontsValue || {};
    const key = element && element.font ? element.font : "";
    const entry = key ? fonts[key] : null;
    return entry && entry.family ? entry.family : null;
  }

  int(v) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  }

  clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }
}
